import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  startBroadcast, joinBroadcast, BroadcastController, ViewerController, ViewerDiagnostics,
  LiveGiftEvent, AgentCaptionEvent,
  isSfuEnabled,
} from '../services/webrtcBroadcastService';
import {
  createAgentBroadcastEngine,
  type AgentBroadcastEngine,
} from './AgentBroadcastCanvas';
import { listEntities, EntityRecord } from '../services/entitiesService';
import { startSfuIngest, stopSfuIngest, type SfuIngestSession } from '../services/sfuIngestService';
import {
  getWebRtcPolicyConfig, WebRtcPolicyConfig, WEBRTC_POLICY,
  stroopsToDisplay, formatDuration,
} from '../services/webrtcPolicyService';
import {
  requestLiveSession, confirmLivePayment, markLivePaymentFailed,
  stopLiveSession, listActiveBroadcasts, extendLiveSession,
  obtainViewerToken,
  LiveSession, ApproxLocation,
} from '../services/liveSessionService';
import {
  getProfile, getPublicProfile, setProfile as setProfileOnChain,
  setPrivacy as setPrivacyOnChain,
  validateProfile, remainingBytes, PROFILE_MAX_BYTES,
  ProfileRecord, EMPTY_PROFILE,
  PasskeySigner, SigningWallet,
} from '../services/profileService';
import { TrustlinesPanel, TrustlinesWallet } from './TrustlinesPanel';
import { LiveGiftModal } from './LiveGiftModal';
import LiveToolsLayer from './LiveToolsLayer';
import LiveToolsControlPanel from './LiveToolsControlPanel';
import PastBroadcastsTab from './PastBroadcastsTab';
import AgentBadge from './AgentBadge';
import AgentsTab from './AgentsTab';
import { useBroadcastRecorder } from '../hooks/useBroadcastRecorder';
import { uploadBroadcast, type RecordedEvent } from '../services/pastBroadcastsService';
import {
  listVideoDevices, captureStream, nextCameraDevice, normaliseCaptureError,
  VideoDeviceInfo, NormalisedCaptureError,
} from '../services/mediaDevices';

/**
 * ProfileOverlay
 *
 * A modernised, tabbed profile surface with three distinct sections:
 *
 *   1. **Profile** — display name, short bio, avatar URL, X / Discord
 *      handles, and a public/private toggle.  All six fields live on
 *      chain in the `geo-profile` contract; the component shows a
 *      byte-by-byte counter per field against the contract caps and an
 *      explicit "this will be written on chain" confirmation before
 *      signing.
 *
 *   2. **Live** — paid WebRTC broadcasting (owner) or "Watch live"
 *      (viewer).  Unchanged flow: policy fetch → optional payment →
 *      grant token → WebRTC signaling.  The server is authoritative
 *      on duration; the countdown in UI is cosmetic.
 *
 *   3. **Trustlines** — full Stellar-SDK-backed view/add/remove/pay
 *      surface (see `TrustlinesPanel`).  Not rendered for smart-
 *      account (C…) wallets because classic trustlines don't apply
 *      to contract accounts.
 *
 * Viewer awareness:
 *
 *   - `isOwner = true`: my own profile.  Profile editor is exposed,
 *     the "Live" tab shows broadcast controls, the trustlines tab
 *     exposes add/remove + a "Send to someone" payment flow.
 *   - `isOwner = false`: someone else's profile.  Shows the redacted
 *     public record (`get_public_profile`), exposes "Watch live",
 *     and the trustlines tab only exposes a per-line "Send <asset> to
 *     <them>" button that signs from the viewer's wallet.
 *
 * All network side-effects are isolated to the service layer.  The
 * component only takes signer objects as props; it never imports a
 * wallet SDK directly.
 */

// ── Types ────────────────────────────────────────────────────────────────────

/** Legacy prop kept for backward compatibility with existing callers. */
export interface ProfileFields {
  displayName: string;
  bio: string;
  avatarUrl: string;
}

export type ExecuteLivePayment = (args: {
  sessionId: string;
  country: number | null;
  wallet: string;
  amountStroops: string;
  amountDisplay: string;
}) => Promise<{ paymentReference: string; paidAmount?: string }>;

export interface ProfileOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stellar address (G… or C…) of the profile being viewed. */
  wallet: string;
  /** ISO numeric country code relevant to live-session policy lookup. */
  country?: number;
  /** Human-readable country name (e.g. "United States") used in the
   *  "Evaluating …" hint on the Live tab so the user knows which
   *  jurisdiction's policy is about to be fetched.  Optional — the
   *  overlay falls back to the numeric code when absent. */
  countryName?: string;
  /**
   * Which source yielded the currently-evaluated country.  Rendered
   * as a subtle hint on the Live tab so the user understands *why*
   * the policy keeps refreshing (e.g. the idle globe is auto-
   * spinning the map centre into different countries).
   *
   *   - `'location'` → geolocation ("Go online").  Stable.
   *   - `'marker'`   → selected map marker.  Stable.
   *   - `'map'`      → whatever country the map centre is currently
   *     over.  Re-evaluates every time the map centre moves (e.g.
   *     idle-spin).  This is the source the user is usually asking
   *     about when they see the "could not be determined" banner.
   */
  countrySource?: 'location' | 'marker' | 'map';
  /** Whether the viewer has already granted/used geolocation this
   *  session (i.e. `playerLocation` is set in App.tsx).  Controls
   *  whether the Live tab offers the "Use my location" button. */
  hasLocation?: boolean;
  /** Invoked when the user clicks the in-overlay "Use my location"
   *  button on the Live tab.  Kicks off the same geolocation request
   *  as the game panel's Go Online button. */
  onRequestLocation?: () => void;
  /** Viewer's own wallet address — used to decide isOwner and to sign payments. */
  viewerAddress?: string;
  /** Explicit owner flag; when omitted it is derived from wallet vs viewerAddress. */
  isOwner?: boolean;
  /** Live-session marker from outside (profile can also detect via WebRTC hub). */
  isLive?: boolean;
  /** Deprecated: if supplied, used as the initial seed before the contract read lands. */
  initialFields?: ProfileFields;
  /** Optional callback fired after a successful on-chain save. */
  onSaved?: (record: ProfileRecord) => void;
  onBroadcastStateChange?: (live: boolean) => void;
  /**
   * Fired whenever *any* live session (own broadcast OR watching
   * someone else) is active or idle.  App.tsx uses this to keep the
   * map-marker one-click shortcut from yanking the ProfileOverlay to
   * a different wallet while the user has media streaming — clicking
   * another marker while a session is live leaves the current overlay
   * alone so the minimised pill stays connected.
   */
  onSessionActiveChange?: (active: boolean) => void;

  // Signers
  /** Passkey signer for smart-account (C…) owners.  Required to save when the wallet is C…. */
  passkey?: PasskeySigner;
  /** Stellar wallet signer (Freighter / WalletConnect) for G… owners AND for viewer-side payments. */
  stellarWallet?: SigningWallet & TrustlinesWallet;

  /** Runs the Stellar/wallet payment step for paid live sessions. */
  onExecuteLivePayment?: ExecuteLivePayment;

  /**
   * Optional current location of the viewer.  When the owner opens
   * their own overlay it's used both for the HUD and to attach an
   * approximate (~11 km) coordinate to the paid-session request so
   * remote viewers see *roughly where* the broadcast is coming from.
   */
  approxLocation?: ApproxLocation;

  /**
   * Open the overlay on a specific tab.  Used by the wallet-panel /
   * game-panel quick-peek icon chips (👤 / 🎥 / 💎) to deep-link into
   * Profile, Live, or Trustlines without making the user tap a second
   * time.  When the wallet is a smart-account (C…) the overlay falls
   * back to the Profile tab because trustlines aren't shown for C
   * addresses.  Defaults to 'profile'.
   */
  initialTab?: 'profile' | 'live' | 'trustlines';

  /**
   * TikTok-style swipe-between-broadcasts.  When provided, the
   * video pane renders up/down arrow controls and responds to
   * vertical swipes by asking the caller to jump to the neighbour
   * broadcast.  The caller (App.tsx) computes the neighbour using
   * the active live-feed algorithm and flips `wallet` to the next
   * broadcaster — `ProfileOverlay` itself is deliberately ignorant
   * of which algorithm is active so adding more sort strategies
   * doesn't touch this component.
   */
  onNextBroadcast?: () => void;
  onPrevBroadcast?: () => void;
  /** Label of the next broadcast for the button tooltip. */
  nextBroadcastLabel?: string | null;
  prevBroadcastLabel?: string | null;
  /** Re-open the overlay for a different wallet. Powers the
   *  "🤖 by <creator>" link on agent broadcasts (agent VOD → the
   *  human account that runs it) and any other cross-profile jumps. */
  onOpenWallet?: (wallet: string) => void;

  /**
   * The viewer's own trust groups, used to populate the "Go Live"
   * visibility selector (public vs. one-or-more trust groups). Empty
   * or omitted → only the Public option is offered.
   */
  myTrustGroups?: { id: number; name: string }[];

  /**
   * Create the on-chain geo-session that carries a broadcast's scope
   * (public or trust-group-restricted) and return its id. Called by the
   * go-live flow when the broadcaster picks a restricted audience so the
   * SFU can resolve the member allowlist from chain. Returns `null` when
   * the on-chain session couldn't be created (the caller then falls back
   * to a public broadcast). Owner-only.
   */
  onStartOnchainSession?: (opts: {
    isPublic: boolean;
    country: number | null;
    trustGroupIds: number[];
  }) => Promise<number | null>;
}

type Tab = 'profile' | 'live' | 'trustlines' | 'past_broadcasts' | 'agents';

type BroadcastPhase =
  | 'idle'
  | 'loading_policy'
  | 'policy_blocked'
  | 'ready_to_start'
  | 'payment_pending'
  // Payment succeeded (or wasn't required) but the broadcaster
  // hasn't hit "Start Live" yet.  This gives them a chance to
  // collect themselves — adjust camera, compose a thought —
  // *after* their wallet has fronted the cost, instead of firing
  // straight into the stream the moment the chain confirms.
  | 'awaiting_go_live'
  // The broadcaster has hit "Start Live" and we're running the
  // 3-2-1 countdown.  No WebRTC traffic yet; the local camera
  // preview is already warm from the earlier getUserMedia call
  // so the transition into `connecting` is instant.
  | 'countdown'
  | 'connecting'
  | 'live'
  | 'ended';

// ── Constants ────────────────────────────────────────────────────────────────

const ENDED_LABELS: Record<string, string> = {
  ended_duration:         'Max live duration reached',
  ended_user:             'Stopped by broadcaster',
  ended_admin:            'Terminated by admin',
  ended_policy:           'Ended because policy changed',
  ended_payment_failed:   'Ended because payment failed',
  ended_error:            'Ended due to an error',
  blocked_by_policy:      'Blocked by country policy',
  webrtc_policy_not_set:  'WebRTC policy is not set for this country',
  webrtc_disallowed_in_country: 'WebRTC is disallowed in this country',
  denied_by_compliance_list:    'Wallet is on the compliance deny-list',
  payment_handler_missing: 'No payment handler is wired into this UI',
  payment_failed:         'Payment failed',
  country_unknown:        'Your current country could not be determined — enable location or pan the map over a country to load its live-session policy',
  policy_load_failed:     'Could not load the WebRTC policy for your country',
  // Strict guard for paid broadcasts.  We only allow a session to start
  // when the country was resolved from `playerLocation` (i.e. the
  // viewer's actual GPS).  Map-centre or marker-based country picks
  // would let a viewer in country A pay for a country-B priced
  // broadcast and publish nothing pinpointable on the map — see the
  // location-policy bug fix that introduced this reason.
  location_required_for_broadcast: 'Live broadcasts require your real location. Tap Go LIVE with my location to continue.',
  invalid_grant:                 'Live-session grant token is invalid — please try starting the broadcast again',
  grant_expired:                 'Live-session grant expired — please start the broadcast again',
  invalid_state:                 'Live-session is in an unexpected state — please refresh and try again',
  missing_live_session_grant:    'Missing live-session grant — please start the broadcast again',
  invalid_live_session_grant:    'Live-session grant token did not validate — please try again',
  live_session_invalid_state:    'Live-session is in an unexpected state — please refresh',
  broadcaster_already_live:      'You are already broadcasting on another tab or device',
  broadcaster_not_live:          'Broadcaster is not live right now',
  viewer_cap_reached:            'This broadcast is full — please try again later',
  not_found:                     'Live-session was not found on the backend (did the server restart?)',
  missing_broadcasterId:         'Missing broadcaster id — please refresh and try again',
  socket_closed:                 'Signaling connection closed',
  webrtc_ws_error:               'Signaling connection error',
  broadcaster_left:              'Broadcaster ended the session',
  no_active_session:             'There is no active live session to extend right now',
  extension_not_required:        'This session is free — no payment needed to extend',
  extend_failed:                 'Could not extend the session — please try again',
  missing_payment_reference:     'Payment receipt was missing a reference — please try again',
  ice_failed:                    'Could not establish a direct media path to the broadcaster (NAT/firewall). Try a different network or contact the site operator about TURN.',
  ice_timeout:                   'Media path did not connect in time (NAT/firewall). Please retry — you will not be re-charged.',
  connection_state_failed:       'Media connection failed — please retry the watch',
  connection_state_disconnected: 'Media connection dropped — please retry the watch',
  connection_state_closed:       'Media connection closed',
  // Capture errors surfaced by mediaDevices.ts.  Tesla's infotainment
  // browser and desktops without a webcam land on `no_camera_device`.
  no_camera_device:              'No camera detected on this device. Some vehicles (e.g. Tesla) and kiosk browsers do not expose their cameras to web pages for privacy reasons. You can still watch a broadcast that is live under this wallet from another device.',
  camera_not_allowed:            'Camera or microphone permission was denied. Enable access in your browser site settings and try again.',
  camera_in_use:                 'Camera is in use by another app. Close other video calls or tabs and try again.',
  insecure_origin:               'Camera access requires a secure origin (https:// or localhost).',
  overconstrained:               'Selected camera is unavailable — try a different one.',
  camera_switch_failed:          'Could not switch camera — keeping the previous one.',
};

function prettyReason(reason: string | null | undefined): string {
  if (!reason) return 'Session ended';
  return ENDED_LABELS[reason] || reason;
}

/**
 * Returns `true` when `reason` is a NAT / ICE / TURN style
 * failure — i.e. one where a TURN-relay-only retry has a realistic
 * chance of succeeding.  We deliberately exclude transient socket
 * drops (`socket_closed`, `disconnected`) because those usually
 * recover on their own and don't benefit from forcing relay mode.
 */
function isNatRelatedReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return (
    reason === 'ice_failed' ||
    reason === 'ice_timeout' ||
    reason === 'connection_state_failed'
  );
}

/**
 * Conservative user-agent sniff for Tesla's in-vehicle browser.
 * Tesla ships a custom Chromium build that reports a Tesla-specific
 * token (`Tesla/` or sometimes `QtCarBrowser`) in the UA string on
 * most firmware versions.  We only use this to *warn* users up-
 * front that outbound media may be blocked — we never gate any
 * functionality on it because Tesla rotates UA strings between
 * firmware releases and a false negative just means they get the
 * same experience they have today.
 */
function isTeslaBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Tesla|QtCarBrowser/i.test(ua);
}

/**
 * True when the ended-reason is `ice_timeout` AND the diagnostics
 * show the ICE agent failed to gather even a single local
 * candidate.  This is the signature of a network that blocks
 * outbound WebRTC entirely (Tesla in-vehicle Wi-Fi, some corporate
 * firewalls, ultra-restrictive guest networks).  We render a
 * distinct "your network blocks media traffic" message with a
 * hotspot suggestion in this case — the generic "TURN unreachable"
 * wording misleads users into thinking it's a server-side issue.
 */
function isNoLocalCandidatesFailure(
  reason: string | null | undefined,
  diag: ViewerDiagnostics | null | undefined,
): boolean {
  if (!reason || !diag) return false;
  if (reason !== 'ice_timeout' && reason !== 'ice_failed') return false;
  // After a relay-only retry, zero local candidates is conclusive:
  // we couldn't even reach the TURN server.  During a direct
  // attempt, zero is suspicious but not definitive (gathering may
  // still be in progress).  The watchdog gives us 20s before firing
  // so "gathering still in progress" after a timeout is itself a
  // sign the network stack is broken.
  return diag.localCandidateTypes.length === 0;
}

// ── Keyframes (injected once) ────────────────────────────────────────────────
//
// Keyframes can't live in inline style objects, so we inject them via a
// singleton <style> tag the first time this module is evaluated in the
// browser.  Safe to run in SSR (typeof document guard).
(function injectProfileOverlayKeyframes() {
  if (typeof document === 'undefined') return;
  const id = 'gt-profile-overlay-keyframes';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes gt-live-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
      70%  { box-shadow: 0 0 0 10px rgba(255,255,255,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
    }
    @keyframes gt-countdown-pop {
      0%   { transform: scale(0.6); opacity: 0; }
      30%  { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1.0); opacity: 1; }
    }
    @keyframes gt-gift-slide-in {
      0%   { transform: translateX(40px); opacity: 0; }
      100% { transform: translateX(0);    opacity: 1; }
    }
  `;
  document.head.appendChild(style);
})();

// ── Hooks ────────────────────────────────────────────────────────────────────

function useCountdown(targetIso: string | null | undefined, onExpire?: () => void): number {
  const [remaining, setRemaining] = useState<number>(() =>
    targetIso ? Math.max(0, Math.floor((new Date(targetIso).getTime() - Date.now()) / 1000)) : 0,
  );
  useEffect(() => {
    if (!targetIso) { setRemaining(0); return; }
    const tick = () => {
      const r = Math.max(0, Math.floor((new Date(targetIso).getTime() - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0 && onExpire) onExpire();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso, onExpire]);
  return remaining;
}

// ── Component ────────────────────────────────────────────────────────────────

const ProfileOverlay: React.FC<ProfileOverlayProps> = (props) => {
  const {
    isOpen, onClose, wallet, country,
    countryName, countrySource, hasLocation, onRequestLocation,
    viewerAddress, isOwner: isOwnerProp,
    isLive: isLiveProp = false, initialFields, onSaved,
    onBroadcastStateChange, onSessionActiveChange, passkey, stellarWallet,
    onExecuteLivePayment, approxLocation, initialTab,
    onNextBroadcast, onPrevBroadcast,
    nextBroadcastLabel, prevBroadcastLabel,
    onOpenWallet,
    myTrustGroups, onStartOnchainSession,
  } = props;

  const isOwner = useMemo(() => {
    if (typeof isOwnerProp === 'boolean') return isOwnerProp;
    return !!viewerAddress && viewerAddress === wallet;
  }, [isOwnerProp, viewerAddress, wallet]);

  // Tab selection: honour `initialTab` each time the overlay opens
  // (wallet / game panel icon chips deep-link via this prop).  Trustlines
  // are not rendered for smart-account (C…) wallets, so in that case we
  // clamp back to 'profile'.
  const [tab, setTab] = useState<Tab>(() => {
    if (initialTab === 'trustlines' && wallet.startsWith('C')) return 'profile';
    return initialTab || 'profile';
  });
  useEffect(() => {
    if (!isOpen || !initialTab) return;
    if (initialTab === 'trustlines' && wallet.startsWith('C')) setTab('profile');
    else setTab(initialTab);
  }, [isOpen, initialTab, wallet]);

  // ── Profile state ──────────────────────────────────────────────────────────
  const seed: ProfileRecord = useMemo(() => ({
    ...EMPTY_PROFILE,
    displayName: initialFields?.displayName ?? '',
    bio:         initialFields?.bio ?? '',
    avatarUrl:   initialFields?.avatarUrl ?? '',
  }), [initialFields]);

  const [record, setRecord]       = useState<ProfileRecord>(seed);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [draft, setDraft]         = useState<ProfileRecord>(seed);
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [pendingSave, setPendingSave] = useState(false);

  const dirty = useMemo(() => {
    return (
      draft.displayName   !== record.displayName ||
      draft.bio           !== record.bio ||
      draft.avatarUrl     !== record.avatarUrl ||
      draft.xHandle       !== record.xHandle ||
      draft.discordHandle !== record.discordHandle ||
      draft.isPrivate     !== record.isPrivate
    );
  }, [draft, record]);

  const validationError = useMemo(() => validateProfile(draft), [draft]);

  // ── Live-session state ─────────────────────────────────────────────────────
  const [broadcast, setBroadcast] = useState<BroadcastController | null>(null);
  const [viewer, setViewer]       = useState<ViewerController | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  /**
   * Per-session audience log kept on the broadcaster's client so
   * we can render the end-of-broadcast summary card (who joined,
   * how long they watched, origin country).  Keyed by `peerId`
   * because a single wallet can (in principle) watch from two
   * devices — we want to surface that as two distinct sessions
   * so the totals add up to the `viewers_update` count the HUD
   * already displays mid-broadcast.
   *
   * The entries are intentionally persisted across a stream's
   * internal reconnect loop — if the broadcaster's ws drops and
   * comes back, existing viewers are still "present" from our
   * point of view (the server itself keeps the viewer's wallet
   * cached for exactly this reason and re-announces them on
   * `broadcaster_reconnected`).
   */
  type AudienceEntry = {
    peerId: string;
    wallet?: string;
    country?: number | null;
    joinedAt: number;
    leftAt: number | null;
  };
  const [audienceLog, setAudienceLog] = useState<AudienceEntry[]>([]);
  const audienceLogRef = useRef<AudienceEntry[]>([]);
  useEffect(() => { audienceLogRef.current = audienceLog; }, [audienceLog]);

  /**
   * Viewer-side flag: set to `true` when the broadcaster ends the
   * live (either voluntarily, on duration, on admin termination).
   * We keep the `remoteStream` in state so the last frame stays
   * visible under an "ended" overlay until the user explicitly
   * taps Next / Close — the user specifically asked that we NOT
   * auto-scroll to the next broadcast, matching the way Twitch /
   * TikTok Live both render a terminal card.
   */
  const [viewerLiveEnded, setViewerLiveEnded] = useState(false);
  /**
   * Broadcaster-side flag: set by the "Pause" / "Resume" control
   * and used to (a) flip the button label, (b) render a "Paused"
   * pill on the local preview HUD, and (c) guard against the
   * stream auto-resuming on a reconnect (sfuClient resends the
   * flag on each successful connect so we never get stuck).
   */
  const [broadcastPaused, setBroadcastPaused] = useState(false);
  /**
   * Viewer-side mirror of the above — set when the hub pushes
   * `broadcast_paused` / `broadcast_resumed` over the ws.  Drives
   * the "Broadcast paused" overlay on the remote video pane.
   */
  const [remoteBroadcastPaused, setRemoteBroadcastPaused] = useState(false);
  /**
   * Screen-share state.  `mode` is one of:
   *   - `null`         → not sharing; the live video track comes from the camera.
   *   - `'fullscreen'` → the camera is replaced by `getDisplayMedia`; viewers
   *     see only the screen.  Simple and works on every browser that
   *     supports getDisplayMedia.
   *   - `'split'`      → a hidden <canvas> composites the camera (small
   *     picture-in-picture) on top of the screen share, and the canvas'
   *     captured stream becomes the outgoing video.  This keeps the
   *     broadcaster's face visible while they demo an app.
   *
   * `handleStartScreenShare` launches or switches between modes;
   * `handleStopScreenShare` restores the original camera track.
   */
  const [screenShareMode, setScreenShareMode] = useState<'fullscreen' | 'split' | null>(null);
  /**
   * Slice 6b-ui — which upstream the SFU publishes as the primary
   * video after the live session is on air.  `drone` means RTP ingest
   * (PlainTransport); the browser camera track is dropped from the SFU
   * producer (`replaceTrack(null)`) while the operator's local preview
   * can still show the webcam.
   */
  const [liveVideoSource, setLiveVideoSource] = useState<'camera' | 'screen' | 'drone'>('camera');
  const [liveDroneIngest, setLiveDroneIngest] = useState<Pick<SfuIngestSession, 'ingestId' | 'sourceId' | 'rtp'> | null>(null);
  const [liveSourceBusy, setLiveSourceBusy] = useState(false);
  const [liveSourceDraft, setLiveSourceDraft] = useState<string>('camera');
  const [broadcasterDroneEntities, setBroadcasterDroneEntities] = useState<EntityRecord[]>([]);
  const liveDroneIngestIdRef = useRef<string | null>(null);
  // Live gifts — queue of recently received gifts for the
  // celebratory toasts + a flag for whether the watcher's "Send
  // gift" modal is open.  `giftToasts` is capped to the last 6
  // entries to keep the DOM bounded during a firehose of tips.
  // Each gift stays visible for ~6 seconds after arrival.
  const [giftToasts, setGiftToasts] = useState<LiveGiftEvent[]>([]);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  // Refs hold the non-serialisable screen-capture plumbing so
  // re-renders don't force React to reconcile MediaStreamTracks
  // or canvases.  We do NOT store these in state — setState on a
  // MediaStreamTrack can crash React's internal Object.is check
  // on devtools snapshotting.
  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraTrackBeforeShareRef = useRef<MediaStreamTrack | null>(null);
  const compositeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const compositeCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const compositeRafRef = useRef<number | null>(null);
  const compositeStreamRef = useRef<MediaStream | null>(null);
  const compositeCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const compositeScreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const [phase, setPhase]         = useState<BroadcastPhase>('idle');
  const [policy, setPolicy]       = useState<WebRtcPolicyConfig | null>(null);
  const [activeSession, setActiveSession] = useState<LiveSession | null>(null);

  // ── Go Live visibility scope ────────────────────────────────────────────
  // 'public'  → anyone can see me on the map + watch my broadcast.
  // 'groups'  → only members of the selected trust group(s). This one
  //             scope governs BOTH on-chain map presence and SFU viewers.
  const [broadcastVisibility, setBroadcastVisibility] = useState<'public' | 'groups'>('public');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const availableGroups = useMemo(
    () => (Array.isArray(myTrustGroups) ? myTrustGroups : []),
    [myTrustGroups],
  );
  const toggleGroupSelected = useCallback((id: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );
  }, []);
  // Viewer-side gate state for restricted rooms we're not a member of.
  const [viewerScopeBlocked, setViewerScopeBlocked] = useState(false);

  /**
   * Reconnect indicator.  When the signaling ws drops mid-session the
   * service emits `onReconnecting(n)` and (on success)
   * `onReconnected()`.  We store the current attempt number so the
   * HUD can render "Reconnecting… (attempt 3)" with a visible counter
   * — this makes the reconnect path observable to the broadcaster who
   * would otherwise just see their feed freeze with no explanation.
   * The value is null when no reconnect is in flight.  Kept separate
   * for broadcaster vs viewer because both views can render different
   * copy ("your stream" vs "the broadcaster's stream").
   */
  const [reconnectingBroadcast, setReconnectingBroadcast] = useState<number | null>(null);
  const [reconnectingWatch, setReconnectingWatch] = useState<number | null>(null);
  /**
   * When viewing someone else's broadcast, the server pushes
   * `broadcaster_disconnected` with a `graceMs` deadline during
   * their reconnect window.  The deadline ticks down into this
   * state so the viewer UI can render "Broadcaster reconnecting…
   * (45s)" and auto-hide when the grace expires.
   */
  const [broadcasterGraceUntil, setBroadcasterGraceUntil] = useState<number | null>(null);
  /**
   * 3-2-1 pre-broadcast countdown ticker.  When the broadcaster
   * clicks the big green "Start Live" button *after* a successful
   * payment, we transition `phase → 'countdown'` and set this to
   * 3, then tick it down to 1 and finally launch the WebRTC
   * capture + SFU publish path.  Rendered as a fullscreen overlay
   * above the local camera preview so the broadcaster has a
   * moment to compose themselves before going live.
   */
  const [countdownSec, setCountdownSec] = useState<number | null>(null);
  // ── Past-broadcasts (VOD) recording opt-in ─────────────────────────
  // Broadcaster ticks this on the Start screen if they want a copy
  // of the live saved to their profile for later replay.  Defaults
  // off so broadcasters who don't care never see network activity
  // tied to recording.
  // Save-to-profile preference.  Defaults ON for owners so a
  // broadcaster who forgets to flip the toggle still has the recording
  // archived automatically — they can still hit "Discard" on the
  // post-broadcast summary if they change their mind.  Recording itself
  // is opportunistic: we ALWAYS run MediaRecorder while live (so a
  // user who toggles ON mid-stream gets the full broadcast back), and
  // this flag only controls whether the buffered blob auto-uploads on
  // stop or waits for an explicit Save click.
  const [saveBroadcastEnabled, setSaveBroadcastEnabled] = useState(true);
  const [savedRecordingNotice, setSavedRecordingNotice] = useState<string | null>(null);
  /**
   * Buffered recording awaiting a save decision.  Populated by
   * `handleStopBroadcast` whenever the MediaRecorder produced a blob;
   * cleared by either the auto-upload path, an explicit Save click, or
   * Discard.  While set, the post-broadcast summary renders Save /
   * Discard buttons so a broadcaster who didn't pre-opt-in (or whose
   * broadcast hit the duration cap before they could click Save) can
   * still archive the tape — see `verifyUploadGrant` on the server.
   */
  const [pendingRecording, setPendingRecording] = useState<null | {
    blob: Blob;
    startedAt: string;
    endedAt: string;
    durationSec: number;
    events: RecordedEvent[];
    sessionId: string;
    grantToken: string;
    title: string | null;
  }>(null);
  const [savingPendingRecording, setSavingPendingRecording] = useState(false);
  const broadcastTitleRef = useRef<string | null>(null);
  // Grant token is only used transiently inside handleStartBroadcast via a
  // local (liveGrant); we keep it readable in state so the Live Tools
  // control panel (which mutates server-side tool config) can authenticate
  // its writes with the same HMAC grant the broadcaster has already paid for.
  const [activeGrant, setActiveGrant] = useState<string | null>(null);
  const [notice, setNotice]       = useState<string | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);

  // Streams are kept in state so the associated <video> elements can be
  // rendered *first* and then have their srcObject attached in an effect.
  // Setting srcObject before the element is mounted silently drops the
  // stream and leaves the user staring at a black box.
  const [localStream,  setLocalStream]  = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // Viewer-side fallback for "signaling-only" broadcasts (the headless
  // MCP / geoai `start_agent_live` peer never produces RTP). When the
  // server's welcome reports broadcasterMode === 'signaling_only', we
  // build a local AgentBroadcastEngine (Ken-Burns avatar canvas + audio
  // mixer), feed every incoming `live_agent_caption` into it, and pipe
  // its synthetic MediaStream into the existing `<video>` element so
  // the watcher sees the agent's avatar and hears the agent's voice
  // even though no RTP ever crosses the network. Held in a ref so
  // callbacks captured at handleWatch time can dispose the engine
  // imperatively without re-firing the joinBroadcast effect.
  const signalingOnlyEngineRef = useRef<AgentBroadcastEngine | null>(null);
  /**
   * Pending caption buffer.
   *
   * The hub can deliver `live_agent_caption` events to a viewer
   * BEFORE the broadcaster `welcome` frame finishes propagating
   * (small race in the WS connect → join → first-message order on
   * a freshly-joined room). Without a buffer, the very first
   * caption + audio after Watch is silently dropped because
   * `signalingOnlyEngineRef.current` is still null when
   * `onAgentCaption` fires. We park up to MAX_PENDING_CAPTIONS in
   * order, then drain them through `pushCaption()` the moment
   * `onMode` builds the engine. (Hypothesis C in the geoai
   * report.) Older entries are dropped if the buffer overflows
   * — better to lose stale audio than to spam viewers with a
   * burst once the engine catches up.
   */
  const pendingCaptionsRef = useRef<AgentCaptionEvent[]>([]);
  const MAX_PENDING_CAPTIONS = 5;
  const disposeSignalingOnlyEngine = useCallback(() => {
    const eng = signalingOnlyEngineRef.current;
    signalingOnlyEngineRef.current = null;
    pendingCaptionsRef.current = [];
    try {
      if ((window as any).__gtSignalingEngine === eng) {
        delete (window as any).__gtSignalingEngine;
      }
    } catch { /* SSR / strict CSP */ }
    if (eng) {
      try { eng.dispose(); } catch { /* idempotent — safe to call twice */ }
    }
  }, []);

  // ── Camera enumeration ────────────────────────────────────────────────────
  //
  // The MediaDevices API lets us list every camera attached to the
  // device, so phones with front+rear can pick either side (and flip
  // mid-broadcast), while platforms that expose zero cameras (Tesla's
  // infotainment browser, kiosk hardware, desktops without a webcam)
  // are detected up-front so we can steer the user toward watching
  // from this device rather than hitting `NotFoundError` at start.
  const [videoDevices, setVideoDevices] = useState<VideoDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [cameraProbeError, setCameraProbeError] = useState<NormalisedCaptureError | null>(null);
  const [flipping, setFlipping] = useState(false);

  // When viewing someone *else's* profile, we poll the discovery
  // endpoint so the UI knows the owner is live without waiting for a
  // click on "Watch live" that would fail with `broadcaster_not_live`.
  // The server returns the same LiveSession shape as the owner gets
  // back from `requestLiveSession`, so the HUD can show the same
  // countdown + approx-location pills both sides.
  const [remoteLiveSession, setRemoteLiveSession] = useState<LiveSession | null>(null);

  // Minimised mode lets the broadcaster browse the map without tearing
  // down the paid session.  We keep the overlay mounted (so WebRTC
  // state survives), and only swap the UI shell for a compact pill.
  const [minimized, setMinimized] = useState(false);

  // Effective live flag:  prop > owner's own broadcast > discovered remote session.
  const isLive = isLiveProp || !!broadcast || !!remoteLiveSession;

  // We track the <video> elements in *state* (not a plain ref) so the
  // srcObject-attach effect below re-runs whenever the element mounts
  // or unmounts.  Previously, minimising the overlay tore down the
  // <video> DOM node and on restore a new element was mounted, but
  // `useRef` doesn't trigger any re-render so `srcObject` was never
  // reattached — the user saw a black frame until they stopped and
  // restarted the broadcast.  Tracking the element as state fixes it.
  const [localVideoEl,  setLocalVideoEl]  = useState<HTMLVideoElement | null>(null);
  const [remoteVideoEl, setRemoteVideoEl] = useState<HTMLVideoElement | null>(null);

  // Countdown target:  prefer the owner's own session (owner side) then
  // fall back to the discovery-polled `remoteLiveSession` (viewer side)
  // so the timer ticks identically for everyone watching.
  const countdownTarget: string | null =
    (phase === 'live' && activeSession?.scheduledEndAt) ? activeSession.scheduledEndAt :
    (remoteLiveSession?.scheduledEndAt) ? remoteLiveSession.scheduledEndAt :
    null;
  const remaining = useCountdown(countdownTarget);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !wallet) return;
    let active = true;
    setLoadingProfile(true);
    setProfileError(null);
    const loader = isOwner ? getProfile : getPublicProfile;
    loader(wallet)
      .then((r) => {
        if (!active) return;
        setRecord(r);
        setDraft(r);
      })
      .catch((err) => {
        if (!active) return;
        setProfileError(err?.message || 'Failed to load profile');
      })
      .finally(() => { if (active) setLoadingProfile(false); });
    return () => { active = false; };
  }, [isOpen, wallet, isOwner]);

  // Unmount-only cleanup: tear down the active broadcast/viewer
  // controllers and release camera/mic tracks so OS-level indicators turn
  // off even if handleStopBroadcast/handleStopWatch didn't get a chance
  // to run (overlay closed, component swapped, route change).
  //
  // IMPORTANT: this effect MUST have an empty dep array.  An earlier
  // version listed [broadcast, viewer, localStream, remoteStream] so the
  // cleanup closure always had the latest values, but that made React
  // run the cleanup on *every* state change too — including the
  // `remoteStream` transition fired by `ontrack`.  The side effect was
  // that clicking "Watch live" stopped the viewer the instant the first
  // frame arrived, which presented as the overlay opening and then
  // immediately closing.  Refs fix that: the effect only fires on unmount
  // and reads the latest values at that moment.
  const broadcastRef     = useRef<BroadcastController | null>(null);
  const viewerRef        = useRef<ViewerController | null>(null);
  const localStreamRef   = useRef<MediaStream | null>(null);
  const remoteStreamRef  = useRef<MediaStream | null>(null);
  const activeSessionRef = useRef<LiveSession | null>(null);
  useEffect(() => { broadcastRef.current     = broadcast;     }, [broadcast]);
  useEffect(() => { viewerRef.current        = viewer;        }, [viewer]);
  useEffect(() => { localStreamRef.current   = localStream;   }, [localStream]);
  useEffect(() => { remoteStreamRef.current  = remoteStream;  }, [remoteStream]);
  useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);
  const activeGrantRef = useRef<string | null>(null);
  useEffect(() => { activeGrantRef.current = activeGrant; }, [activeGrant]);

  /**
   * MediaRecorder-backed VOD pipeline.  start() / stop() are called
   * from the broadcast lifecycle below — start when we go live, stop
   * (and upload) when the broadcaster ends.  We capture `recorder`
   * onto a ref so the stop callback can read the latest value
   * without re-running every dependency change.
   */
  const recorder = useBroadcastRecorder({
    getStream: () => localStreamRef.current,
  });
  const recorderRef = useRef(recorder);
  useEffect(() => { recorderRef.current = recorder; }, [recorder]);

  // Slice 6b-ui — wallet-linked drones / vehicles / GCS for RTP ingest picker
  // and live-tools entity quick-picks (single fetch while overlay is open as owner).
  useEffect(() => {
    if (!isOpen || !wallet || !isOwner) return;
    let cancelled = false;
    listEntities({ wallet })
      .then((list) => {
        if (cancelled) return;
        const drones = list.filter((e) =>
          e.entityType === 'drone'
          || e.entityType === 'vehicle'
          || e.entityType === 'ground_control_station',
        );
        setBroadcasterDroneEntities(drones);
      })
      .catch(() => { if (!cancelled) setBroadcasterDroneEntities([]); });
    return () => { cancelled = true; };
  }, [isOpen, wallet, isOwner]);

  /**
   * Ref-mirror of `remoteLiveSession` for the same reason the
   * broadcaster side mirrors its own session: the viewer's
   * `shouldResume` predicate is captured into the joinBroadcast
   * closure at call time, but the session it needs to check can be
   * extended later (session_extended pushes a new scheduledEndAt).
   * Reading via the ref avoids staleness.
   */
  const remoteLiveSessionRef = useRef<LiveSession | null>(null);
  useEffect(() => { remoteLiveSessionRef.current = remoteLiveSession; }, [remoteLiveSession]);
  useEffect(() => () => {
    try { broadcastRef.current?.stop(); } catch { /* ignore */ }
    try { viewerRef.current?.stop();    } catch { /* ignore */ }
    try { localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } }); } catch { /* ignore */ }
    try { remoteStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } }); } catch { /* ignore */ }
    // The signaling-only engine owns its own MediaStream + AudioContext;
    // they would otherwise leak across overlay close → reopen.
    try { signalingOnlyEngineRef.current?.dispose(); } catch { /* ignore */ }
    signalingOnlyEngineRef.current = null;
  }, []);

  // Bubble a unified "is a live session currently active?" signal up to
  // the parent so it can gate behaviours that would otherwise tear the
  // overlay apart (e.g. auto-switching the profile when the map marker
  // for a different wallet is clicked — see App.tsx).
  useEffect(() => {
    const active = !!broadcast || !!viewer;
    onSessionActiveChange?.(active);
  }, [broadcast, viewer, onSessionActiveChange]);

  // ── Live-session discovery (viewer + same-wallet second device) ───────────
  //
  // Poll the discovery endpoint every 10 s to see if this wallet has a
  // paid broadcast running somewhere.  The poll runs in two scenarios:
  //
  //   1. We're viewing someone *else's* profile (classic viewer side).
  //   2. We're viewing our own profile on a second device (e.g. the
  //      phone is broadcasting and we opened the same wallet on a
  //      Tesla / desktop).  In that case the owner-side phase machine
  //      has no local broadcast, so we fall back to discovery and
  //      offer a "Watch" button instead of the start flow.
  //
  // We skip the poll only when the owner is already broadcasting
  // locally (their phase machine is authoritative) or when the overlay
  // is closed.
  useEffect(() => {
    if (!isOpen || !wallet) return;
    if (isOwner && !!broadcast) return;
    let alive = true;
    const tick = async () => {
      try {
        const sessions = await listActiveBroadcasts(wallet);
        if (!alive) return;
        setRemoteLiveSession(sessions.length > 0 ? sessions[0] : null);
      } catch {
        if (!alive) return;
        setRemoteLiveSession(null);
      }
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { alive = false; clearInterval(id); };
  }, [isOpen, wallet, isOwner, broadcast]);

  // ── Camera enumeration (owner side) ────────────────────────────────────────
  //
  // Populate the camera picker whenever the owner opens the Live
  // controls.  We re-run when the selected device changes so the UI
  // reflects the current selection; and on `devicechange` events so a
  // freshly plugged-in USB webcam or an OS-level permission flip
  // surfaces immediately.
  useEffect(() => {
    if (!isOpen || !isOwner) return;
    let alive = true;

    const refresh = async () => {
      const devices = await listVideoDevices();
      if (!alive) return;
      setVideoDevices(devices);
      // If the user hadn't picked anything yet, default to whatever
      // the platform wants.  On phones the first videoinput is
      // typically the front ("user") camera.
      setSelectedDeviceId((prev) => {
        if (prev && devices.some((d) => d.deviceId === prev)) return prev;
        return devices[0]?.deviceId || null;
      });
      // No cameras at all → remember the probe error so the UI can
      // steer the user to "Watch from this device" instead of a
      // confusing `NotFoundError` when they click Start.
      setCameraProbeError(devices.length === 0
        ? { kind: 'no_camera_device', message: 'No camera detected on this device.' }
        : null);
    };

    refresh();
    const md = (typeof navigator !== 'undefined' && navigator.mediaDevices) || null;
    const handler = () => { void refresh(); };
    try { md?.addEventListener?.('devicechange', handler); } catch { /* ignore */ }
    return () => {
      alive = false;
      try { md?.removeEventListener?.('devicechange', handler); } catch { /* ignore */ }
    };
  }, [isOpen, isOwner]);

  // Attach the captured stream onto the <video> element whenever
  // *either* the element or the stream changes.  Keying the effect on
  // the element (tracked via state above) ensures that a remount —
  // e.g. unminimise, tab switch back to "Live" — correctly reattaches
  // srcObject instead of leaving a freshly-mounted <video> blank.
  useEffect(() => {
    if (!localVideoEl) return;
    if (localVideoEl.srcObject !== localStream) {
      localVideoEl.srcObject = localStream;
    }
    if (localStream) {
      localVideoEl.play?.().catch(() => { /* autoplay may be blocked until user gesture */ });
    }
  }, [localVideoEl, localStream]);

  useEffect(() => {
    if (!remoteVideoEl) return;
    if (remoteVideoEl.srcObject !== remoteStream) {
      remoteVideoEl.srcObject = remoteStream;
    }
    if (remoteStream) {
      remoteVideoEl.play?.().catch(() => { /* ignore autoplay rejection */ });
    }
  }, [remoteVideoEl, remoteStream]);

  // Stabilise the country value so the policy effect below doesn't
  // keep re-firing while the idle globe auto-spins the map centre
  // through new countries in the background.  We capture `country`
  // (plus its display metadata) on two events only:
  //
  //   1. The open edge (`isOpen` transitions false → true).  The user
  //      is about to read the Live tab, so we want a stable snapshot.
  //   2. Geolocation transitioning from unknown → known.  This is the
  //      "Use my location" button we surface on the Live tab when the
  //      country is undetermined — it's an explicit user action and
  //      should refresh the policy right away.
  //
  // Closing and reopening the overlay re-captures, giving the user a
  // simple manual refresh: "move the map, tap profile again".
  const [stableCountry,       setStableCountry]       = useState<number | undefined>(country);
  const [stableCountryName,   setStableCountryName]   = useState<string | undefined>(countryName);
  const [stableCountrySource, setStableCountrySource] = useState<typeof countrySource>(countrySource);
  const wasOpenRef        = useRef<boolean>(false);
  const lastHasLocationRef = useRef<boolean | undefined>(hasLocation);
  useEffect(() => {
    const justOpened     = isOpen && !wasOpenRef.current;
    const locationJustOn = isOpen && !!hasLocation && !lastHasLocationRef.current;
    if (justOpened || locationJustOn) {
      setStableCountry(country);
      setStableCountryName(countryName);
      setStableCountrySource(countrySource);
    }
    wasOpenRef.current = isOpen;
    lastHasLocationRef.current = hasLocation;
  }, [isOpen, hasLocation, country, countryName, countrySource]);

  useEffect(() => {
    if (!isOpen || !isOwner) return;
    if (stableCountry == null) {
      setPhase('policy_blocked');
      setNotice('country_unknown');
      return;
    }
    let active = true;
    setPhase('loading_policy');
    getWebRtcPolicyConfig(stableCountry)
      .then((cfg) => {
        if (!active) return;
        setPolicy(cfg);
        if (cfg.policy === WEBRTC_POLICY.DISALLOWED || cfg.policy === WEBRTC_POLICY.NOT_SET) {
          setPhase('policy_blocked');
        } else {
          setPhase('ready_to_start');
        }
      })
      .catch(() => {
        if (!active) return;
        setPhase('policy_blocked');
        setNotice('policy_load_failed');
      });
    return () => { active = false; };
  }, [isOpen, isOwner, stableCountry]);

  // ── Profile actions ────────────────────────────────────────────────────────

  const handleSaveClicked = useCallback(() => {
    if (!isOwner) return;
    if (validationError) return;
    setPendingSave(true);
  }, [isOwner, validationError]);

  const commitSave = useCallback(async () => {
    setPendingSave(false);
    if (!isOwner) return;
    setSaving(true);
    setProfileError(null);
    try {
      await setProfileOnChain({
        owner: wallet,
        input: {
          displayName:   draft.displayName,
          bio:           draft.bio,
          avatarUrl:     draft.avatarUrl,
          xHandle:       draft.xHandle,
          discordHandle: draft.discordHandle,
          isPrivate:     draft.isPrivate,
        },
        passkey,
        wallet: stellarWallet,
      });
      const fresh = await getProfile(wallet);
      setRecord(fresh);
      setDraft(fresh);
      setEditing(false);
      onSaved?.(fresh);
    } catch (err: any) {
      setProfileError(err?.message || 'Failed to write profile on-chain');
    } finally {
      setSaving(false);
    }
  }, [isOwner, wallet, draft, passkey, stellarWallet, onSaved]);

  const togglePrivacyOnly = useCallback(async (next: boolean) => {
    if (!isOwner) return;
    setSaving(true);
    setProfileError(null);
    try {
      await setPrivacyOnChain({ owner: wallet, isPrivate: next, passkey, wallet: stellarWallet });
      const fresh = await getProfile(wallet);
      setRecord(fresh);
      setDraft(fresh);
      onSaved?.(fresh);
    } catch (err: any) {
      setProfileError(err?.message || 'Failed to update privacy flag');
    } finally {
      setSaving(false);
    }
  }, [isOwner, wallet, passkey, stellarWallet, onSaved]);

  // ── Live-session actions ───────────────────────────────────────────────────

  // IMPORTANT: read mutable handles via refs, not render-time closures.
  // Callbacks passed into webrtcBroadcastService (onSessionEnded / onEnded)
  // capture whichever version of this handler existed when joinBroadcast /
  // startBroadcast ran — at that moment broadcast/localStream were still
  // null, so a closure-based handler could not actually stop the
  // controller.  The service's internal `stopped` flag therefore never
  // flipped to true, and its ws.on('close') listener fired a second
  // onSessionEnded('socket_closed') that clobbered the real ended reason
  // (we saw "Signaling connection closed" instead of "Stopped by
  // broadcaster" or "Max live duration reached").
  const handleStopBroadcast = useCallback(async (informBackend: boolean = true) => {
    // Finalise any still-open audience entries so the summary card
    // sees a `leftAt` for each session — without this the last
    // wave of viewers (who didn't send `left` because we shut down
    // the room first) would appear as if they're still watching.
    const stopTs = Date.now();
    setAudienceLog((prev) => prev.map((e) => e.leftAt == null ? { ...e, leftAt: stopTs } : e));
    // Snapshot the session credentials BEFORE we tear them down — the
    // recording upload below needs an active grant to authenticate,
    // and the session id is what binds the recording to the paid
    // broadcast it came from.
    const sessionForUpload = activeSessionRef.current;
    const grantForUpload   = activeGrant;
    const ingestIdSnapshot = liveDroneIngestIdRef.current;
    liveDroneIngestIdRef.current = null;
    setLiveDroneIngest(null);
    setLiveVideoSource('camera');
    setLiveSourceDraft('camera');
    if (ingestIdSnapshot && sessionForUpload?.id && grantForUpload) {
      try {
        await stopSfuIngest(ingestIdSnapshot, { sessionId: sessionForUpload.id, grantToken: grantForUpload });
      } catch { /* ignore */ }
    }
    const recorderActive   = recorderRef.current.state.recording;
    // Kick off the recorder finalisation in parallel with the rest of
    // the teardown.  The Promise resolves with the assembled blob;
    // if it fails for any reason we just skip the upload — the live
    // broadcast itself ending matters more than archiving it.
    const recordingResultP = recorderActive
      ? recorderRef.current.stop().catch(() => null)
      : Promise.resolve(null);
    try { broadcastRef.current?.stop(); } catch { /* ignore */ }
    setBroadcast(null);
    setViewerCount(0);
    const streamSnapshot = localStreamRef.current;
    try {
      streamSnapshot?.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
    } catch { /* ignore */ }
    if (localVideoEl) {
      try { localVideoEl.srcObject = null; } catch { /* ignore */ }
    }
    setLocalStream(null);
    // Clear any pre-broadcast pending state so a cancel during the
    // "Start Live" confirmation screen doesn't leave a dangling
    // session id + grant that could later try to auto-resume.
    pendingGoLiveRef.current = null;
    setCountdownSec(null);
    // Tear down any in-flight screen share so the hidden canvas +
    // rAF loop don't leak after the broadcast ends.
    try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    screenStreamRef.current = null;
    cameraTrackBeforeShareRef.current = null;
    if (compositeRafRef.current != null) {
      try { cancelAnimationFrame(compositeRafRef.current); } catch { /* ignore */ }
      compositeRafRef.current = null;
    }
    try { compositeStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    compositeStreamRef.current = null;
    compositeCanvasRef.current = null;
    compositeCtxRef.current = null;
    compositeCameraVideoRef.current = null;
    compositeScreenVideoRef.current = null;
    setScreenShareMode(null);
    const session = activeSessionRef.current;
    // Wait for the recorder so we know whether we have a blob to
    // archive.  The upload itself can run AFTER `stopLiveSession`
    // because the broadcasts route uses `verifyUploadGrant`, which
    // accepts the just-ended session for a 30-minute grace window
    // (see server/services/paidLiveSessionService.js).  This means
    // we don't have to delay backend teardown for the upload.
    let recordingResult: { blob: Blob; startedAt: string; endedAt: string; durationSec: number; events: RecordedEvent[] } | null = null;
    if (recorderActive) {
      try { recordingResult = await recordingResultP; } catch { recordingResult = null; }
    }
    const haveTape =
      !!recordingResult &&
      recordingResult.blob.size > 0 &&
      !!sessionForUpload &&
      !!grantForUpload;
    const wantedAutoSave = saveBroadcastEnabled;
    // Kick off the auto-upload (if the broadcaster opted in) without
    // blocking the rest of the teardown.  Errors funnel into the
    // "saved recording" banner so the broadcaster sees a single,
    // human-readable message either way.
    if (haveTape && wantedAutoSave) {
      void uploadBroadcast({
        sessionId:    sessionForUpload!.id,
        grantToken:   grantForUpload!,
        blob:         recordingResult!.blob,
        startedAt:    recordingResult!.startedAt,
        endedAt:      recordingResult!.endedAt,
        durationSec:  recordingResult!.durationSec,
        title:        broadcastTitleRef.current,
        events:       recordingResult!.events,
      })
        .then(() => setSavedRecordingNotice('Saved to your profile under "Past broadcasts".'))
        .catch((err: any) => setSavedRecordingNotice(humanizeRecordingSaveError(err?.message)));
    } else if (haveTape && !wantedAutoSave) {
      // Stash the buffered blob so the post-broadcast summary can
      // offer Save / Discard.  We hold onto the grant + session id
      // here because the user might dwell on the summary for a few
      // minutes before deciding — by that point `activeSession` /
      // `activeGrant` may already have been cleared by other state
      // transitions.
      setPendingRecording({
        blob:        recordingResult!.blob,
        startedAt:   recordingResult!.startedAt,
        endedAt:     recordingResult!.endedAt,
        durationSec: recordingResult!.durationSec,
        events:      recordingResult!.events,
        sessionId:   sessionForUpload!.id,
        grantToken:  grantForUpload!,
        title:       broadcastTitleRef.current,
      });
    }
    if (informBackend && session) {
      try { await stopLiveSession(session.id); } catch { /* ignore */ }
    }
    onBroadcastStateChange?.(false);
    setPhase((p) => (p === 'ended' ? p : 'idle'));
  }, [onBroadcastStateChange, localVideoEl, activeGrant, saveBroadcastEnabled]);

  /**
   * Post-broadcast "Save copy to my profile" handler.  Uploads the
   * blob the recorder buffered while live, using the upload-grant
   * window the backend opens for ~30 minutes after the session
   * flips to ENDED_*.  Idempotent: a successful upload clears the
   * pending blob; failure leaves it in place so the broadcaster can
   * retry without re-broadcasting.
   */
  const handleSaveNow = useCallback(async () => {
    const pending = pendingRecording;
    if (!pending || savingPendingRecording) return;
    setSavingPendingRecording(true);
    setSavedRecordingNotice(null);
    try {
      await uploadBroadcast({
        sessionId:    pending.sessionId,
        grantToken:   pending.grantToken,
        blob:         pending.blob,
        startedAt:    pending.startedAt,
        endedAt:      pending.endedAt,
        durationSec:  pending.durationSec,
        title:        pending.title,
        events:       pending.events,
      });
      setSavedRecordingNotice('Saved to your profile under "Past broadcasts".');
      setPendingRecording(null);
    } catch (err: any) {
      setSavedRecordingNotice(humanizeRecordingSaveError(err?.message));
    } finally {
      setSavingPendingRecording(false);
    }
  }, [pendingRecording, savingPendingRecording]);

  const handleDiscardRecording = useCallback(() => {
    setPendingRecording(null);
    setSavedRecordingNotice(null);
  }, []);

  const handleStartBroadcast = useCallback(async () => {
    setNotice(null); setEndedReason(null);
    setAudienceLog([]);
    setBroadcastPaused(false);
    // STRICT location guard.  The country that gates policy + pricing
    // is resolved from a 3-tier fallback in App.tsx (location → marker
    // → map centre).  Map-centre fallbacks would let a viewer in
    // country A pay for a country-B broadcast and publish a session
    // with no real `approxLocation`, leaving the live marker
    // unpinnable on the map.  We refuse to start a paid broadcast
    // unless the country was resolved from the viewer's actual GPS.
    if (countrySource !== 'location' || !hasLocation) {
      setPhase('policy_blocked');
      setNotice('location_required_for_broadcast');
      return;
    }
    if (!policy || policy.policy !== WEBRTC_POLICY.ALLOWED) {
      setPhase('policy_blocked');
      setNotice(policy?.policy === WEBRTC_POLICY.DISALLOWED ? 'webrtc_disallowed_in_country' : 'webrtc_policy_not_set');
      return;
    }

    // Resolve the live-presence scope. A trust-group broadcast first
    // creates an on-chain geo-session (start_session) so the SFU can
    // resolve the member allowlist from chain; that same scope governs
    // map presence. If the on-chain step fails we abort rather than
    // silently going public — the broadcaster explicitly chose privacy.
    let visibility: 'public' | 'groups' = 'public';
    let onchainSessionId: number | null = null;
    const wantGroups = broadcastVisibility === 'groups' && selectedGroupIds.length > 0;
    if (wantGroups) {
      if (!onStartOnchainSession) {
        setPhase('policy_blocked'); setNotice('trust_group_scope_unavailable');
        return;
      }
      try {
        onchainSessionId = await onStartOnchainSession({
          isPublic: false,
          country: country == null ? null : Number(country),
          trustGroupIds: selectedGroupIds,
        });
      } catch (err: any) {
        setPhase('policy_blocked'); setNotice(err?.message || 'start_session_failed');
        return;
      }
      if (onchainSessionId == null) {
        setPhase('policy_blocked'); setNotice('start_session_failed');
        return;
      }
      visibility = 'groups';
    }

    let result;
    try {
      result = await requestLiveSession({
        wallet, country: country == null ? undefined : Number(country),
        broadcasterId: wallet,
        approxLocation: approxLocation && Number.isFinite(approxLocation.lat) && Number.isFinite(approxLocation.lng)
          ? { lat: approxLocation.lat, lng: approxLocation.lng }
          : undefined,
        visibility,
        onchainSessionId,
        trustGroupIds: visibility === 'groups' ? selectedGroupIds : undefined,
      });
    } catch (err: any) {
      const reason = err?.payload?.reason || err?.message || 'request_failed';
      setPhase('policy_blocked'); setNotice(reason);
      return;
    }

    setActiveSession(result.session);
    setActiveGrant(result.grantToken);

    // Keep the authoritative session + grant in locals so we never
    // read a stale value from `activeGrant` state inside this callback.
    let liveSession = result.session;
    let liveGrant   = result.grantToken;

    if (result.requiresPayment) {
      if (!onExecuteLivePayment) {
        await markLivePaymentFailed(result.session.id, 'payment_handler_missing').catch(() => {});
        setPhase('ended'); setEndedReason('payment_handler_missing');
        return;
      }
      setPhase('payment_pending');
      try {
        const receipt = await onExecuteLivePayment({
          sessionId: result.session.id,
          country: result.session.country,
          wallet,
          amountStroops: result.sessionCost,
          amountDisplay: stroopsToDisplay(result.sessionCost),
        });
        const confirmed = await confirmLivePayment(result.session.id, {
          paymentReference: receipt.paymentReference,
          paidAmount: receipt.paidAmount || result.sessionCost,
        });
        setActiveSession(confirmed.session);
        setActiveGrant(confirmed.grantToken);
        liveSession = confirmed.session;
        liveGrant   = confirmed.grantToken;
      } catch (err: any) {
        const reason = err?.message || 'payment_failed';
        try { await markLivePaymentFailed(result.session.id, reason); } catch { /* ignore */ }
        setPhase('ended'); setEndedReason(reason);
        return;
      }
    }

    // Open the camera early (while the broadcaster reviews their
    // "Start Live" confirmation screen) so a cold-start latency
    // from getUserMedia doesn't stretch the 3-2-1 countdown into
    // 5-6 seconds on slow hardware.  The stream is held in state
    // and only handed to the SFU path inside `beginLiveStream`.
    let stream: MediaStream | null = null;
    try {
      stream = await captureStream({
        deviceId: selectedDeviceId || undefined,
        audio: true,
      });
    } catch (captureErr: unknown) {
      const norm = captureErr as NormalisedCaptureError;
      const reason = (norm && typeof norm === 'object' && 'kind' in norm)
        ? norm.kind
        : 'camera_not_allowed';
      try { await stopLiveSession(liveSession.id); } catch { /* ignore */ }
      setPhase('ended'); setEndedReason(reason);
      return;
    }
    setLocalStream(stream);
    // Stash credentials on a ref so `handleGoLive` — which runs
    // after the countdown user-gesture — can pick them up without
    // recomputing session state.
    pendingGoLiveRef.current = { grant: liveGrant, sid: liveSession.id };
    setPhase('awaiting_go_live');
  }, [policy, wallet, country, countrySource, hasLocation, approxLocation, onExecuteLivePayment, selectedDeviceId, broadcastVisibility, selectedGroupIds, onStartOnchainSession]);

  // Holds the session id + grant token between "payment confirmed"
  // and "Start Live clicked + countdown finished".  Kept as a ref
  // so the async countdown timer doesn't accidentally read a stale
  // state value if the user somehow triggers a re-render while the
  // 3-2-1 ticks down.
  const pendingGoLiveRef = useRef<{ grant: string; sid: string } | null>(null);

  /**
   * Append a newly-received live gift to the toast queue.  We cap
   * the visible list at 6 entries so a viral broadcast with a
   * stream of tips doesn't hose the DOM, and each toast auto-
   * dismisses after ~6 seconds via a timer queued here.
   *
   * Declared before `beginLiveStream` / `handleWatch` because both
   * of them capture it in their `onGift` callbacks — the
   * exhaustive-deps lint requires it in their dep arrays and
   * TypeScript enforces the forward-declaration order.
   */
  const pushGiftToast = useCallback((gift: LiveGiftEvent) => {
    setGiftToasts((prev) => {
      const next = [...prev, gift];
      return next.length > 6 ? next.slice(next.length - 6) : next;
    });
    window.setTimeout(() => {
      setGiftToasts((prev) => prev.filter((g) => g !== gift));
    }, 6000);
  }, []);

  /**
   * Internal helper — given an already-captured local stream and
   * the session credentials we stashed at payment time, spin up the
   * SFU publisher and wire the viewer-count + reconnect callbacks.
   * This is called both by the post-countdown "Go live now" path
   * and (indirectly) by any future auto-retry logic that bypasses
   * the countdown (e.g. after a failed first connection where the
   * grant is still valid).
   */
  const beginLiveStream = useCallback(async () => {
    const creds = pendingGoLiveRef.current;
    const stream = localStreamRef.current;
    if (!creds || !stream) {
      setPhase('ended'); setEndedReason('internal_go_live_missing_state');
      return;
    }
    setPhase('connecting');
    try {
      const grant = creds.grant;
      const sid   = creds.sid;
      const ctrl = startBroadcast(stream, {
        broadcasterId: wallet,
        wallet,
        country: country == null ? undefined : Number(country),
        liveSessionId: sid,
        grantToken: grant,
        // Fallback local counting — the server's `viewers_update` is
        // authoritative and will overwrite this, but we keep the optimistic
        // increment so the HUD doesn't lag a roundtrip when a viewer joins.
        onViewerJoined: (info) => {
          setViewerCount((v) => v + 1);
          setAudienceLog((prev) => {
            // Ignore duplicate joins for the same peerId (a brief
            // signaling reconnect could technically re-announce).
            if (prev.some((e) => e.peerId === info.peerId && e.leftAt == null)) return prev;
            return [...prev, {
              peerId: info.peerId,
              wallet: info.wallet,
              country: info.country ?? null,
              joinedAt: Date.now(),
              leftAt: null,
            }];
          });
        },
        onViewerLeft: (peerId) => {
          setViewerCount((v) => Math.max(0, v - 1));
          setAudienceLog((prev) => prev.map((e) =>
            (e.peerId === peerId && e.leftAt == null)
              ? { ...e, leftAt: Date.now() }
              : e));
        },
        onViewersUpdate: (count) => setViewerCount(count),
        onDenied: (reason) => {
          setPhase('ended'); setEndedReason(reason);
          setReconnectingBroadcast(null);
          void handleStopBroadcast(false);
        },
        onSessionEnded: (reason) => {
          setPhase('ended'); setEndedReason(reason);
          setReconnectingBroadcast(null);
          void handleStopBroadcast(false);
        },
        onSessionExtended: ({ scheduledEndAt }) => {
          setActiveSession((prev) => prev ? { ...prev, scheduledEndAt } : prev);
        },
        // Keep trying as long as the paid session has time left — the
        // server will reject the reconnect with `session_not_live` /
        // `session_expired` once its authoritative duration timer
        // fires, at which point `onSessionEnded` takes over and
        // clears the reconnecting pill.
        shouldResume: () => {
          const s = activeSessionRef.current;
          if (!s || !s.scheduledEndAt) return false;
          return new Date(s.scheduledEndAt).getTime() > Date.now();
        },
        onReconnecting: (n) => setReconnectingBroadcast(n),
        onReconnected:  () => setReconnectingBroadcast(null),
        onGift: (gift) => {
          pushGiftToast(gift);
          // If a recording is in flight, log the gift onto its
          // event timeline so the VOD replays the same toast at
          // the same moment.  No-op when the broadcaster opted
          // not to save a copy.
          try { recorderRef.current.captureGiftEvent(gift); } catch { /* ignore */ }
        },
        onError: (err) => setNotice(err.message),
      });
      setBroadcast(ctrl);
      setPhase('live');
      setLiveVideoSource('camera');
      setLiveSourceDraft('camera');
      liveDroneIngestIdRef.current = null;
      setLiveDroneIngest(null);
      onBroadcastStateChange?.(true);
      // ALWAYS start the MediaRecorder here — recording is
      // opportunistic regardless of the `saveBroadcastEnabled`
      // toggle.  This way a broadcaster who didn't tick the
      // checkbox up-front (or who wants to start saving partway
      // through) can still archive the full broadcast: the
      // post-broadcast summary surfaces a "Save copy to my
      // profile" button while the buffered tape is still in
      // memory.  If the broadcaster never opts in, the blob is
      // discarded when the overlay unmounts.
      try { recorderRef.current.start(); } catch { /* ignore */ }
    } catch (err: any) {
      try { pendingGoLiveRef.current = null; } catch { /* ignore */ }
      try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      const reason = err?.message || 'broadcast_failed';
      setPhase('ended'); setEndedReason(reason);
    }
  }, [wallet, country, onBroadcastStateChange, handleStopBroadcast, pushGiftToast]);

  /**
   * The "Start Live" click handler — kicks off the 3-2-1 countdown
   * then hands off to `beginLiveStream`.  We drive the countdown
   * with a local mutable tick value rather than leaning on React
   * state, because `setCountdownSec(n - 1)` inside a setTimeout
   * captures the closure value at schedule time and makes the
   * countdown brittle under re-renders (the overlay re-mounts
   * on Android rotation, for instance).
   */
  /**
   * Toggle the broadcaster pause state.  Reads the current value
   * via the callback form so rapid double-taps converge on the
   * server's authoritative state (via the ws echo we get back)
   * rather than oscillating off a stale closure.
   */
  const handleTogglePause = useCallback(() => {
    const ctrl = broadcastRef.current;
    if (!ctrl) return;
    setBroadcastPaused((prev) => {
      const next = !prev;
      const applied = ctrl.setPaused?.(next);
      // If the controller couldn't apply the pause (e.g. no
      // active tracks yet) surface a notice and keep the
      // previous value — the UI shouldn't lie about the state.
      if (applied === false) {
        setNotice('pause_unavailable');
        return prev;
      }
      return next;
    });
  }, []);

  const handleGoLive = useCallback(() => {
    if (!pendingGoLiveRef.current) return;
    setPhase('countdown');
    setCountdownSec(3);
    let tick = 3;
    const id = setInterval(() => {
      tick -= 1;
      if (tick <= 0) {
        clearInterval(id);
        setCountdownSec(null);
        void beginLiveStream();
      } else {
        setCountdownSec(tick);
      }
    }, 1000);
  }, [beginLiveStream]);

  // Switch to a specific camera (or cycle to the next one if no id is
  // given).  When called before a broadcast starts we only update the
  // selection; when called mid-broadcast we capture a new video track
  // from the requested device and hot-swap every viewer's sender via
  // `replaceTrack`, so the switch is seamless (no reconnect, no gap).
  //
  // Critical sequencing note: many Android devices (Sony Xperia with
  // its shared-sensor multi-camera array being a notable offender,
  // but also certain Pixels and Samsungs) refuse to open a second
  // camera while any other camera on the same device is active,
  // surfacing as `NotReadableError: Camera is in use by another app`
  // even though the original track is from this same page.  The
  // portable recipe is:
  //
  //   1. Stop the currently-running video track (releases the sensor).
  //   2. Call getUserMedia for the new camera.
  //   3. If that fails, try to reacquire the *previous* camera so the
  //      broadcast doesn't go blank — otherwise surface the error and
  //      leave replaceTrack(null) in place so viewers know video
  //      paused rather than seeing a stale frozen frame.
  //
  // We intentionally capture video-only and preserve the existing
  // audio track so the mic permission prompt doesn't reappear and
  // any echo-cancellation state stays warm.
  const handleSwitchCamera = useCallback(async (deviceId?: string) => {
    setNotice(null);
    const current = videoDevices.find((d) => d.deviceId === selectedDeviceId) || null;
    const target = deviceId
      ? videoDevices.find((d) => d.deviceId === deviceId)
      : nextCameraDevice(current, videoDevices);
    if (!target) return;
    const nothingToSwap = !broadcastRef.current && !localStreamRef.current;
    if (nothingToSwap) {
      setSelectedDeviceId(target.deviceId);
      return;
    }
    setFlipping(true);
    const preview = localStreamRef.current;
    const prevVideo = preview?.getVideoTracks()[0] || null;
    const prevDeviceId = prevVideo?.getSettings().deviceId || selectedDeviceId || null;
    try {
      // Step 1: release the old camera BEFORE requesting the new one.
      // On viewers, `replaceTrack(null)` freezes the last frame and
      // keeps the audio/video transceivers alive so our subsequent
      // `replaceTrack(newVideo)` doesn't require renegotiation.
      if (prevVideo) {
        try { broadcastRef.current?.replaceTrack('video', null); } catch { /* ignore */ }
        if (preview) preview.removeTrack(prevVideo);
        try { prevVideo.stop(); } catch { /* ignore */ }
      }

      // Step 2: acquire the target camera.  If this throws, fall back
      // to the previous one so the broadcast recovers gracefully.
      let next: MediaStream;
      try {
        next = await captureStream({ deviceId: target.deviceId, audio: false });
      } catch (captureErr: unknown) {
        const norm = normaliseCaptureError(captureErr);
        try {
          if (prevDeviceId) {
            const recovered = await captureStream({ deviceId: prevDeviceId, audio: false });
            const recoveredVideo = recovered.getVideoTracks()[0] || null;
            if (recoveredVideo) {
              broadcastRef.current?.replaceTrack('video', recoveredVideo);
              preview?.addTrack(recoveredVideo);
            }
          }
        } catch { /* even the previous camera is gone — give up */ }
        setNotice(norm.kind === 'unknown' ? 'camera_switch_failed' : norm.kind);
        return;
      }
      const newVideo = next.getVideoTracks()[0] || null;
      if (!newVideo) {
        setNotice('camera_switch_failed');
        return;
      }

      // Step 3: push the new track to every viewer and into the
      // broadcaster's own preview so the HUD updates too.
      broadcastRef.current?.replaceTrack('video', newVideo);
      if (preview) {
        preview.addTrack(newVideo);
        // Safari (mac + iOS) sometimes caches the first frame of the
        // previous track when srcObject references the same stream —
        // re-assigning forces a repaint with the new track.
        if (localVideoEl) {
          try {
            localVideoEl.srcObject = preview;
            localVideoEl.play?.().catch(() => { /* autoplay may have been revoked */ });
          } catch { /* ignore */ }
        }
      }
      setSelectedDeviceId(target.deviceId);
    } catch (err: unknown) {
      const norm = normaliseCaptureError(err);
      setNotice(norm.kind === 'unknown' ? 'camera_switch_failed' : norm.kind);
    } finally {
      setFlipping(false);
    }
  }, [videoDevices, selectedDeviceId, localVideoEl]);

  /**
   * Fired after the gift modal successfully submits a Horizon
   * payment.  We (1) fan the gift out via the active controller
   * (viewer or broadcaster — whichever one holds the live
   * WebSocket to the room) so the broadcaster + every other
   * watcher sees the same toast, and (2) echo the gift locally
   * so the sender sees their own animation too — the server
   * doesn't round-trip the sender's own message.
   */
  const handleGiftSent = useCallback((info: {
    assetCode: string; assetIssuer: string | null;
    amount: string; memo: string; txHash: string;
  }) => {
    const ctrl = viewerRef.current || broadcastRef.current;
    if (ctrl) {
      try {
        ctrl.sendGift({
          assetCode: info.assetCode,
          assetIssuer: info.assetIssuer,
          amount: info.amount,
          memo: info.memo,
          txHash: info.txHash,
        });
      } catch { /* ignore */ }
    }
    pushGiftToast({
      fromPeerId: 'self',
      fromWallet: viewerAddress || null,
      assetCode: info.assetCode,
      assetIssuer: info.assetIssuer,
      amount: info.amount,
      memo: info.memo,
      txHash: info.txHash,
      sentAt: Date.now(),
    });
  }, [viewerAddress, pushGiftToast]);

  /**
   * Internal helper — tears down the composite pipeline (canvas +
   * rAF loop + hidden <video> elements).  Safe to call even if
   * the split-screen pipeline was never assembled.
   */
  const teardownCompositePipeline = useCallback(() => {
    if (compositeRafRef.current != null) {
      try { cancelAnimationFrame(compositeRafRef.current); } catch { /* ignore */ }
      compositeRafRef.current = null;
    }
    try { compositeStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    compositeStreamRef.current = null;
    try { compositeCameraVideoRef.current?.pause?.(); } catch { /* ignore */ }
    try { compositeScreenVideoRef.current?.pause?.(); } catch { /* ignore */ }
    compositeCameraVideoRef.current = null;
    compositeScreenVideoRef.current = null;
    compositeCtxRef.current = null;
    compositeCanvasRef.current = null;
  }, []);

  /**
   * Stop screen sharing — restores the camera track onto every
   * viewer and into the local preview.  Also runs cleanup when the
   * user ends the share via the browser's own "Stop sharing"
   * banner (the screen track fires an `ended` event which we
   * listen to below).
   */
  const handleStopScreenShare = useCallback(() => {
    try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    screenStreamRef.current = null;
    teardownCompositePipeline();

    const camTrack = cameraTrackBeforeShareRef.current;
    cameraTrackBeforeShareRef.current = null;

    const preview = localStreamRef.current;
    if (preview && camTrack) {
      // Drop any non-camera video tracks (screen or composite) and
      // put the camera back in as the sole video track.
      preview.getVideoTracks().forEach((t) => {
        if (t !== camTrack) { try { preview.removeTrack(t); } catch { /* ignore */ } }
      });
      if (!preview.getVideoTracks().includes(camTrack)) {
        try { preview.addTrack(camTrack); } catch { /* ignore */ }
      }
      if (localVideoEl) {
        try { localVideoEl.srcObject = preview; } catch { /* ignore */ }
      }
    }
    broadcastRef.current?.replaceTrack?.('video', camTrack || null);
    setScreenShareMode(null);
    setLiveVideoSource('camera');
  }, [localVideoEl, teardownCompositePipeline]);

  /**
   * Start (or switch) a screen share.  Modes:
   *   - `'fullscreen'`: replace the camera track with the screen track.
   *     Minimal machinery; the viewer sees exactly what's shared.
   *   - `'split'`: composite the camera on top of the screen in a
   *     picture-in-picture layout using a hidden <canvas>.  The
   *     composited stream becomes the outgoing video.
   */
  const handleStartScreenShare = useCallback(async (mode: 'fullscreen' | 'split') => {
    if (!broadcastRef.current) return;
    const getDisplayMedia = (navigator.mediaDevices as any)?.getDisplayMedia;
    if (typeof getDisplayMedia !== 'function') {
      setNotice('screen_share_unsupported');
      return;
    }
    setNotice(null);

    // Slice 6b-ui: screen capture replaces the browser video producer — tear
    // down any PlainTransport drone ingest first so viewers don't keep two
    // competing video producers.
    if (liveDroneIngestIdRef.current) {
      const sid = activeSessionRef.current?.id;
      const g = activeGrantRef.current;
      const iid = liveDroneIngestIdRef.current;
      liveDroneIngestIdRef.current = null;
      setLiveDroneIngest(null);
      if (sid && g) {
        try { await stopSfuIngest(iid, { sessionId: sid, grantToken: g }); } catch { /* ignore */ }
      }
    }

    // If we're already sharing, tear down the existing pipeline
    // before acquiring a new one — avoids two mic/audio captures
    // fighting for the same sink on macOS.
    if (screenShareMode) {
      teardownCompositePipeline();
      try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      screenStreamRef.current = null;
    } else {
      // First-time share: remember the camera track so we can
      // restore it when the user stops sharing.  The broadcast's
      // currentTracks.video is already this track so replaceTrack
      // can put it back without reacquiring the camera.
      cameraTrackBeforeShareRef.current = localStreamRef.current?.getVideoTracks()[0] || null;
    }

    let screenStream: MediaStream;
    try {
      screenStream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: { frameRate: 30 },
        // Capture tab/system audio when the browser offers it —
        // useful for demo'ing videos in the shared tab.  The user
        // still gets a checkbox in the picker they can untick.
        audio: true,
      });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User cancelled the share picker — not an error, just
        // bail out silently if this was the first attempt.
        if (!screenShareMode) {
          cameraTrackBeforeShareRef.current = null;
        }
        return;
      }
      setNotice('screen_share_failed');
      return;
    }
    screenStreamRef.current = screenStream;
    const screenTrack = screenStream.getVideoTracks()[0];
    if (!screenTrack) {
      setNotice('screen_share_no_track');
      return;
    }
    // Chrome fires `ended` on the track when the user clicks
    // "Stop sharing" in the browser's floating banner.  Treat
    // that as a clean stop so the camera restores automatically.
    try { screenTrack.addEventListener('ended', handleStopScreenShare); } catch { /* ignore */ }

    if (mode === 'fullscreen') {
      // Swap video on viewers + local preview.
      const preview = localStreamRef.current;
      if (preview) {
        preview.getVideoTracks().forEach((t) => { try { preview.removeTrack(t); } catch { /* ignore */ } });
        try { preview.addTrack(screenTrack); } catch { /* ignore */ }
        if (localVideoEl) {
          try { localVideoEl.srcObject = preview; } catch { /* ignore */ }
        }
      }
      broadcastRef.current.replaceTrack?.('video', screenTrack);
      setScreenShareMode('fullscreen');
      setLiveVideoSource('screen');
      return;
    }

    // Split mode — build the canvas compositor.
    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setNotice('screen_share_canvas_unavailable');
      try { screenTrack.stop(); } catch { /* ignore */ }
      return;
    }
    compositeCanvasRef.current = canvas;
    compositeCtxRef.current = ctx;

    // Off-DOM <video> elements play the two source streams so the
    // canvas compositor can sample them with `drawImage`.  They're
    // never attached to the document — just used as readable
    // video sources.
    const cameraTrack = cameraTrackBeforeShareRef.current;
    const cameraSrc = cameraTrack ? new MediaStream([cameraTrack]) : null;
    const cameraVideo = cameraSrc ? document.createElement('video') : null;
    if (cameraVideo && cameraSrc) {
      cameraVideo.srcObject = cameraSrc;
      cameraVideo.muted = true;
      cameraVideo.playsInline = true;
      try { await cameraVideo.play(); } catch { /* ignore */ }
      compositeCameraVideoRef.current = cameraVideo;
    }
    const screenVideo = document.createElement('video');
    screenVideo.srcObject = screenStream;
    screenVideo.muted = true;
    screenVideo.playsInline = true;
    try { await screenVideo.play(); } catch { /* ignore */ }
    compositeScreenVideoRef.current = screenVideo;

    // Kick off the render loop.  We target 30fps via rAF
    // throttling which matches the screenTrack frame rate.
    let lastDraw = 0;
    const targetFrameMs = 1000 / 30;
    const renderLoop = (ts: number) => {
      if (!compositeCtxRef.current || !compositeCanvasRef.current) return;
      if (ts - lastDraw < targetFrameMs) {
        compositeRafRef.current = requestAnimationFrame(renderLoop);
        return;
      }
      lastDraw = ts;
      const c = compositeCanvasRef.current;
      const cc = compositeCtxRef.current;
      cc.fillStyle = '#000';
      cc.fillRect(0, 0, c.width, c.height);
      // Screen share fills most of the frame; the camera PiP sits
      // in the bottom-right quadrant at 25% scale.
      const sv = compositeScreenVideoRef.current;
      if (sv && sv.readyState >= 2 && sv.videoWidth && sv.videoHeight) {
        // Letterbox the screen into the canvas preserving aspect.
        const r = Math.min(c.width / sv.videoWidth, c.height / sv.videoHeight);
        const w = sv.videoWidth * r;
        const h = sv.videoHeight * r;
        const x = (c.width - w) / 2;
        const y = (c.height - h) / 2;
        cc.drawImage(sv, x, y, w, h);
      }
      const cv = compositeCameraVideoRef.current;
      if (cv && cv.readyState >= 2 && cv.videoWidth && cv.videoHeight) {
        const pipW = Math.round(c.width * 0.25);
        const pipH = Math.round(pipW * (cv.videoHeight / cv.videoWidth));
        const pipX = c.width - pipW - 16;
        const pipY = c.height - pipH - 16;
        // Drop shadow card behind the PiP so it reads cleanly on
        // dark content.
        cc.fillStyle = 'rgba(15, 23, 42, 0.85)';
        cc.fillRect(pipX - 4, pipY - 4, pipW + 8, pipH + 8);
        cc.drawImage(cv, pipX, pipY, pipW, pipH);
      }
      compositeRafRef.current = requestAnimationFrame(renderLoop);
    };
    compositeRafRef.current = requestAnimationFrame(renderLoop);

    // Grab the canvas-backed stream at 30fps and ship its video
    // track through the broadcast path.
    const canvasStream = (canvas as any).captureStream?.(30) as MediaStream | undefined;
    const compositeVideoTrack = canvasStream?.getVideoTracks?.()[0];
    if (!canvasStream || !compositeVideoTrack) {
      setNotice('screen_share_canvas_stream_failed');
      teardownCompositePipeline();
      try { screenTrack.stop(); } catch { /* ignore */ }
      return;
    }
    compositeStreamRef.current = canvasStream;

    const preview = localStreamRef.current;
    if (preview) {
      preview.getVideoTracks().forEach((t) => { try { preview.removeTrack(t); } catch { /* ignore */ } });
      try { preview.addTrack(compositeVideoTrack); } catch { /* ignore */ }
      if (localVideoEl) {
        try { localVideoEl.srcObject = preview; } catch { /* ignore */ }
      }
    }
    broadcastRef.current.replaceTrack?.('video', compositeVideoTrack);
    setScreenShareMode('split');
    setLiveVideoSource('screen');
  }, [localVideoEl, screenShareMode, teardownCompositePipeline, handleStopScreenShare]);

  /**
   * Slice 6b-ui — apply the "Video source" draft (camera / screen / drone RTP).
   * Only meaningful while the SFU path is active (`REACT_APP_USE_SFU=true`).
   */
  const handleApplyLiveVideoDraft = useCallback(async () => {
    if (!broadcastRef.current || !isSfuEnabled()) return;
    const sid = activeSessionRef.current?.id;
    const grant = activeGrantRef.current;
    if (!sid || !grant) {
      setNotice('live_session_missing');
      return;
    }
    setNotice(null);
    setLiveSourceBusy(true);
    try {
      const d = liveSourceDraft;
      if (d === 'camera') {
        if (liveDroneIngestIdRef.current) {
          const iid = liveDroneIngestIdRef.current;
          liveDroneIngestIdRef.current = null;
          try { await stopSfuIngest(iid, { sessionId: sid, grantToken: grant }); } catch { /* ignore */ }
          setLiveDroneIngest(null);
        }
        if (screenShareMode) handleStopScreenShare();
        const preview = localStreamRef.current;
        let v = preview?.getVideoTracks()[0] || null;
        if (!v) {
          try {
            const cap = await captureStream({ deviceId: selectedDeviceId || undefined, audio: false });
            v = cap.getVideoTracks()[0] || null;
            if (v && preview) {
              preview.getVideoTracks().forEach((t) => { try { preview.removeTrack(t); } catch { /* ignore */ } });
              try { preview.addTrack(v); } catch { /* ignore */ }
              if (localVideoEl) try { localVideoEl.srcObject = preview; } catch { /* ignore */ }
            }
          } catch (e: unknown) {
            const norm = normaliseCaptureError(e);
            setNotice(norm.kind === 'unknown' ? 'camera_recover_failed' : norm.kind);
            return;
          }
        }
        broadcastRef.current.replaceTrack?.('video', v);
        setLiveVideoSource('camera');
        return;
      }
      if (d === 'screen_fs' || d === 'screen_split') {
        await handleStartScreenShare(d === 'screen_fs' ? 'fullscreen' : 'split');
        return;
      }
      if (d.startsWith('drone:')) {
        const entityId = d.slice('drone:'.length);
        if (!entityId) return;
        if (screenShareMode) handleStopScreenShare();
        if (liveDroneIngestIdRef.current) {
          const oldId = liveDroneIngestIdRef.current;
          liveDroneIngestIdRef.current = null;
          try { await stopSfuIngest(oldId, { sessionId: sid, grantToken: grant }); } catch { /* ignore */ }
        }
        const ingest = await startSfuIngest({ sourceId: entityId, kind: 'video' }, { sessionId: sid, grantToken: grant });
        liveDroneIngestIdRef.current = ingest.ingestId;
        setLiveDroneIngest({ ingestId: ingest.ingestId, sourceId: ingest.sourceId, rtp: ingest.rtp });
        broadcastRef.current.replaceTrack?.('video', null);
        setLiveVideoSource('drone');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setNotice(msg || 'live_source_apply_failed');
    } finally {
      setLiveSourceBusy(false);
    }
  }, [liveSourceDraft, screenShareMode, handleStopScreenShare, handleStartScreenShare, selectedDeviceId, localVideoEl]);

  // Same rationale as handleStopBroadcast above: this handler is passed
  // to webrtcBroadcastService as onEnded/onDenied, and it MUST be able to
  // stop the live controller even when the closure was created before
  // joinBroadcast resolved.  Reading via refs keeps the handler stable
  // across renders and guarantees viewer?.stop() actually targets the
  // running controller — otherwise the service's ws.on('close') fires a
  // follow-up onEnded('socket_closed') that clobbers the true reason
  // (e.g. 'broadcaster_left' / 'ended_duration').
  const handleStopWatch = useCallback(() => {
    try { viewerRef.current?.stop(); } catch { /* ignore */ }
    setViewer(null);
    setViewerCount(0);
    setViewerLiveEnded(false);
    setRemoteBroadcastPaused(false);
    // Tear down the synthetic agent-canvas engine (if any) so the
    // raf loop, AudioContext, and captureStream are released. Safe
    // to call when the engine was never created (media broadcasts).
    disposeSignalingOnlyEngine();
    const snap = remoteStreamRef.current;
    try {
      snap?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    } catch { /* ignore */ }
    if (remoteVideoEl) {
      try { remoteVideoEl.srcObject = null; } catch { /* ignore */ }
    }
    setRemoteStream(null);
  }, [remoteVideoEl, disposeSignalingOnlyEngine]);

  // Set to `true` while the viewer is running in TURN-relay-only
  // mode (retry after a direct-path failure on strict 5G / corporate
  // NAT).  Used to suppress the "Retry via relay" button after we've
  // already tried that path — if relay also fails, we know the TURN
  // server itself is unreachable and the user needs to change
  // networks, not click again.
  const [watchUsedRelay, setWatchUsedRelay] = useState(false);
  // Last-known diagnostics snapshot from the running viewer.  We
  // keep the last value around after a failure so the error card
  // can render a "Show connection details" block — this is the
  // only visibility we get into iPhone Safari sessions since iOS
  // has no accessible browser devtools console.
  const [watchDiag, setWatchDiag] = useState<ViewerDiagnostics | null>(null);

  /**
   * Decode a `live_agent_caption` event into a Blob + push it
   * through the engine. Extracted so the same logic powers both
   * the live caption handler and the pending-buffer drain.
   * Failures are non-fatal — the caption text still renders in
   * the trailing window even if the audio decode fails.
   */
  const pushCaptionToEngine = useCallback((eng: AgentBroadcastEngine, caption: AgentCaptionEvent) => {
    const text = (caption.text || '').trim();
    if (!text) return;
    let audioBlob: Blob | null = null;
    if (caption.audio_base64) {
      try {
        const bin = atob(caption.audio_base64);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        audioBlob = new Blob([buf], { type: caption.mime || 'audio/mpeg' });
      } catch {
        audioBlob = null;
      }
    }
    eng.pushCaption({
      text,
      audio: audioBlob,
      durationMs: caption.duration_ms,
    }).catch(() => { /* push failures are non-fatal — caption still renders for trailing window */ });
  }, []);

  const handleWatch = useCallback(async (options?: { forceRelay?: boolean }) => {
    setNotice(null); setEndedReason(null); setWatchDiag(null);
    setViewerLiveEnded(false);
    setViewerScopeBlocked(false);
    const forceRelay = !!options?.forceRelay;
    setWatchUsedRelay(forceRelay);
    // For human broadcasts the broadcasterId equals the profile wallet,
    // but for agent broadcasts the room is keyed by the **agent** address
    // (the on-air identity) while the paying / discoverable identity is
    // the operator wallet.  Using `wallet` blindly here meant a phone
    // viewing the operator's profile sent `broadcasterId = operator` to
    // the SFU hub; the room was actually keyed by the agent address, so
    // the server immediately replied `denied: broadcaster_not_live`,
    // which surfaced as "click Watch live → flash → back to button".
    // The discovery payload always carries the correct broadcasterId,
    // so prefer that whenever it's known.
    const targetBroadcasterId = remoteLiveSession?.broadcasterId || wallet;

    // Trust-group-restricted broadcasts require a signed viewer token
    // proving the watcher is a member. Public broadcasts skip this. The
    // viewer proves ownership of THEIR wallet (viewerAddress) — that
    // identity, not the profile wallet, is what the hub gate checks.
    let viewerToken: string | null = null;
    let viewerWallet: string | undefined = wallet;
    if (remoteLiveSession?.visibility === 'groups' && !isOwner) {
      const me = viewerAddress || '';
      if (!me.startsWith('G') || !stellarWallet) {
        // Only G-address viewers can sign the SEP-10 grant challenge today.
        setViewerScopeBlocked(true);
        setNotice('restricted_to_trust_group');
        return;
      }
      try {
        viewerToken = await obtainViewerToken(remoteLiveSession.id, me, stellarWallet);
        viewerWallet = me;
      } catch {
        setViewerScopeBlocked(true);
        setNotice('restricted_to_trust_group');
        return;
      }
    }

    const v = joinBroadcast({
      broadcasterId: targetBroadcasterId,
      wallet: viewerWallet,
      viewerToken,
      country: country == null ? undefined : Number(country),
      // On retry we skip host / srflx candidate pair checks entirely
      // and go straight to TURN — this is the only path that works
      // across symmetric carrier NAT (5G) and most vehicle Wi-Fi.
      iceTransportPolicy: forceRelay ? 'relay' : 'all',
      onStream: (stream) => setRemoteStream(stream),
      onMode: (mode) => {
        // The headless MCP / geoai broadcaster keeps a hello-only WS
        // open so the agent counts as on-air for caption fan-out, but
        // never produces any RTP. Without this branch the viewer would
        // sit on a black <video> element until the SFU watchdog gave
        // up with "Media path did not connect (NAT/firewall)" — which
        // is misleading because the network is fine; there is just
        // nothing to receive. Build a local agent-broadcast engine
        // (Ken-Burns avatar canvas + audio mixer), hand its synthetic
        // MediaStream to the existing <video>, and let the caption
        // handler below feed it.
        if (mode !== 'signaling_only') return;
        if (signalingOnlyEngineRef.current) return; // already rigged
        // Hydrate the agent's geo-profile so the canvas renders the
        // correct displayName + avatarUrl. Falls back to the
        // procedural gradient + initials when the lookup fails or
        // the agent never set a profile.
        const eng = createAgentBroadcastEngine({
          agentPublicKey: targetBroadcasterId,
          displayName: null,
          avatarUrl: null,
        });
        signalingOnlyEngineRef.current = eng;
        // Dev/diagnostic hook: lets the geoai team (and anyone
        // running the Chrome console snippets in their bug
        // report) inspect the live engine without rebuilding.
        // Production code never reads this — it's just an
        // escape hatch and it auto-clears on dispose below.
        try { (window as any).__gtSignalingEngine = eng; } catch { /* SSR / strict CSP */ }
        try { setRemoteStream(eng.getStream()); } catch { /* canvas.captureStream unsupported — captions still arrive */ }
        getPublicProfile(targetBroadcasterId)
          .then((prof) => {
            if (signalingOnlyEngineRef.current !== eng) return;
            const name = (prof?.displayName || '').trim();
            const url  = (prof?.avatarUrl   || '').trim();
            if (name) eng.setDisplayName(name);
            if (url)  eng.setAvatar(url);
          })
          .catch(() => { /* profile fetch failed — engine keeps fallback rendering */ });

        // Drain anything that arrived between Watch and welcome.
        // The buffer is FIFO so playback order matches the wire
        // order — a viewer who joined right as the agent said
        // "Good evening from Old Town" should hear that first
        // before the next caption.
        const buffered = pendingCaptionsRef.current.splice(0);
        for (const caption of buffered) {
          pushCaptionToEngine(eng, caption);
        }
      },
      onAgentCaption: (caption: AgentCaptionEvent) => {
        // Mirror what the broadcaster modal does locally so the
        // caption ticker + voice playback feel identical to a media
        // broadcast. We only act when the signaling-only engine is
        // active; for media broadcasts the audio + caption band are
        // baked into the inbound RTP stream and we'd otherwise
        // double-play.
        const eng = signalingOnlyEngineRef.current;
        if (!eng) {
          // Engine not yet built — buffer for replay on welcome.
          // (See pendingCaptionsRef docs above.)
          const buf = pendingCaptionsRef.current;
          buf.push(caption);
          while (buf.length > MAX_PENDING_CAPTIONS) buf.shift();
          return;
        }
        pushCaptionToEngine(eng, caption);
      },
      onDenied: (reason) => {
        setEndedReason(reason); setReconnectingWatch(null); setBroadcasterGraceUntil(null);
        handleStopWatch();
      },
      onEnded:  (reason) => {
        setEndedReason(reason || 'ended');
        setReconnectingWatch(null);
        setBroadcasterGraceUntil(null);
        // Soft-close the viewer so no more bandwidth is consumed,
        // but leave the remote MediaStream bound to the <video>
        // element.  When the inbound tracks go to `readyState =
        // "ended"` the video naturally freezes on the last frame,
        // which is exactly the TikTok-style "this live ended" UX
        // the user asked for.  A caller-initiated Stop / Next is
        // needed to clear the pane.
        try { viewerRef.current?.stop(); } catch { /* ignore */ }
        // Same idea for the synthetic engine — keep the last frame
        // (avatar + final caption) on screen until the user clicks
        // Stop / Next.
        setViewer(null);
        setViewerCount(0);
        setViewerLiveEnded(true);
      },
      onSessionExtended: ({ scheduledEndAt }) => {
        setRemoteLiveSession((prev) => prev ? { ...prev, scheduledEndAt } : prev);
      },
      onViewersUpdate: (count) => setViewerCount(count),
      onDiagnostics: (diag) => setWatchDiag(diag),
      onError:  (err) => setNotice(err.message),
      // Viewers reconnect aggressively while the remote session's
      // scheduled end is still in the future.  If the remote session
      // snapshot was never delivered (race at mount), fall back to
      // allowing reconnect — the server will refuse if the session
      // actually ended, which trips `onEnded`.  Reads via the ref
      // so a session_extended update mid-flight is honoured.
      shouldResume: () => {
        const s = remoteLiveSessionRef.current;
        if (!s) return true;
        if (!s.scheduledEndAt) return true;
        return new Date(s.scheduledEndAt).getTime() > Date.now();
      },
      onReconnecting: (n) => setReconnectingWatch(n),
      onReconnected:  () => setReconnectingWatch(null),
      onBroadcasterDisconnected: (graceMs) => {
        setBroadcasterGraceUntil(Date.now() + Math.max(0, graceMs));
      },
      onBroadcasterReconnected: () => setBroadcasterGraceUntil(null),
      onBroadcastPaused:    () => setRemoteBroadcastPaused(true),
      onBroadcastResumed:   () => setRemoteBroadcastPaused(false),
      onGift: (gift) => pushGiftToast(gift),
    });
    setViewer(v);
  }, [wallet, country, handleStopWatch, pushGiftToast, remoteLiveSession?.broadcasterId, remoteLiveSession?.visibility, remoteLiveSession?.id, isOwner, viewerAddress, stellarWallet, pushCaptionToEngine]);

  const handleWatchViaRelay = useCallback(() => {
    handleWatch({ forceRelay: true });
  }, [handleWatch]);

  // Pay-again-to-extend / viewer tip-to-extend.  Re-uses the same
  // `onExecuteLivePayment` handler as the initial session payment —
  // the backend /extend route is idempotent around the payment
  // reference and all peers in the room get a `session_extended` WS
  // push that updates the countdown automatically.  For viewers who
  // are tipping, we bill *their* wallet (not the broadcaster's) so
  // the routing in `liveSessionPaymentService` picks the right path.
  const [extending, setExtending] = useState(false);
  const handleExtend = useCallback(async (fromViewer: boolean) => {
    const target = activeSession || remoteLiveSession;
    if (!target) { setNotice('no_active_session'); return; }
    if (!policy)  { setNotice('policy_load_failed'); return; }
    if (!onExecuteLivePayment) { setNotice('payment_handler_missing'); return; }
    const payerWallet = fromViewer
      ? (viewerAddress || '')
      : wallet;
    if (!payerWallet) { setNotice('payment_handler_missing'); return; }
    // V2: `effectiveExtensionCost` may differ from `sessionCost` when
    // the policy sets an explicit per-extension price.  The mirror
    // normalises "0" → "reuse session cost" so this read is always
    // the correct number of stroops to charge.
    const cost = (policy.effectiveExtensionCost && policy.effectiveExtensionCost !== '0')
      ? policy.effectiveExtensionCost
      : (policy.sessionCost || '0');
    if (cost === '0') { setNotice('extension_not_required'); return; }
    // Client-side policy gates (the backend is authoritative — these
    // just avoid pointless payment flows when we already know the
    // server will reject the extension).
    if (fromViewer && policy.extensionsEnabled === false) {
      setNotice('viewer_extensions_disabled'); return;
    }
    const extCount = (target.extensions && target.extensions.length) || 0;
    if ((policy.effectiveMaxExtensions || 0) > 0
      && extCount >= (policy.effectiveMaxExtensions || 0)) {
      setNotice('max_extensions_reached'); return;
    }
    setExtending(true);
    setNotice(null);
    try {
      const receipt = await onExecuteLivePayment({
        sessionId: target.id,
        country: country == null ? null : Number(country),
        wallet: payerWallet,
        amountStroops: cost,
        amountDisplay: stroopsToDisplay(cost),
      });
      await extendLiveSession(target.id, {
        paymentReference: receipt.paymentReference,
        paidAmount: receipt.paidAmount || cost,
        payerWallet,
        fromViewer,
      });
      // Non-connected peers (discovery-polled remote viewers who haven't
      // joined the hub) won't receive the session_extended push, so
      // nudge their local view immediately — the next poll will confirm.
      if (!activeSession && remoteLiveSession) {
        const bumpSec = (policy.effectiveExtensionSeconds || policy.effectiveMaxDurationSeconds || 0);
        setRemoteLiveSession((prev) => prev ? {
          ...prev,
          scheduledEndAt: new Date(
            Math.max(
              new Date(prev.scheduledEndAt || new Date().toISOString()).getTime(),
              Date.now(),
            ) + (bumpSec * 1000),
          ).toISOString(),
        } : prev);
      }
    } catch (err: any) {
      setNotice(err?.message || 'extend_failed');
    } finally {
      setExtending(false);
    }
  }, [
    activeSession, remoteLiveSession, policy, country, wallet,
    viewerAddress, onExecuteLivePayment,
  ]);

  if (!isOpen) return null;

  const costDisplay        = policy ? stroopsToDisplay(policy.sessionCost) : '0';
  const maxDurationDisplay = policy ? formatDuration(policy.effectiveMaxDurationSeconds) : '—';
  // V2: dedicated display values for the *extension* action.  These
  // intentionally shadow sessionCost / maxDuration when the policy
  // configures per-extension overrides, so the HUD shows
  // "Tip X XLM → +Ns" with the actually-charged amount.
  const extensionCostStroops = policy
    ? ((policy.effectiveExtensionCost && policy.effectiveExtensionCost !== '0')
        ? policy.effectiveExtensionCost
        : policy.sessionCost)
    : '0';
  const extensionCostDisplay = policy ? stroopsToDisplay(extensionCostStroops) : '0';
  const extensionDurationDisplay = policy
    ? formatDuration(policy.effectiveExtensionSeconds || policy.effectiveMaxDurationSeconds)
    : '—';
  // Client-side gate for the Extend/Tip buttons: mirrors the backend
  // rejection reasons so the UI can show a friendly tooltip instead
  // of a raw error toast after a failed payment.
  const extensionGate: { disabled: boolean; reason: string | null } = (() => {
    if (!policy) return { disabled: false, reason: null };
    const ext = (activeSession?.extensions || remoteLiveSession?.extensions || []).length;
    if ((policy.effectiveMaxExtensions || 0) > 0 && ext >= (policy.effectiveMaxExtensions || 0)) {
      return { disabled: true, reason: `Max extensions (${policy.effectiveMaxExtensions}) reached` };
    }
    const target = activeSession || remoteLiveSession;
    const startedMs = target?.startedAt ? Date.parse(target.startedAt) : NaN;
    if ((policy.effectiveMaxTotalDurationSeconds || 0) > 0 && Number.isFinite(startedMs)) {
      const usedSec = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
      const remainingHeadroom = (policy.effectiveMaxTotalDurationSeconds || 0) - usedSec;
      if (remainingHeadroom <= 0) {
        return { disabled: true, reason: 'Session has reached its total duration cap' };
      }
      if ((policy.effectiveMinExtensionSeconds || 0) > 0
        && remainingHeadroom < (policy.effectiveMinExtensionSeconds || 0)) {
        return { disabled: true, reason: 'Remaining headroom below minimum extension' };
      }
    }
    return { disabled: false, reason: null };
  })();
  // Viewer-specific gate: `extensionsEnabled=false` still lets
  // broadcasters pay-again but hides the tip button from viewers.
  const viewerExtensionsDisabled = !!policy && policy.extensionsEnabled === false;

  // The session we want to render in the HUD / countdown.  Owners see
  // their own `activeSession`; viewers and passers-by see the
  // discovery-polled `remoteLiveSession`.
  const displaySession: LiveSession | null = activeSession || remoteLiveSession;
  const hudApprox: ApproxLocation | null = (() => {
    if (isOwner && approxLocation && Number.isFinite(approxLocation.lat) && Number.isFinite(approxLocation.lng)) {
      return { lat: approxLocation.lat, lng: approxLocation.lng };
    }
    if (displaySession && displaySession.approxLat != null && displaySession.approxLng != null) {
      return { lat: displaySession.approxLat, lng: displaySession.approxLng };
    }
    return null;
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  // Minimised shell — keeps the overlay mounted (so WebRTC state
  // survives) but lets the broadcaster/viewer keep interacting with
  // the map underneath.  A single tap on the pill restores the full
  // overlay; we do not unmount anything.
  if (minimized) {
    return (
      <>
        <LiveMinimizedPill
          isOwner={isOwner}
          isLive={isLive}
          record={record}
          wallet={wallet}
          viewerCount={viewerCount}
          remaining={remaining}
          approx={hudApprox}
          onExpand={() => setMinimized(false)}
          onStop={isOwner && broadcast ? () => void handleStopBroadcast(true) : undefined}
          onStopWatch={!isOwner && viewer ? handleStopWatch : undefined}
          reconnecting={reconnectingBroadcast != null || reconnectingWatch != null || broadcasterGraceUntil != null}
        />
      </>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <Header
          wallet={wallet}
          record={record}
          isOwner={isOwner}
          isLive={isLive}
          onClose={onClose}
          onMinimize={(isLive || !!broadcast || !!viewer) ? () => setMinimized(true) : undefined}
        />

        <LiveReconnectBanner
          reconnectingBroadcast={reconnectingBroadcast}
          reconnectingWatch={reconnectingWatch}
          broadcasterGraceUntil={broadcasterGraceUntil}
        />

        <Tabs
          tab={tab}
          onChange={setTab}
          showTrustlines={!wallet.startsWith('C')}
          // Show the Agents tab to visitors too — the AgentsTab itself
          // hides operator-only controls (register, edit caps, go live,
          // dispose, revoke, movement loop) when isOwner is false, so
          // public viewers get a read-only tour of the agent fleet.
          showAgents={true}
        />

        <div style={{ marginTop: 14 }}>
          {tab === 'profile' && (
            <ProfileTab
              isOwner={isOwner}
              record={record}
              draft={draft}
              onDraftChange={setDraft}
              editing={editing}
              onEdit={() => { setEditing(true); setDraft(record); }}
              onCancel={() => { setEditing(false); setDraft(record); }}
              onSave={handleSaveClicked}
              onTogglePrivacy={togglePrivacyOnly}
              loading={loadingProfile}
              saving={saving}
              dirty={dirty}
              validationError={validationError}
              error={profileError}
            />
          )}

          {tab === 'live' && (
            <LiveTab
              isOwner={isOwner}
              isLive={isLive}
              wallet={wallet}
              record={record}
              policy={policy}
              phase={phase}
              costDisplay={costDisplay}
              maxDurationDisplay={maxDurationDisplay}
              extensionCostDisplay={extensionCostDisplay}
              extensionDurationDisplay={extensionDurationDisplay}
              extensionGateReason={extensionGate.reason}
              extensionDisabled={extensionGate.disabled}
              viewerExtensionsDisabled={viewerExtensionsDisabled}
              remaining={remaining}
              activeSession={displaySession}
              broadcast={broadcast}
              viewer={viewer}
              viewerCount={viewerCount}
              endedReason={endedReason}
              notice={notice}
              approx={hudApprox}
              onStart={handleStartBroadcast}
              onGoLive={handleGoLive}
              visibility={broadcastVisibility}
              onVisibilityChange={setBroadcastVisibility}
              availableGroups={availableGroups}
              selectedGroupIds={selectedGroupIds}
              onToggleGroup={toggleGroupSelected}
              viewerScopeBlocked={viewerScopeBlocked}
              countdownSec={countdownSec}
              audienceLog={audienceLog}
              viewerLiveEnded={viewerLiveEnded}
              broadcastPaused={broadcastPaused}
              remoteBroadcastPaused={remoteBroadcastPaused}
              onTogglePause={handleTogglePause}
              screenShareMode={screenShareMode}
              onStartScreenShare={handleStartScreenShare}
              onStopScreenShare={handleStopScreenShare}
              canSendGift={!!viewer && !!stellarWallet && !!viewerAddress && !isOwner}
              onOpenGift={() => setGiftModalOpen(true)}
              giftToasts={giftToasts}
              onStop={() => void handleStopBroadcast(true)}
              onWatch={handleWatch}
              onWatchViaRelay={handleWatchViaRelay}
              watchUsedRelay={watchUsedRelay}
              watchDiag={watchDiag}
              onStopWatch={handleStopWatch}
              onMinimize={(isLive || !!broadcast || !!viewer) ? () => setMinimized(true) : undefined}
              onExtend={handleExtend}
              extending={extending}
              extensionCount={displaySession?.extensions?.length ?? 0}
              localStream={localStream}
              remoteStream={remoteStream}
              localVideoRef={setLocalVideoEl}
              remoteVideoRef={setRemoteVideoEl}
              videoDevices={videoDevices}
              selectedDeviceId={selectedDeviceId}
              onSelectCamera={handleSwitchCamera}
              flipping={flipping}
              cameraProbeError={cameraProbeError}
              remoteLiveSession={remoteLiveSession}
              onNextBroadcast={onNextBroadcast}
              onPrevBroadcast={onPrevBroadcast}
              nextBroadcastLabel={nextBroadcastLabel}
              prevBroadcastLabel={prevBroadcastLabel}
              countryName={stableCountryName}
              countrySource={stableCountrySource}
              hasLocation={hasLocation}
              onRequestLocation={onRequestLocation}
              liveSessionId={
                /* Owner-side: use the live session we paid for. Viewer-side:
                   use the discovered remote session.  Either one drives the
                   tool overlay so late-joining viewers reconstruct the
                   broadcaster's current tool layout from the snapshot. */
                displaySession?.id || remoteLiveSession?.id || null
              }
              liveSessionGrant={activeGrant}
              saveBroadcastEnabled={saveBroadcastEnabled}
              onToggleSaveBroadcast={setSaveBroadcastEnabled}
              savedRecordingNotice={savedRecordingNotice}
              pendingRecordingMeta={pendingRecording
                ? { durationSec: pendingRecording.durationSec, sizeBytes: pendingRecording.blob.size }
                : null}
              savingPendingRecording={savingPendingRecording}
              onSaveNow={handleSaveNow}
              onDiscardRecording={handleDiscardRecording}
              sfuBroadcastSource={
                broadcast && isSfuEnabled()
                  ? {
                    current: liveVideoSource,
                    draft: liveSourceDraft,
                    onDraftChange: setLiveSourceDraft,
                    onApply: () => { void handleApplyLiveVideoDraft(); },
                    busy: liveSourceBusy,
                    drones: broadcasterDroneEntities,
                    rtp: liveDroneIngest,
                  }
                  : null
              }
              pilotEntities={broadcasterDroneEntities}
              onUnlockSignalingAudio={() => {
                // Bound to the watch <video>'s tap-to-unmute
                // user gesture so AudioContext.resume() runs in
                // the same gesture stack as the unmute flip.
                // Without this, BufferSources scheduled BEFORE
                // the tap stay silent — the bug the geoai team
                // surfaced 2026-05-06 (their report §4-A).
                const eng = signalingOnlyEngineRef.current;
                if (!eng) return;
                eng.unlockAudio()
                  .then((info) => {
                    try {
                    } catch { /* swallow */ }
                  })
                  .catch(() => { /* user-gesture lost; viewers can re-tap */ });
              }}
            />
          )}

          {tab === 'past_broadcasts' && (
            <PastBroadcastsTab
              subjectWallet={wallet}
              viewerWallet={isOwner ? wallet : null}
              isOwner={isOwner}
              ownerSigner={isOwner ? (stellarWallet ?? null) : null}
              /* The replay player wants to overlay an "@displayname"
                 + avatar on every recording so the VOD feels like
                 TikTok / Twitter X.  Forward whichever fields are
                 already loaded on the profile record — the modal
                 falls back to a short G-address if either field is
                 missing. */
              subjectDisplayName={record?.displayName || null}
              subjectAvatarUrl={record?.avatarUrl || null}
              onOpenWallet={onOpenWallet}
            />
          )}

          {tab === 'trustlines' && !wallet.startsWith('C') && (
            <TrustlinesPanel
              subjectAddress={wallet}
              isOwner={isOwner}
              ownerWallet={isOwner ? stellarWallet : undefined}
              viewerWallet={stellarWallet}
            />
          )}

          {tab === 'agents' && (
            <AgentsTab
              subjectWallet={wallet}
              isOwner={isOwner}
              onExecuteLivePayment={onExecuteLivePayment}
              stellarWallet={stellarWallet ?? null}
              passkey={passkey ?? null}
              viewerBroadcastCountry={country == null ? null : Number(country)}
              approxLocation={approxLocation}
              onOpenWallet={onOpenWallet}
            />
          )}
        </div>

        {pendingSave && (
          <OnChainConfirmModal
            draft={draft}
            record={record}
            onCancel={() => setPendingSave(false)}
            onConfirm={commitSave}
          />
        )}

        {giftModalOpen && viewerAddress && stellarWallet && !isOwner && (
          <LiveGiftModal
            broadcasterAddress={wallet}
            viewerAddress={viewerAddress}
            viewerWallet={stellarWallet as unknown as TrustlinesWallet}
            onClose={() => setGiftModalOpen(false)}
            onSent={handleGiftSent}
          />
        )}
      </div>
    </div>
  );
};

// ── Header ──────────────────────────────────────────────────────────────────

const Header: React.FC<{
  wallet: string;
  record: ProfileRecord;
  isOwner: boolean;
  isLive: boolean;
  onClose: () => void;
  /** When provided, renders a minimise button so the overlay can be
   *  collapsed to a floating pill without tearing down the session. */
  onMinimize?: () => void;
}> = ({ wallet, record, isOwner, isLive, onClose, onMinimize }) => (
  <div style={headerStyle}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, flex: 1 }}>
      <Avatar url={record.avatarUrl} fallback={(record.displayName || wallet).slice(0, 2).toUpperCase()} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {record.displayName || shortAddr(wallet)}
          </h3>
          {isLive && (
            <span style={liveBadgeStyle}>LIVE</span>
          )}
          <AgentBadge wallet={wallet} />
          {record.isPrivate && !isOwner && (
            <span style={privateBadgeStyle} title="This user has a private profile">private</span>
          )}
        </div>
        <div style={walletTextStyle}>{shortAddr(wallet)}</div>
      </div>
    </div>
    <div style={{ display: 'flex', gap: 4 }}>
      <ShareProfileButton wallet={wallet} displayName={record.displayName || ''} />
      {onMinimize && (
        <button
          onClick={onMinimize}
          aria-label="Minimise to pill"
          title="Minimise — keeps the live session running while you browse the map"
          style={minBtnStyle}
        >
          –
        </button>
      )}
      <button onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
    </div>
  </div>
);

/**
 * Small "Share" button rendered next to the close button.  Copies the
 * canonical public-profile URL (`/geoai/<G…>`) to the clipboard, with
 * a 2-second "Copied!" affordance and a Web Share fallback for mobile
 * (where the OS-native share sheet feels nicer than a clipboard
 * toast).  The URL is shareable on Twitter / X / Discord and pulls
 * server-side OG / Twitter-card meta when crawled (see
 * server/index.js).
 */
const ShareProfileButton: React.FC<{ wallet: string; displayName?: string }> = ({ wallet, displayName }) => {
  const [copied, setCopied] = React.useState(false);
  const copiedTimer = React.useRef<number | null>(null);
  React.useEffect(() => () => {
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
  }, []);
  const url = React.useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/geoai/${wallet}`;
  }, [wallet]);
  const onShare = React.useCallback(async () => {
    if (!url) return;
    const shareText = displayName ? `${displayName} on GeoTrust` : 'GeoTrust profile';
    // Prefer the OS share sheet on mobile where it's the native UX.
    // Fall back to clipboard on desktop / browsers without the API.
    try {
      const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
      if (nav.share && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) {
        await nav.share({ title: shareText, text: shareText, url });
        return;
      }
    } catch {
      // User cancelled or share API rejected — fall through to clipboard.
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Last-resort fallback: a tiny prompt so the user can copy
      // manually.  Old browsers / locked-down WebViews land here.
      // eslint-disable-next-line no-alert
      window.prompt('Copy your public profile link:', url);
    }
  }, [url, displayName]);
  return (
    <button
      type="button"
      onClick={onShare}
      aria-label="Share public profile link"
      title={copied ? 'Link copied!' : `Share — copies ${url}`}
      style={shareBtnStyle}
    >
      {copied ? '✓' : '↗'}
    </button>
  );
};

// ── Minimised live-session pill ─────────────────────────────────────────────
//
// Rendered in place of the full overlay while `minimized === true`.  The
// pill is intentionally small so the map is usable underneath, docks to
// the bottom-centre of the viewport, and keeps the pulsing LIVE badge +
// countdown visible so the user always knows a paid session is still
// running.
/**
 * Banner rendered directly under the overlay header while a
 * signaling-layer reconnect is in flight.  Three scenarios surface
 * here:
 *
 *   - **Broadcaster reconnecting**: OUR signaling ws dropped and
 *     we're retrying.  Viewers have already been told by the server
 *     (via `broadcaster_disconnected`) that we're gone.
 *   - **Watcher reconnecting**: OUR signaling ws (as viewer) dropped.
 *     The broadcaster is unaffected; this is typically a local Wi-Fi
 *     glitch / phone tunnel change.
 *   - **Broadcaster paused (grace window)**: another user's
 *     broadcast we're watching had its ws drop.  We stay subscribed
 *     until `broadcasterGraceUntil` ticks down to zero — at which
 *     point the server will force-end the room (if the broadcaster
 *     never comes back) and we'll fall into the ended-card path.
 *
 * We keep the three independent so a single device watching another
 * live while themselves broadcasting can surface both messages
 * simultaneously (rare but plausible).
 */
const LiveReconnectBanner: React.FC<{
  reconnectingBroadcast: number | null;
  reconnectingWatch: number | null;
  broadcasterGraceUntil: number | null;
}> = ({ reconnectingBroadcast, reconnectingWatch, broadcasterGraceUntil }) => {
  // Recompute the remaining grace window every second so the viewer
  // gets a live-ticking countdown without us storing another piece
  // of React state.  Cheap (one setInterval while active) and only
  // runs while the banner itself is mounted.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (reconnectingBroadcast == null && reconnectingWatch == null && broadcasterGraceUntil == null) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [reconnectingBroadcast, reconnectingWatch, broadcasterGraceUntil]);

  if (reconnectingBroadcast == null && reconnectingWatch == null && broadcasterGraceUntil == null) {
    return null;
  }

  const graceSecLeft = broadcasterGraceUntil
    ? Math.max(0, Math.ceil((broadcasterGraceUntil - nowTick) / 1000))
    : 0;

  return (
    <div style={reconnectBannerStyle}>
      <span style={reconnectDotStyle} aria-hidden />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0, flex: 1 }}>
        {reconnectingBroadcast != null && (
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Reconnecting your broadcast… (attempt {reconnectingBroadcast})
          </span>
        )}
        {reconnectingWatch != null && (
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Reconnecting to the broadcast… (attempt {reconnectingWatch})
          </span>
        )}
        {broadcasterGraceUntil != null && (
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Broadcaster temporarily disconnected — waiting {graceSecLeft}s for reconnect
          </span>
        )}
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
          Your paid time is still reserved — the feed will resume automatically.
        </span>
      </div>
    </div>
  );
};

const LiveMinimizedPill: React.FC<{
  isOwner: boolean;
  isLive: boolean;
  record: ProfileRecord;
  wallet: string;
  viewerCount: number;
  remaining: number;
  approx: ApproxLocation | null;
  onExpand: () => void;
  onStop?: () => void;
  onStopWatch?: () => void;
  /** Show an amber "Reconnecting…" pill on the minimised control
   *  when either side of the signaling ws is mid-retry.  We keep
   *  the full banner for the expanded overlay — this is just a
   *  discreet hint so users don't assume the session died when
   *  they collapsed the panel. */
  reconnecting?: boolean;
}> = ({ isOwner, isLive, record, wallet, viewerCount, remaining, approx, onExpand, onStop, onStopWatch, reconnecting }) => {
  const label = record.displayName || shortAddr(wallet);
  const approxLabel = formatApproxLocation(approx);
  return (
    <div style={minimizedPillWrapStyle}>
      <button
        type="button"
        onClick={onExpand}
        title="Restore the profile overlay"
        style={minimizedPillStyle}
      >
        {isLive && (
          <span style={liveHudBadgeStyle}>
            <span style={livePulseDotStyle} />
            LIVE
          </span>
        )}
        {reconnecting && (
          <span style={minimizedReconnectBadgeStyle} title="Reconnecting…">
            <span style={reconnectDotStyle} />
            Reconnecting
          </span>
        )}
        <Avatar url={record.avatarUrl} fallback={label.slice(0, 2).toUpperCase()} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
            {label}
          </span>
          <span style={{ fontSize: 11, color: '#cbd5f5' }}>
            {isOwner
              ? `${viewerCount} viewer${viewerCount === 1 ? '' : 's'}${remaining > 0 ? ` · ${formatDuration(remaining)}` : ''}`
              : `${viewerCount > 0 ? `${viewerCount} watching` : 'Watching'}${remaining > 0 ? ` · ${formatDuration(remaining)}` : ''}`}
            {approxLabel ? ` · ${approxLabel}` : ''}
          </span>
        </div>
        <span style={minimizedExpandHintStyle} aria-hidden>↗</span>
      </button>
      {onStop && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStop(); }}
          style={minimizedStopBtnStyle}
          title="End broadcast"
        >
          End
        </button>
      )}
      {onStopWatch && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStopWatch(); }}
          style={minimizedStopBtnStyle}
          title="Stop watching"
        >
          Stop
        </button>
      )}
    </div>
  );
};

const Avatar: React.FC<{ url: string; fallback: string }> = ({ url, fallback }) => (
  url ? (
    <img src={url} alt="" style={avatarStyle} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
  ) : (
    <div style={{ ...avatarStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb', color: '#374151', fontWeight: 700 }}>
      {fallback}
    </div>
  )
);

// ── Tabs ────────────────────────────────────────────────────────────────────

const Tabs: React.FC<{
  tab: Tab;
  onChange: (t: Tab) => void;
  showTrustlines: boolean;
  showAgents: boolean;
}> = ({ tab, onChange, showTrustlines, showAgents }) => (
  <div style={{ display: 'flex', gap: 4, marginTop: 14, borderBottom: '1px solid #e5e7eb' }}>
    <TabBtn label="Profile"    active={tab === 'profile'}    onClick={() => onChange('profile')} />
    <TabBtn label="Live"       active={tab === 'live'}       onClick={() => onChange('live')} />
    <TabBtn
      label="Past broadcasts"
      active={tab === 'past_broadcasts'}
      onClick={() => onChange('past_broadcasts')}
    />
    {showTrustlines && (
      <TabBtn label="Trustlines" active={tab === 'trustlines'} onClick={() => onChange('trustlines')} />
    )}
    {showAgents && (
      <TabBtn label="🤖 Agents" active={tab === 'agents'} onClick={() => onChange('agents')} />
    )}
  </div>
);

const TabBtn: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      border: 'none',
      background: 'transparent',
      padding: '8px 14px',
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      color: active ? '#111827' : '#6b7280',
      borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
      cursor: 'pointer',
      marginBottom: -1,
    }}
  >
    {label}
  </button>
);

// ── Profile tab ─────────────────────────────────────────────────────────────

const ProfileTab: React.FC<{
  isOwner: boolean;
  record: ProfileRecord;
  draft: ProfileRecord;
  onDraftChange: (next: ProfileRecord) => void;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onTogglePrivacy: (next: boolean) => void;
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  validationError: ReturnType<typeof validateProfile>;
  error: string | null;
}> = ({
  isOwner, record, draft, onDraftChange, editing, onEdit, onCancel,
  onSave, onTogglePrivacy, loading, saving, dirty, validationError, error,
}) => {
  if (loading && record.version === 0) {
    return <div style={{ fontSize: 13, color: '#6b7280' }}>Loading profile…</div>;
  }

  if (!isOwner) {
    // Read-only view.
    if (record.version === 0) {
      return (
        <div style={readOnlyCardStyle}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            This user hasn't written a profile on-chain yet.
          </div>
        </div>
      );
    }
    return (
      <div style={readOnlyCardStyle}>
        {record.bio && <p style={{ margin: '0 0 10px 0', fontSize: 14, lineHeight: 1.5 }}>{record.bio}</p>}
        <ReadOnlyHandle label="X / Twitter" value={record.xHandle}    hrefPrefix="https://x.com/" />
        <ReadOnlyHandle label="Discord"     value={record.discordHandle} />
        <div style={metaStyle}>
          Last updated at ledger {record.updatedAtLedger || '—'} · v{record.version}
        </div>
      </div>
    );
  }

  // Owner view.
  if (!editing) {
    return (
      <div style={readOnlyCardStyle}>
        {record.bio && <p style={{ margin: '0 0 10px 0', fontSize: 14, lineHeight: 1.5 }}>{record.bio}</p>}
        {(record.xHandle || record.discordHandle) && (
          <div style={{ marginBottom: 10 }}>
            <ReadOnlyHandle label="X / Twitter" value={record.xHandle}    hrefPrefix="https://x.com/" />
            <ReadOnlyHandle label="Discord"     value={record.discordHandle} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={record.isPrivate}
              disabled={saving}
              onChange={(e) => onTogglePrivacy(e.target.checked)}
            />
            Private profile
          </label>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            (public readers see an empty record)
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={btnPrimary} onClick={onEdit}>Edit profile</button>
        </div>

        <div style={metaStyle}>
          {record.version === 0 ? 'No profile on-chain yet.' : (
            <>On-chain · v{record.version} · ledger {record.updatedAtLedger}</>
          )}
        </div>

        {error && <div style={errStyle}>{error}</div>}
      </div>
    );
  }

  // Editor.
  const err = validationError;
  return (
    <div style={readOnlyCardStyle}>
      <EditorField
        label="Display name"
        field="displayName"
        value={draft.displayName}
        onChange={(v) => onDraftChange({ ...draft, displayName: v })}
      />
      <EditorField
        label="Bio"
        field="bio"
        value={draft.bio}
        textarea
        onChange={(v) => onDraftChange({ ...draft, bio: v })}
      />
      <EditorField
        label="Avatar URL"
        field="avatarUrl"
        value={draft.avatarUrl}
        placeholder="https://..."
        onChange={(v) => onDraftChange({ ...draft, avatarUrl: v })}
      />
      <EditorField
        label="X / Twitter handle"
        field="xHandle"
        value={draft.xHandle}
        placeholder="alice"
        onChange={(v) => onDraftChange({ ...draft, xHandle: v })}
      />
      <EditorField
        label="Discord handle"
        field="discordHandle"
        value={draft.discordHandle}
        placeholder="alice#1234"
        onChange={(v) => onDraftChange({ ...draft, discordHandle: v })}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={draft.isPrivate}
          onChange={(e) => onDraftChange({ ...draft, isPrivate: e.target.checked })}
        />
        Private profile (public readers see an empty record)
      </label>

      {err && (
        <div style={errStyle}>
          {err.field} is {err.actualBytes} bytes; on-chain cap is {err.maxBytes}.
        </div>
      )}
      {error && <div style={errStyle}>{error}</div>}

      <div style={onChainNoteStyle}>
        Saving writes your profile to the <strong>geo-profile</strong> Soroban contract.
        You will be prompted to sign a Stellar transaction; a small network fee applies.
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          style={btnPrimary}
          onClick={onSave}
          disabled={saving || !dirty || !!err}
        >
          {saving ? 'Saving…' : 'Review & save on-chain'}
        </button>
        <button style={btnSecondary} onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
};

const EditorField: React.FC<{
  label: string;
  field: keyof typeof PROFILE_MAX_BYTES;
  value: string;
  textarea?: boolean;
  placeholder?: string;
  onChange: (v: string) => void;
}> = ({ label, field, value, textarea, placeholder, onChange }) => {
  const left = remainingBytes(field, value);
  const max  = PROFILE_MAX_BYTES[field];
  const overLimit = left < 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{label}</div>
        <div style={{ fontSize: 11, color: overLimit ? '#b91c1c' : '#6b7280' }}>
          {max - left}/{max} bytes
        </div>
      </div>
      {textarea ? (
        <textarea
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      )}
    </div>
  );
};

const ReadOnlyHandle: React.FC<{ label: string; value: string; hrefPrefix?: string }> = ({ label, value, hrefPrefix }) => {
  if (!value) return null;
  const trimmed = value.replace(/^@/, '').trim();
  const href = hrefPrefix ? `${hrefPrefix}${encodeURIComponent(trimmed)}` : undefined;
  return (
    <div style={{ fontSize: 12, color: '#374151', marginBottom: 3 }}>
      <span style={{ color: '#6b7280', marginRight: 6 }}>{label}:</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer noopener" style={{ color: '#2563eb', textDecoration: 'none' }}>
          @{trimmed}
        </a>
      ) : (
        <span>{value}</span>
      )}
    </div>
  );
};

// ── On-chain confirmation modal ─────────────────────────────────────────────

const OnChainConfirmModal: React.FC<{
  draft: ProfileRecord;
  record: ProfileRecord;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ draft, record, onCancel, onConfirm }) => {
  const diffs: { label: string; before: string; after: string }[] = [];
  const push = (label: string, before: string, after: string) => {
    if (before !== after) diffs.push({ label, before, after });
  };
  push('Display name',   record.displayName,   draft.displayName);
  push('Bio',            record.bio,           draft.bio);
  push('Avatar URL',     record.avatarUrl,     draft.avatarUrl);
  push('X handle',       record.xHandle,       draft.xHandle);
  push('Discord handle', record.discordHandle, draft.discordHandle);
  push('Privacy',
    record.isPrivate ? 'Private' : 'Public',
    draft.isPrivate  ? 'Private' : 'Public',
  );

  return (
    <div style={modalOverlayStyle} onClick={onCancel}>
      <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Write profile on-chain?</div>
        <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 12 }}>
          The following fields will be written to the <strong>geo-profile</strong> contract.
          This is public data (unless you toggle "Private").  You'll be prompted to sign
          a Stellar transaction; a small network fee applies.
        </div>
        {diffs.length === 0 ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>No changes to save.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {diffs.map((d) => (
              <div key={d.label} style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 600 }}>{d.label}</div>
                <div style={{ color: '#6b7280', textDecoration: 'line-through' }}>
                  {d.before || <em>empty</em>}
                </div>
                <div style={{ color: '#111827' }}>
                  {d.after || <em>empty</em>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={btnSecondary} onClick={onCancel}>Cancel</button>
          <button style={btnPrimary} onClick={onConfirm} disabled={diffs.length === 0}>
            Sign & save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Fullscreen-capable video pane ───────────────────────────────────────────

/**
 * Wraps a <video> in a relatively-positioned container with a
 * fullscreen toggle button.  Uses the standard Fullscreen API plus
 * WebKit-prefixed fallbacks (older iOS Safari) and keeps the button
 * label in sync with the current fullscreen state so Esc / system
 * gestures don't leave the UI stuck.
 */
type HudMetric = { label: string; value: string; emphasis?: 'warn' | 'normal' };

type HudData = {
  live?: boolean;              // show pulsing LIVE badge
  title?: string;              // broadcaster display name
  subtitle?: string;           // short wallet / region hint
  metrics?: HudMetric[];       // remaining, viewers, cost, etc.
  /**
   * Broadcaster avatar URL. When set, VideoPane renders a small
   * round avatar to the LEFT of the title block. Especially
   * important for AI-agent broadcasts in `signaling_only` mode
   * where the underlying <video> might be black (no RTP) — without
   * the avatar pill the viewer has no visual confirmation of WHO is
   * actually broadcasting, even though the text says "G…XYZ is
   * broadcasting" in the LIVE chip.
   */
  avatarUrl?: string | null;
  /**
   * Broadcaster wallet G-address. When set, VideoPane renders an
   * inline <AgentBadge wallet={...} /> next to the title so the
   * "this broadcaster is an AI agent" indicator is visible directly
   * on the video pane (it was previously only in the overlay's
   * header, hidden whenever the user fullscreened the video).
   */
  wallet?: string | null;
  fullscreenStop?: {           // optional red "end" button shown only in FS
    label: string;
    onClick: () => void;
  };
};

/**
 * A single fullscreen-action button.  Rendered as a compact pill so a
 * row of them fits on a phone in landscape.  `variant` maps to the
 * existing HUD colour language (`danger` = red "end", `warn` = amber
 * "extend in the last minute", `accent` = purple "tip / gift",
 * anything else = neutral grey-glass).
 */
type FullscreenAction = {
  key: string;
  /** Button content — accepts ReactNode so callers can embed an
   *  inline `<StellarIcon />` alongside the text label. */
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  /** Visual variant.  `'gift'` renders a yellow-on-white tip style
   *  so the Stellar-branded "Gift" CTA stands out without reusing
   *  the red/danger colour reserved for Stop / End. */
  variant?: 'neutral' | 'danger' | 'accent' | 'warn' | 'gift';
};

/**
 * Collapsible technical-details card rendered under the "relay
 * fallback failed" message.  Shows ICE / WS states and the
 * *categories* (not IPs) of candidates gathered by each peer so
 * remote support can triage an iPhone Safari black-screen without
 * requiring the user to open devtools — which iOS doesn't expose.
 *
 * Interpretation cheat sheet for operators reading the output:
 *   - `remote: (none)`          → signaling delivered no candidates; broadcaster never left `new` state.
 *   - `local: [mdns] only`      → Chrome wrapped host IPs in .local; iOS can't resolve → need srflx/relay.
 *   - `local: [host, srflx]`    → direct path should work; if it didn't, a middlebox dropped UDP.
 *   - `local: [] in relay-only` → TURN server unreachable from this network (port 443 blocked).
 *   - `wsState: closed early`   → the signaling WSS dropped; check Azure WebSocket health.
 */
const ConnectionDetails: React.FC<{ diag: ViewerDiagnostics }> = ({ diag }) => {
  const [open, setOpen] = useState(false);
  const fmtList = (xs: string[]) => xs.length ? xs.join(', ') : '(none)';
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      style={connectionDetailsStyle}
    >
      <summary style={connectionSummaryStyle}>Show connection details</summary>
      <div style={connectionBodyStyle}>
        <div><strong>WebSocket:</strong> {diag.wsState}</div>
        <div><strong>ICE state:</strong> {diag.iceState} (gathering: {diag.iceGatheringState})</div>
        <div><strong>Connection:</strong> {diag.pcState}</div>
        <div><strong>Policy:</strong> {diag.iceTransportPolicy}</div>
        <div><strong>Local candidates:</strong> {fmtList(diag.localCandidateTypes)}</div>
        <div><strong>Remote candidates:</strong> {fmtList(diag.remoteCandidateTypes)}</div>
        <div><strong>Elapsed:</strong> {(diag.elapsedMs / 1000).toFixed(1)}s</div>
      </div>
    </details>
  );
};

const VideoPane: React.FC<{
  /**
   * Callback ref — the parent tracks the <video> element in state so
   * srcObject can be (re)attached across remounts.  VideoPane fans
   * out to its own internal ref too so the fullscreen button can
   * still reach the element as a fallback.
   */
  videoRef: (el: HTMLVideoElement | null) => void;
  /** Initial muted state.  For the broadcaster's own preview this is
   *  always true (we don't want to hear our own mic).  For remote
   *  viewer streams it starts true as well — iOS Safari otherwise
   *  refuses to autoplay a <video> with unmuted audio, which presents
   *  as a black frame until the user interacts.  When `unmuteable`
   *  is true we render a tap-to-unmute overlay so the user can opt in. */
  muted?: boolean;
  /** Show a tap-to-unmute control.  Only set this on remote viewer
   *  streams — the broadcaster never needs to hear their own mic. */
  unmuteable?: boolean;
  label?: string;
  hud?: HudData;
  /**
   * TikTok-style swipe-between-broadcasts.  When any of these are
   * set, the pane draws chevron controls and listens for vertical
   * swipes on the container.  The parent — not VideoPane — decides
   * what "next" and "previous" actually mean (the algorithm lives
   * one level up in App.tsx + `liveFeedAlgorithms.ts`).
   */
  onNext?: () => void;
  onPrev?: () => void;
  nextLabel?: string | null;
  prevLabel?: string | null;
  /**
   * Live-gift toasts to float in the top-right corner of the pane.
   * Rendered *inside* the container so the toasts remain visible when
   * the container is fullscreened — the old implementation rendered
   * them as a DOM sibling which got hidden by the browser's
   * fullscreen stacking context.
   */
  giftToasts?: LiveGiftEvent[];
  /**
   * Compact action bar shown at the bottom-center of the pane while
   * fullscreen is active.  Lets the viewer tip/gift/extend and the
   * broadcaster pause/extend/end without having to exit fullscreen
   * — the regular button row below the pane is hidden by the browser
   * while a fullscreen element is active.
   */
  fullscreenActions?: FullscreenAction[];
  /**
   * Arbitrary children rendered INSIDE the pane's container, on top
   * of the <video> but below the HUD.  Used for the GeoTrust Live
   * Tools layer (price feeds, charts, contract reads, trustlines)
   * so the tools stay visible when the user fullscreens — a
   * fullscreen element creates its own stacking context, and any DOM
   * sibling outside the container would be hidden by the browser.
   * Same trick we use for `giftToasts`, just exposed as a slot so the
   * caller controls what to render.
   */
  overlaySlot?: React.ReactNode;
  /**
   * Fired SYNCHRONOUSLY from inside the tap-to-unmute click
   * handler, before we flip `<video>.muted = false`. The
   * signaling-only avatar engine uses this hook to call
   * `engine.unlockAudio()` while the user-gesture context is still
   * active — without it, Safari/Chrome leave the underlying
   * AudioContext in `state: 'suspended'` and every BufferSource
   * scheduled for TTS playback emits silence even though the
   * <video> element is unmuted. (Bug report from the geoai team
   * 2026-05-06; root cause = §4-A in the report.) Optional;
   * other VideoPane instances (broadcaster preview, owner-on-
   * second-device) don't need it.
   */
  onUnmute?: () => void;
}> = ({ videoRef, muted, unmuteable, label, hud, onNext, onPrev, nextLabel, prevLabel, giftToasts, fullscreenActions, overlaySlot, onUnmute }) => {
  const containerRef    = useRef<HTMLDivElement | null>(null);
  const localVideoNode  = useRef<HTMLVideoElement | null>(null);
  const [isFs, setIsFs] = useState(false);
  // Internal muted state — seeded from the prop but toggleable by the
  // Unmute button.  We keep it in sync if the parent deliberately
  // remounts us with a different initial mute policy.
  const [isMuted, setIsMuted] = useState<boolean>(muted !== false);
  useEffect(() => { setIsMuted(muted !== false); }, [muted]);

  // Fan-out callback: keep both the parent's tracker and our local
  // ref in sync so the fullscreen button has a live handle to the
  // <video> element even if the parent's state hasn't flushed yet.
  const setVideoNode = useCallback((el: HTMLVideoElement | null) => {
    localVideoNode.current = el;
    videoRef(el);
  }, [videoRef]);

  // Fullscreen on iOS is tricky.  iOS Safari (and all iOS browsers,
  // since they're all WebKit under the hood) supports the standard
  // Element.requestFullscreen API on *arbitrary* elements only from
  // iOS 16.4+; older iOS and iPadOS only let you fullscreen the
  // <video> element itself via the non-standard webkitEnterFullscreen.
  // We try the standard container-level API first (so desktop keeps
  // the HUD visible in fullscreen) and fall back to the iOS-native
  // video path if either the method is missing or the promise is
  // rejected at runtime.  Same story on exit.
  const enterFullscreen = useCallback(async () => {
    const container: any = containerRef.current;
    const video:     any = localVideoNode.current;

    // 1) Standard / desktop / iOS 16.4+ path — fullscreens the
    //    container so the HUD (Live badge, metrics, Stop button)
    //    remains visible.
    if (container) {
      try {
        if (typeof container.requestFullscreen === 'function') {
          await container.requestFullscreen();
          return;
        }
        if (typeof container.webkitRequestFullscreen === 'function') {
          container.webkitRequestFullscreen();
          return;
        }
      } catch { /* fall through to video-native path */ }
    }

    // 2) iOS-native video fullscreen — pops the system player.
    //    The HUD isn't preserved (iOS draws its own chrome) but
    //    the video plays fullscreen which is what the user asked for.
    if (video && typeof video.webkitEnterFullscreen === 'function') {
      try { video.webkitEnterFullscreen(); return; } catch { /* ignore */ }
    }

    // 3) Last resort: request fullscreen on the video element.
    if (video) {
      try {
        if (typeof video.requestFullscreen === 'function') await video.requestFullscreen();
        else if (typeof video.webkitRequestFullscreen === 'function') video.webkitRequestFullscreen();
      } catch { /* ignore */ }
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    const d: any = document;
    const video: any = localVideoNode.current;
    // Standard / desktop path.
    try {
      if (typeof d.exitFullscreen === 'function') { await d.exitFullscreen(); return; }
      if (typeof d.webkitExitFullscreen === 'function') { d.webkitExitFullscreen(); return; }
    } catch { /* fall through to iOS path */ }
    // iOS path: exit is called directly on the video element, not document.
    if (video && typeof video.webkitExitFullscreen === 'function') {
      try { video.webkitExitFullscreen(); } catch { /* ignore */ }
    }
  }, []);

  const toggle = useCallback(() => {
    if (isFs) exitFullscreen(); else enterFullscreen();
  }, [isFs, enterFullscreen, exitFullscreen]);

  // Track fullscreen state from both the standard `document`
  // transition (desktop + iOS 16.4+) AND the legacy iOS
  // `webkitbeginfullscreen` / `webkitendfullscreen` events that
  // fire on the `<video>` element itself.  Without the per-video
  // listener, the iOS native player goes fullscreen but `isFs`
  // never flips — so our exit button stays hidden and the next
  // double-tap can't trigger exit.
  useEffect(() => {
    const d: any = document;
    const sync = () => {
      const video: any = localVideoNode.current;
      const docFs = !!(d.fullscreenElement || d.webkitFullscreenElement);
      const iosFs = !!(video && video.webkitDisplayingFullscreen);
      setIsFs(docFs || iosFs);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync as EventListener);
    const video = localVideoNode.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', sync as EventListener);
      video.addEventListener('webkitendfullscreen',   sync as EventListener);
    }
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync as EventListener);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', sync as EventListener);
        video.removeEventListener('webkitendfullscreen',   sync as EventListener);
      }
    };
  }, []);

  const hasMetrics = (hud?.metrics?.length ?? 0) > 0;
  const showTopBadge = hud?.live || hud?.title || hud?.avatarUrl || hud?.wallet;

  // Tap-to-unmute handler.  Safari requires the `.play()` call to
  // happen inside the user-gesture context that toggled `muted`, so
  // we set `.muted = false` imperatively on the element and re-call
  // play() before updating React state — that way the mute flip and
  // the play resume are both bound to the same input event.
  const handleUnmute = useCallback(() => {
    // FIRE THE PARENT HOOK FIRST. The signaling-only engine's
    // `unlockAudio()` calls `AudioContext.resume()`, which browsers
    // require to happen INSIDE the user-gesture stack. If we
    // flipped `el.muted = false` first React might suspend us for
    // a microtask and the resume() promise would land outside the
    // gesture, leaving the context suspended even after the user
    // tapped. Order: parent hook → element mute flip → React
    // state update.
    if (onUnmute) {
      try { onUnmute(); } catch { /* parent handler error — non-fatal */ }
    }
    const el = localVideoNode.current;
    if (el) {
      try { el.muted = false; } catch { /* ignore */ }
      el.play?.().catch(() => { /* user will need to retry */ });
    }
    setIsMuted(false);
  }, [onUnmute]);

  // ── TikTok-style swipe-between-broadcasts ──
  //
  // We listen for vertical drag gestures on the video container
  // and fire onNext / onPrev once the user has moved far enough
  // AND the gesture is predominantly vertical.  Horizontal swipes
  // are left alone so Mapbox pans underneath the overlay and
  // browser swipe-back gestures keep working as expected.
  const swipeRef = useRef<{ x: number; y: number; t: number; firing: boolean } | null>(null);
  const SWIPE_DIST_PX     = 60;   // total vertical distance required
  const SWIPE_MAX_HOR_RATIO = 0.6; // horizontal must stay ≤ 60% of vertical
  const SWIPE_MAX_MS      = 700;   // gesture must complete within this window

  // ── Manual double-tap detection (iOS Safari) ──
  //
  // React's `onDoubleClick` proxies the DOM `dblclick` event.  On
  // iOS Safari that event is unreliable for touch input — Safari
  // interprets two fast taps as a zoom gesture and suppresses
  // `dblclick` entirely unless `touch-action: manipulation` is set
  // *and* the viewport meta allows it.  We add our own touch-end
  // debouncer so the double-tap-to-fullscreen gesture works
  // identically on iPhone, Android, desktop and Tesla's Chromium.
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);
  const DOUBLE_TAP_MS = 350;   // max gap between the two taps
  const DOUBLE_TAP_SLOP = 24;  // max finger drift between taps (px)

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!onNext && !onPrev) return;
    const t = e.touches[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), firing: false };
  }, [onNext, onPrev]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    const t = e.changedTouches[0];
    if (t) {
      // Double-tap check runs FIRST and is independent of the swipe
      // gesture so the two features never fight: a stationary tap
      // pair toggles fullscreen; a directional drag scrolls
      // broadcasts.  If a user both drags and double-taps, the
      // double-tap loses deliberately (drag wins).
      const now = Date.now();
      const prev = lastTapRef.current;
      const dxSwipe = s ? Math.abs(t.clientX - s.x) : 0;
      const dySwipe = s ? Math.abs(t.clientY - s.y) : 0;
      const wasStationary = dxSwipe < DOUBLE_TAP_SLOP && dySwipe < DOUBLE_TAP_SLOP;
      if (wasStationary && prev
          && (now - prev.t) < DOUBLE_TAP_MS
          && Math.abs(t.clientX - prev.x) < DOUBLE_TAP_SLOP
          && Math.abs(t.clientY - prev.y) < DOUBLE_TAP_SLOP) {
        lastTapRef.current = null;
        // `toggle` is stable — declared below via useCallback — so
        // we grab the latest via the captured ref.  React queues the
        // state update; iOS Safari happily enters fullscreen inside
        // a touchend handler, preserving the user-gesture context
        // needed by requestFullscreen().
        toggle();
        return;
      }
      lastTapRef.current = wasStationary ? { t: now, x: t.clientX, y: t.clientY } : null;
    }
    if (!s || s.firing || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const dt = Date.now() - s.t;
    if (dt > SWIPE_MAX_MS) return;
    if (Math.abs(dy) < SWIPE_DIST_PX) return;
    if (Math.abs(dx) > Math.abs(dy) * SWIPE_MAX_HOR_RATIO) return;
    // Swipe up → next (feed flows upwards like TikTok).
    if (dy < 0) onNext?.();
    else         onPrev?.();
  }, [onNext, onPrev, toggle]);

  return (
    <div
      ref={containerRef}
      style={isFs ? videoPaneStyleFullscreen : videoPaneStyle}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <video
        ref={setVideoNode}
        autoPlay
        playsInline
        muted={isMuted}
        onDoubleClick={toggle}
        style={isFs ? videoStyleFullscreen : videoStyle}
        aria-label={label}
      />

      {/* Caller-supplied overlays — Live Tools live here so they
          remain visible when the pane is fullscreened. */}
      {overlaySlot}

      {/* iOS Safari autoplay gatekeeper.  A remote video with audio
          cannot autoplay without a user gesture — so we start muted
          (so pixels flow immediately, no black screen) and surface a
          prominent tap-to-unmute overlay.  Desktop browsers ignore
          this and just honour autoPlay anyway. */}
      {unmuteable && isMuted && (
        <button
          type="button"
          onClick={handleUnmute}
          aria-label="Unmute broadcast audio"
          style={unmuteOverlayStyle}
        >
          <span style={unmuteIconStyle}>🔇</span>
          <span>Tap to unmute</span>
        </button>
      )}

      {showTopBadge && (
        <div style={hudTopLeftStyle}>
          {hud?.live && (
            <span style={liveHudBadgeStyle} aria-label="Live">
              <span style={livePulseDotStyle} />
              LIVE
            </span>
          )}
          {/* Round avatar pill — drawn over the video so the viewer
              instantly knows WHO is broadcasting. Especially
              important for AI-agent signaling-only broadcasts where
              the underlying <video> can be black (no RTP). Falls
              back to a transparent pill on broken / missing image. */}
          {hud?.avatarUrl && (
            <img
              src={hud.avatarUrl}
              alt=""
              referrerPolicy="no-referrer"
              onError={(e) => {
                // Quietly hide the broken image — `onerror` setting
                // src="" would loop; visibility:hidden keeps the
                // layout slot but skips the broken-image icon.
                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
              }}
              style={{
                width:        28,
                height:       28,
                borderRadius: '50%',
                objectFit:    'cover',
                border:       '2px solid rgba(255, 255, 255, 0.85)',
                boxShadow:    '0 2px 8px rgba(0, 0, 0, 0.45)',
                background:   'rgba(15, 23, 42, 0.6)',
              }}
            />
          )}
          {(hud?.title || hud?.wallet) && (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
              {hud?.title && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ ...hudTitleStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {hud.title}
                  </span>
                  {/* Inline AgentBadge — surfaces the "🤖 AI agent"
                      pill directly on the video pane (it was
                      previously only visible in the profile
                      overlay's header, which is hidden in
                      fullscreen). AgentBadge handles its own
                      "this wallet IS an agent" detection so it's
                      safe to mount unconditionally — non-agent
                      broadcasters render nothing. */}
                  {hud?.wallet && <AgentBadge wallet={hud.wallet} compact />}
                </div>
              )}
              {hud?.subtitle && <span style={hudSubtitleStyle}>{hud.subtitle}</span>}
            </div>
          )}
        </div>
      )}

      {hasMetrics && (
        <div style={hudBottomLeftStyle}>
          {hud!.metrics!.map((m) => (
            <div
              key={m.label}
              style={{
                ...hudMetricStyle,
                color: m.emphasis === 'warn' ? '#fecaca' : '#e5e7eb',
              }}
            >
              <span style={hudMetricLabelStyle}>{m.label}</span>
              <span style={hudMetricValueStyle}>{m.value}</span>
            </div>
          ))}
        </div>
      )}

      {isFs && hud?.fullscreenStop && (
        <button
          type="button"
          onClick={hud.fullscreenStop.onClick}
          style={hudStopBtnStyle}
        >
          {hud.fullscreenStop.label}
        </button>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={isFs ? 'Exit fullscreen' : 'Enter fullscreen'}
        title={isFs ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
        style={fullscreenBtnStyle}
      >
        {isFs ? '⤫' : '⤢'}
      </button>

      {/* TikTok-style chevrons: render whenever the parent wired at
          least one navigation callback (so the controls appear even
          if the viewer is at the end of the feed — we just hide the
          "disabled" side).  In fullscreen we push them toward the
          right edge so they don't fight the Stop-watching button. */}
      {(onPrev || onNext) && (
        <>
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous live broadcast"
              title={prevLabel ? `← ${prevLabel}` : 'Previous broadcast (swipe down)'}
              style={{ ...feedNavBtnStyle, top: isFs ? '40%' : '38%' }}
            >
              ▲
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              aria-label="Next live broadcast"
              title={nextLabel ? `→ ${nextLabel}` : 'Next broadcast (swipe up)'}
              style={{ ...feedNavBtnStyle, top: isFs ? '60%' : '62%' }}
            >
              ▼
            </button>
          )}
          {/* One-time hint overlay — a narrow strip at the top of
              the pane telling the user they can swipe between
              broadcasts.  Kept subtle so it doesn't cover the HUD
              badges. */}
          <div style={feedSwipeHintStyle} aria-hidden>
            Swipe ↕ for next live
          </div>
        </>
      )}

      {/* Gift toasts live *inside* the pane so they remain visible
          while the pane is fullscreened — a fullscreen element creates
          its own stacking context and DOM siblings disappear.  The
          toasts are absolutely positioned so they don't push the
          video content around. */}
      {giftToasts && giftToasts.length > 0 && (
        <GiftToastLayer toasts={giftToasts} />
      )}

      {/* Fullscreen action bar — pill-shaped buttons at the bottom of
          the pane.  Only rendered while fullscreen is active since the
          regular button row below the pane is hidden by the browser
          in that state.  The bar lets viewers tip/gift/stop and
          broadcasters pause/extend/end without exiting fullscreen. */}
      {isFs && fullscreenActions && fullscreenActions.length > 0 && (
        <div style={fsActionBarStyle} role="toolbar" aria-label="Broadcast actions">
          {fullscreenActions.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={a.onClick}
              disabled={!!a.disabled}
              title={a.title}
              style={{
                ...fsActionBtnStyle,
                ...(a.variant === 'danger' ? fsActionBtnDangerStyle
                  : a.variant === 'accent' ? fsActionBtnAccentStyle
                  : a.variant === 'warn'   ? fsActionBtnWarnStyle
                  : a.variant === 'gift'   ? fsActionBtnGiftStyle
                  : null),
                opacity: a.disabled ? 0.5 : 1,
                cursor: a.disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Broadcaster live-tools pane ────────────────────────────────────────────

/**
 * Collapsible wrapper around `LiveToolsControlPanel`.
 *
 * The control panel itself is a fairly tall, multi-section editor (add
 * tool, configure type-specific fields, toggle visibility, change refresh
 * interval, force-refresh, remove).  Mounting it inline below the local
 * camera preview pushes the broadcast HUD off-screen on phones, so we
 * default to *collapsed* and let the broadcaster expand only when they
 * actually want to manage tools.  When collapsed it's just a thin
 * status row — pointing out how many tools are queued / live without
 * tearing down the panel's WS subscription.  Expanded, it's the full
 * editor; the panel's internal subscriptions live for as long as it's
 * mounted, which is fine because they're cheap (event-bus only) and the
 * subscriber filters by `sessionId`.
 */
const BroadcasterLiveToolsPane: React.FC<{
  sessionId: string;
  grantToken: string | null;
  broadcasterWallet: string;
  pilotEntities: EntityRecord[];
}> = ({ sessionId, grantToken, broadcasterWallet, pilotEntities }) => {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <div style={broadcasterToolsPaneStyle}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        style={broadcasterToolsHeaderStyle}
        aria-expanded={!collapsed}
        title={collapsed
          ? 'Open the live tools control panel'
          : 'Hide the live tools control panel'}
      >
        <span style={{ fontWeight: 600 }}>Live tools</span>
        <span style={{ opacity: 0.7, fontSize: 11 }}>
          {grantToken
            ? collapsed ? 'Tap to manage' : 'Tap to hide'
            : 'Pay to start a session to enable editing'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12 }}>
          {collapsed ? '▾' : '▴'}
        </span>
      </button>
      {!collapsed && (
        <div style={{ marginTop: 8 }}>
          <LiveToolsControlPanel
            sessionId={sessionId}
            grantToken={grantToken}
            broadcasterWallet={broadcasterWallet}
            pilotEntities={pilotEntities}
          />
        </div>
      )}
    </div>
  );
};

const broadcasterToolsPaneStyle: React.CSSProperties = {
  marginTop: 12,
  width: '100%',
};

const broadcasterToolsHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 12px',
  background: 'rgba(15, 18, 25, 0.92)',
  color: '#f4f6fa',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 10,
  cursor: 'pointer',
  font: '500 12px/1.2 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  textAlign: 'left',
};

// ── Live tab ────────────────────────────────────────────────────────────────

const LiveTab: React.FC<{
  isOwner: boolean;
  isLive: boolean;
  wallet: string;
  record: ProfileRecord;
  policy: WebRtcPolicyConfig | null;
  phase: BroadcastPhase;
  costDisplay: string;
  maxDurationDisplay: string;
  /** V2: display strings for the *extension* action.  These may
   *  differ from cost/duration when the policy configures per-extension
   *  overrides (zero-semantics in `webrtcPolicyService` already
   *  resolves 0 → sessionCost / maxDuration). */
  extensionCostDisplay?: string;
  extensionDurationDisplay?: string;
  /** Populated with a human-readable reason when the extension action
   *  must be disabled (max extensions reached, total-duration cap hit,
   *  min-extension floor exceeds headroom).  Rendered as a tooltip on
   *  the greyed-out button. */
  extensionGateReason?: string | null;
  extensionDisabled?: boolean;
  /** Viewer-paid extensions can be disabled per-country.  Broadcasters
   *  can still pay-again themselves, so this flag only hides the
   *  *viewer* Tip button. */
  viewerExtensionsDisabled?: boolean;
  remaining: number;
  activeSession: LiveSession | null;
  broadcast: BroadcastController | null;
  viewer: ViewerController | null;
  viewerCount: number;
  endedReason: string | null;
  notice: string | null;
  approx: ApproxLocation | null;
  onStart: () => void;
  /** Fires the post-payment "Start Live" confirmation (begins the
   *  3-2-1 countdown and then publishes to the SFU).  Separate from
   *  `onStart` so the policy/payment pipeline can complete without
   *  the WebRTC traffic starting — gives broadcasters a deliberate
   *  gate between "I've been charged" and "I'm on air". */
  onGoLive?: () => void;
  /** Go-live visibility scope selector (owner-side, pre-broadcast). */
  visibility?: 'public' | 'groups';
  onVisibilityChange?: (v: 'public' | 'groups') => void;
  availableGroups?: { id: number; name: string }[];
  selectedGroupIds?: number[];
  onToggleGroup?: (id: number) => void;
  /** Viewer-side: true when a restricted room refused this watcher. */
  viewerScopeBlocked?: boolean;
  /** Live countdown value (3 → 2 → 1 → null).  Null means either
   *  we haven't started the countdown yet or the countdown finished
   *  and we're in `connecting`.  Rendered as a fullscreen overlay
   *  on top of the local camera preview so the broadcaster sees
   *  themselves while the timer ticks. */
  countdownSec?: number | null;
  /**
   * Tap-to-unmute hook for signaling-only viewer broadcasts.
   * Called SYNCHRONOUSLY from inside the user gesture that
   * unmutes the watch <video>. The parent ProfileOverlay binds
   * this to `signalingOnlyEngineRef.current?.unlockAudio()` so
   * `AudioContext.resume()` runs in the same gesture stack as
   * the unmute flip — fixes the "tap unmute, hear nothing"
   * regression reported by the geoai team (2026-05-06). No-op
   * for media-broadcast viewers (engine is null then).
   */
  onUnlockSignalingAudio?: () => void;
  /** Broadcaster-only per-session audience ledger.  Used to render
   *  the end-of-broadcast summary card.  Viewer devices pass an
   *  empty array — the prop is still threaded through so TypeScript
   *  doesn't need conditional typing for the two sides. */
  audienceLog?: Array<{
    peerId: string;
    wallet?: string;
    country?: number | null;
    joinedAt: number;
    leftAt: number | null;
  }>;
  /** True when the broadcast we're watching has just ended.  The
   *  VideoPane keeps the last frame visible and we render an
   *  overlay on top with "This broadcast has ended" + a Next
   *  button (if another live is available nearby). */
  viewerLiveEnded?: boolean;
  /** Broadcaster-side pause flag — flips the Pause / Resume
   *  button label and renders the "Paused" pill on the local
   *  preview. */
  broadcastPaused?: boolean;
  /** Viewer-side mirror — renders the "Broadcast paused"
   *  overlay on the remote video pane. */
  remoteBroadcastPaused?: boolean;
  /** Fires when the broadcaster taps Pause / Resume. */
  onTogglePause?: () => void;
  /** Current screen-share mode (null = not sharing). */
  screenShareMode?: 'fullscreen' | 'split' | null;
  /** Begin (or switch) a screen share.  The mode determines the
   *  layout: `'fullscreen'` replaces the camera, `'split'`
   *  composites the camera over the screen share as PiP. */
  onStartScreenShare?: (mode: 'fullscreen' | 'split') => void;
  /** Stop screen sharing; restores the camera as the sole video
   *  track on every viewer + the local preview. */
  onStopScreenShare?: () => void;
  /** True when the current viewer has a signer wallet AND is not
   *  the owner — enables the "Send gift" button during a watch. */
  canSendGift?: boolean;
  /** Fires when the watcher taps "Send gift" — parent opens the
   *  LiveGiftModal which handles trustline intersection + payment. */
  onOpenGift?: () => void;
  /** Recently-received gifts (rolled into a toast stack).  Capped
   *  to the most recent 6 entries by the parent; each toast auto-
   *  dismisses after ~6 seconds. */
  giftToasts?: LiveGiftEvent[];
  onStop: () => void;
  onWatch: () => void;
  /**
   * Retry "Watch live" with `iceTransportPolicy: 'relay'`.  Surfaced
   * as a secondary button after a NAT/ICE failure so users on strict
   * 5G or in-vehicle networks can force TURN without understanding
   * any of that jargon.
   */
  onWatchViaRelay?: () => void;
  /** True while the currently-running (or last-ended) attempt was
   *  itself already a relay-only run.  Used to suppress the retry
   *  button — if relay already failed, a second relay attempt won't
   *  magically succeed and we don't want to loop the user. */
  watchUsedRelay?: boolean;
  /** Last diagnostic snapshot from the running viewer — rendered in
   *  a collapsible "Show connection details" block after a NAT
   *  failure so remote support can diagnose iPhone Safari sessions
   *  without browser devtools. */
  watchDiag?: ViewerDiagnostics | null;
  onStopWatch: () => void;
  onMinimize?: () => void;
  /** Extend/tip handler.  `fromViewer=false` → broadcaster pay-again;
   *  `fromViewer=true` → viewer tip to add time. */
  onExtend?: (fromViewer: boolean) => void;
  extending: boolean;
  /** Count of extensions already applied (for display). */
  extensionCount: number;
  localStream:  MediaStream | null;
  remoteStream: MediaStream | null;
  /**
   * Callback refs (not RefObject!) — stored as React state in the
   * parent so the srcObject-attach effect re-runs when the <video>
   * remounts after a minimise/restore round-trip.
   */
  localVideoRef:  (el: HTMLVideoElement | null) => void;
  remoteVideoRef: (el: HTMLVideoElement | null) => void;
  /** All cameras the browser exposes.  Empty on Tesla/kiosks. */
  videoDevices: VideoDeviceInfo[];
  selectedDeviceId: string | null;
  /** Called both before broadcast (update preference) and during
   *  (hot-swap video track across every viewer). */
  onSelectCamera: (deviceId?: string) => void | Promise<void>;
  flipping: boolean;
  /** Pre-broadcast probe: set when the platform exposes zero cameras
   *  so we can steer the user toward the "watch on this device" flow. */
  cameraProbeError: NormalisedCaptureError | null;
  /** TikTok-style swipe-between-broadcasts — forwarded verbatim to
   *  the viewer-side VideoPane so the caller (App.tsx) owns the
   *  algorithm choice and neighbour resolution. */
  onNextBroadcast?: () => void;
  onPrevBroadcast?: () => void;
  nextBroadcastLabel?: string | null;
  prevBroadcastLabel?: string | null;
  /** Discovery-polled session discovered for the owner's wallet —
   *  used to offer "Watch your live broadcast (from another device)"
   *  on a second logged-in device such as a Tesla / desktop. */
  remoteLiveSession: LiveSession | null;
  /** Human-readable name of the country whose policy is being
   *  evaluated (e.g. "United States").  Shown as a hint so the user
   *  understands which jurisdiction the banner below refers to. */
  countryName?: string;
  /** Source of the currently-evaluated country.  `'map'` is the one
   *  that commonly surprises users because the idle globe auto-spins
   *  and shifts which country the map centre is over. */
  countrySource?: 'location' | 'marker' | 'map';
  /** True when geolocation has already been granted and a player
   *  location is available.  Hides the "Use my location" button. */
  hasLocation?: boolean;
  /** Kicks off a geolocation request from within the Live tab so
   *  users can resolve the "country could not be determined" state
   *  without leaving the overlay to hit the game-panel Go Online
   *  button. */
  onRequestLocation?: () => void;
  /**
   * Live Tools wiring.  `liveSessionId` is used by both the overlay
   * (to scope which session's tools to render) and the broadcaster's
   * control panel (to address its REST endpoints).  `liveSessionGrant`
   * is the HMAC grant token returned by the paid-session API — it
   * authenticates the broadcaster's mutating tool calls without
   * exposing any secret key.  Both can be null before a session is
   * paid for; the tools UI will simply render nothing in that state.
   */
  liveSessionId?: string | null;
  liveSessionGrant?: string | null;
  /** Broadcaster opt-in for "Save broadcast to my profile".  Now
   *  displayed both *before* live (Start screen) and *during* live
   *  (collapsible HUD pill) so the broadcaster can flip their mind
   *  at any point.  The MediaRecorder runs opportunistically the
   *  whole time — this toggle only controls whether the buffered
   *  blob auto-uploads on stop or waits for an explicit Save. */
  saveBroadcastEnabled?: boolean;
  onToggleSaveBroadcast?: (enabled: boolean) => void;
  savedRecordingNotice?: string | null;
  /** Post-broadcast Save / Discard hooks.  Set whenever a recording
   *  is buffered in memory and the broadcaster hasn't yet committed
   *  to either saving or dropping it.  When `pendingRecordingMeta`
   *  is non-null the summary card renders Save / Discard CTAs. */
  pendingRecordingMeta?: null | { durationSec: number; sizeBytes: number };
  savingPendingRecording?: boolean;
  onSaveNow?: () => void;
  onDiscardRecording?: () => void;
  /** Slice 6b-ui — SFU-only live video source picker (camera / screen / drone RTP). */
  sfuBroadcastSource?: null | {
    current: 'camera' | 'screen' | 'drone';
    draft: string;
    onDraftChange: (v: string) => void;
    onApply: () => void;
    busy: boolean;
    drones: EntityRecord[];
    rtp: { ingestId: string; sourceId: string; rtp: SfuIngestSession['rtp'] } | null;
  };
  /** Wallet-linked drones / vehicles / GCS — shared with live-tools panel (one listEntities in parent). */
  pilotEntities?: EntityRecord[];
}> = ({
  isOwner, isLive, wallet, record, policy, phase, costDisplay, maxDurationDisplay,
  extensionCostDisplay, extensionDurationDisplay,
  extensionGateReason, extensionDisabled, viewerExtensionsDisabled,
  remaining, activeSession, broadcast, viewer, viewerCount,
  endedReason, notice, approx,
  onStart, onGoLive, countdownSec, audienceLog, viewerLiveEnded,
  visibility = 'public', onVisibilityChange, availableGroups = [],
  selectedGroupIds = [], onToggleGroup, viewerScopeBlocked = false,
  broadcastPaused, remoteBroadcastPaused, onTogglePause,
  screenShareMode, onStartScreenShare, onStopScreenShare,
  canSendGift, onOpenGift, giftToasts,
  onStop, onWatch, onWatchViaRelay, watchUsedRelay, watchDiag,
  onStopWatch, onMinimize, onExtend,
  extending, extensionCount,
  localStream, remoteStream,
  localVideoRef, remoteVideoRef,
  videoDevices, selectedDeviceId, onSelectCamera, flipping,
  cameraProbeError, remoteLiveSession,
  onNextBroadcast, onPrevBroadcast,
  nextBroadcastLabel, prevBroadcastLabel,
  countryName, countrySource, hasLocation, onRequestLocation,
  liveSessionId, liveSessionGrant,
  saveBroadcastEnabled, onToggleSaveBroadcast, savedRecordingNotice,
  pendingRecordingMeta, savingPendingRecording, onSaveNow, onDiscardRecording,
  sfuBroadcastSource,
  pilotEntities = [],
  onUnlockSignalingAudio,
}) => {
  const startedMs = activeSession?.startedAt ? Date.parse(activeSession.startedAt) : NaN;
  const elapsedSec = Number.isFinite(startedMs)
    ? Math.max(0, Math.floor((Date.now() - startedMs) / 1000))
    : 0;
  const endsAt = activeSession?.scheduledEndAt
    ? new Date(activeSession.scheduledEndAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  const broadcasterTitle = record.displayName || shortAddr(wallet);
  const approxLabel = formatApproxLocation(approx);
  if (!isOwner) {
    return (
      <div style={readOnlyCardStyle}>
        {/*
          Tesla's in-vehicle browser ships a Chromium build whose
          network stack blocks outbound WebRTC traffic to most
          third-party STUN/TURN hostnames.  We only learn this
          when ICE gathering returns zero candidates after ~20s of
          watchdog time — by which point the user has already
          clicked Watch live and waited.  A proactive banner saves
          them the confusion without gating the feature (Tesla
          firmware rotates UA strings so we don't want to hard-
          disable watching for false positives).
        */}
        {isLive && !viewer && isTeslaBrowser() && (
          <div style={teslaWarningStyle}>
            <strong>Tesla note:</strong> Tesla's in-vehicle browser
            often blocks outbound media traffic.  If the stream
            doesn't start, tether to your phone's hotspot and
            retry — watching on a phone or laptop also works.
          </div>
        )}
        {isLive ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!viewer ? (
              <button style={btnAccent} onClick={onWatch}>Watch live</button>
            ) : (
              <>
                <button style={btnDanger} onClick={onStopWatch}>Stop watching</button>
                {canSendGift && onOpenGift && (
                  <button
                    style={btnGift}
                    onClick={onOpenGift}
                    title="Tip the broadcaster with any Stellar trustline asset you both hold"
                  >
                    <StellarIcon size={16} /> Send gift
                  </button>
                )}
                {onMinimize && (
                  <button
                    onClick={onMinimize}
                    style={btnSecondary}
                    title="Keep watching but collapse the overlay so you can browse the map"
                  >
                    Minimise & browse map
                  </button>
                )}
              </>
            )}
            {onExtend && policy && Number(policy.sessionCost) > 0
              && !viewerExtensionsDisabled && (
              <button
                onClick={() => onExtend(true)}
                disabled={extending || !!extensionDisabled}
                style={{
                  ...btnSecondary,
                  opacity: (extending || extensionDisabled) ? 0.6 : 1,
                  cursor: extending
                    ? 'wait'
                    : (extensionDisabled ? 'not-allowed' : 'pointer'),
                }}
                title={
                  extensionGateReason
                  || `Pay ${extensionCostDisplay || costDisplay} XLM to add ${extensionDurationDisplay || maxDurationDisplay} to this broadcaster's live time`
                }
              >
                {extending
                  ? 'Tipping…'
                  : `Tip ${extensionCostDisplay || costDisplay} XLM → +${extensionDurationDisplay || maxDurationDisplay}`}
              </button>
            )}
            {viewerExtensionsDisabled && policy && Number(policy.sessionCost) > 0 && (
              <span
                style={{ fontSize: 11, color: '#94a3b8' }}
                title="The country admin has disabled viewer-paid extensions for this broadcaster."
              >
                Tips disabled
              </span>
            )}
            {extensionCount > 0 && (
              <span style={{ fontSize: 12, color: '#0f766e' }}>
                · {extensionCount}× extended
              </span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            This user isn't broadcasting live right now.
          </div>
        )}
        {(viewer || remoteStream) && (
          <div style={{ position: 'relative' }}>
            <VideoPane
              videoRef={remoteVideoRef}
              label="Live broadcast video"
              unmuteable
              onUnmute={onUnlockSignalingAudio}
              overlaySlot={
                liveSessionId
                  ? <LiveToolsLayer sessionId={liveSessionId} mode="viewer" />
                  : null
              }
              onNext={onNextBroadcast}
              onPrev={onPrevBroadcast}
              nextLabel={nextBroadcastLabel ?? null}
              prevLabel={prevBroadcastLabel ?? null}
              giftToasts={giftToasts}
              fullscreenActions={(() => {
                const acts: FullscreenAction[] = [];
                if (viewer) {
                  if (canSendGift && onOpenGift) {
                    acts.push({
                      key: 'gift',
                      label: (
                        <>
                          <StellarIcon size={14} style={{ marginRight: 6 }} /> Gift
                        </>
                      ),
                      onClick: onOpenGift,
                      variant: 'gift',
                      title: 'Tip the broadcaster with any shared trustline asset',
                    });
                  }
                  if (onExtend && policy && Number(policy.sessionCost) > 0 && !viewerExtensionsDisabled) {
                    acts.push({
                      key: 'tip-extend',
                      label: extending
                        ? 'Tipping…'
                        : `Tip ${extensionCostDisplay || costDisplay} XLM · +${extensionDurationDisplay || maxDurationDisplay}`,
                      onClick: () => onExtend(true),
                      disabled: !!extending || !!extensionDisabled,
                      variant: 'warn',
                      title: extensionGateReason
                        || `Pay to extend this broadcaster's live time`,
                    });
                  }
                  // Note: the prominent "Stop watching" red pill is
                  // already rendered by `hud.fullscreenStop` in the
                  // top-right corner, so we don't duplicate it here.
                }
                return acts;
              })()}
              hud={{
                live: isLive && !viewerLiveEnded,
                title: broadcasterTitle,
                subtitle: shortAddr(wallet),
                // Surface the broadcaster's avatar + wallet on the
                // pane itself so AI-agent broadcasts (which often
                // render a black <video> in signaling-only mode)
                // still tell the viewer WHO is broadcasting at a
                // glance. AgentBadge auto-detects whether the
                // wallet is registered as an agent and renders the
                // 🤖 chip; non-agent broadcasters get just the
                // avatar.
                avatarUrl: record.avatarUrl ?? null,
                wallet:    wallet || null,
                metrics: [
                  { label: 'Watching', value: String(Math.max(0, viewerCount)) },
                  ...(endsAt ? [{ label: 'Ends at', value: endsAt }] : []),
                  ...(activeSession?.scheduledEndAt
                    ? [{
                        label: 'Remaining',
                        value: formatDuration(remaining),
                        emphasis: (remaining <= 60 ? 'warn' : 'normal') as HudMetric['emphasis'],
                      }]
                    : []),
                  ...(elapsedSec > 0 ? [{ label: 'Elapsed', value: formatDuration(elapsedSec) }] : []),
                  ...(extensionCount > 0 ? [{ label: 'Extended', value: `${extensionCount}×` }] : []),
                  ...(approxLabel ? [{ label: '~ Region', value: approxLabel }] : []),
                ],
                fullscreenStop: viewer
                  ? { label: 'Stop watching', onClick: onStopWatch }
                  : undefined,
              }}
            />
            {remoteBroadcastPaused && !viewerLiveEnded && (
              <div style={pausedOverlayStyle} aria-live="polite">
                <div style={pausedBadgeStyle}>Paused</div>
                <div style={pausedTitleStyle}>Broadcast paused</div>
                <div style={pausedSubStyle}>
                  {broadcasterTitle} will be back in a moment.
                </div>
              </div>
            )}
            {viewerLiveEnded && (
              <div style={liveEndedOverlayStyle}>
                <div style={liveEndedBadgeStyle}>Live ended</div>
                <div style={liveEndedTitleStyle}>This broadcast has ended</div>
                <div style={liveEndedSubStyle}>
                  {broadcasterTitle} finished streaming
                  {endedReason ? ` · ${prettyReason(endedReason)}` : ''}
                </div>
                <div style={liveEndedActionsStyle}>
                  {nextBroadcastLabel && onNextBroadcast && (
                    <button
                      type="button"
                      onClick={onNextBroadcast}
                      style={btnAccent}
                      title={`Watch the next live broadcast from ${nextBroadcastLabel}`}
                    >
                      Next broadcast →
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onStopWatch}
                    style={btnSecondary}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {endedReason && (
          <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
            {prettyReason(endedReason)}
            {/*
              NAT/firewall failures are the #1 reason a paid "Watch
              live" session silently blackframes: the broadcaster's
              phone is on 5G (symmetric NAT) and the viewer is on a
              Tesla / corporate Wi-Fi that drops UDP.  Offer a
              one-tap retry that forces TURN-relay mode — if that
              also fails we hide the button so users don't loop on
              the same dead path.
            */}
            {!viewer && onWatchViaRelay && !watchUsedRelay &&
             isNatRelatedReason(endedReason) && (
              <div style={{ marginTop: 8 }}>
                <button
                  style={btnAccent}
                  onClick={onWatchViaRelay}
                  title="Route the video through our TURN relay — this works on 5G and Tesla Wi-Fi where direct peer-to-peer is blocked.  You won't be re-charged."
                >
                  Retry via relay (works on strict networks)
                </button>
              </div>
            )}
            {!viewer && watchUsedRelay && isNatRelatedReason(endedReason) && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>
                {isNoLocalCandidatesFailure(endedReason, watchDiag) ? (
                  <>
                    <strong>Your current network blocks outbound WebRTC media.</strong>
                    {' '}This is common on Tesla in-vehicle Wi-Fi, corporate
                    firewalls and guest networks — we couldn't reach any
                    STUN or TURN server at all.  The fastest workaround is
                    to tether to your phone's hotspot and retry; switching
                    to a home / cellular network also fixes it.
                  </>
                ) : (
                  <>
                    Relay fallback also failed — the TURN server may be
                    unreachable from your current network.  Try a
                    different Wi-Fi / mobile network, or ask the site
                    operator to check their TURN deployment.
                  </>
                )}
              </div>
            )}
            {/*
              Collapsible diagnostics.  Safe to render for ANY ended
              reason (not just NAT) because the payload contains no
              PII — just ICE / WS states and which *categories* of
              candidates were gathered.  Users can copy-paste this
              into a support chat when their iPhone goes black.
            */}
            {watchDiag && <ConnectionDetails diag={watchDiag} />}
          </div>
        )}
        {notice && <div style={errStyle}>{prettyReason(notice)}</div>}
      </div>
    );
  }

  return (
    <div style={readOnlyCardStyle}>
      {phase === 'loading_policy' && (
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Loading WebRTC policy
          {countryName ? <> for <strong>{countryName}</strong></> : null}
          {countrySource === 'map'
            ? <em style={{ color: '#94a3b8' }}> (from map centre)</em>
            : countrySource === 'location'
            ? <em style={{ color: '#94a3b8' }}> (from your location)</em>
            : countrySource === 'marker'
            ? <em style={{ color: '#94a3b8' }}> (from selected marker)</em>
            : null}
          …
        </div>
      )}

      {(phase === 'policy_blocked' || phase === 'ready_to_start' || phase === 'ended') && policy && (
        <div
          style={{
            padding: 10, borderRadius: 8, fontSize: 13,
            background: phase === 'policy_blocked' ? '#fef2f2' : '#eff6ff',
            border: '1px solid ' + (phase === 'policy_blocked' ? '#fecaca' : '#bfdbfe'),
          }}
        >
          <div style={{ fontWeight: 600 }}>
            {phase === 'policy_blocked' ? 'Live broadcast is not available' : 'Live broadcast'}
            {countryName && (
              <span style={{ marginLeft: 6, fontWeight: 400, color: '#475569', fontSize: 12 }}>
                · {countryName}
                {countrySource === 'map' && <em style={{ color: '#94a3b8' }}> (map centre)</em>}
              </span>
            )}
          </div>
          <div style={{ marginTop: 4, color: '#334155' }}>
            {policy.policy === WEBRTC_POLICY.ALLOWED && (
              <>Cost: <strong>{costDisplay} XLM</strong> · Max duration: <strong>{maxDurationDisplay}</strong>
                {policy.usingFallbackDuration && <em style={{ color: '#6b7280' }}> (backend fallback)</em>}</>
            )}
            {policy.policy === WEBRTC_POLICY.DISALLOWED && 'Country policy disallows WebRTC live sessions.'}
            {policy.policy === WEBRTC_POLICY.NOT_SET && 'Country policy has not enabled WebRTC live sessions yet.'}
          </div>
        </div>
      )}

      {/* Go Live visibility — public vs one-or-more trust groups. This
          one scope governs BOTH who sees you on the map while live AND
          who can watch your broadcast. Owner-only, pre-broadcast. */}
      {isOwner && !broadcast && !viewer && onVisibilityChange
        && (phase === 'idle' || phase === 'loading_policy' || phase === 'ready_to_start' || phase === 'policy_blocked') && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 8 }}>Who can see this session?</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onVisibilityChange('public')}
              style={{
                flex: '1 1 120px', padding: '8px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid ' + (visibility === 'public' ? '#2563eb' : '#cbd5e1'),
                background: visibility === 'public' ? '#dbeafe' : '#fff',
                fontWeight: visibility === 'public' ? 600 : 400,
              }}
            >
              🌐 Public
            </button>
            <button
              type="button"
              onClick={() => onVisibilityChange('groups')}
              style={{
                flex: '1 1 120px', padding: '8px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid ' + (visibility === 'groups' ? '#2563eb' : '#cbd5e1'),
                background: visibility === 'groups' ? '#dbeafe' : '#fff',
                fontWeight: visibility === 'groups' ? 600 : 400,
              }}
            >
              🔒 Trust group(s)
            </button>
          </div>
          {visibility === 'groups' && (
            <div style={{ marginTop: 10 }}>
              {availableGroups.length === 0 ? (
                <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 8 }}>
                  You have no trust groups yet. Create one first, or choose Public.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                    Only members of the selected group(s) will see you on the map and can watch your live.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                    {availableGroups.map((g) => (
                      <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedGroupIds.includes(g.id)}
                          onChange={() => onToggleGroup?.(g.id)}
                        />
                        <span>{g.name || `Group #${g.id}`}</span>
                      </label>
                    ))}
                  </div>
                  {selectedGroupIds.length === 0 && (
                    <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>
                      Select at least one group, or switch to Public.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Viewer-side: this broadcast is restricted to a trust group we
          are not a member of (or we can't sign the access challenge). */}
      {viewerScopeBlocked && !isOwner && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', fontSize: 13, color: '#991b1b' }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Restricted broadcast</div>
          <div>This live session is limited to the broadcaster's trust group. You need to be a member (and connected with a signing wallet) to watch.</div>
        </div>
      )}

      {/* Camera picker (pre-broadcast).  Hidden when there's only one
          camera or none — in the latter case we surface a helpful
          notice + a "Watch from this device" link further down. */}
      {!broadcast && videoDevices.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#334155', flexWrap: 'wrap' }}>
          <span>Camera:</span>
          <select
            value={selectedDeviceId || ''}
            onChange={(e) => { void onSelectCamera(e.target.value); }}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
          >
            {videoDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.facing === 'user' ? 'Front · ' : d.facing === 'environment' ? 'Rear · ' : ''}{d.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Zero-camera device detected up-front (Tesla, kiosk, desktop
          without webcam).  If we *also* discover this wallet already
          broadcasting from another device, show a prominent "Watch"
          button so the user can pull their own stream in one tap. */}
      {!broadcast && cameraProbeError?.kind === 'no_camera_device' && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: '#fef3c7', border: '1px solid #fcd34d', fontSize: 13, color: '#78350f' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No camera on this device</div>
          <div>
            This device does not expose a camera to web pages (common on Tesla infotainment, in-vehicle browsers, and kiosks).
            {remoteLiveSession
              ? ' Your wallet is broadcasting from another device right now — you can watch it here.'
              : ' You can still open this profile on your phone or laptop to start a broadcast.'}
          </div>
          {remoteLiveSession && !viewer && (
            <button
              onClick={onWatch}
              style={{ ...btnAccent, marginTop: 10 }}
            >
              Watch my live broadcast
            </button>
          )}
        </div>
      )}

      {/* Same-wallet second-device case even when the camera *is*
          available: we still haven't started a local broadcast, but
          discovery shows this wallet is already live elsewhere.  We
          offer "Watch" as the primary action rather than encouraging
          a second concurrent broadcast (which the hub will reject
          with `broadcaster_already_live` anyway). */}
      {!broadcast && !viewer && !cameraProbeError && remoteLiveSession && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: '#ecfeff', border: '1px solid #a5f3fc', fontSize: 13, color: '#0e7490' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>You are already live on another device</div>
          <div>Watch your own stream from here, or end the other session and start fresh.</div>
          <button
            onClick={onWatch}
            style={{ ...btnAccent, marginTop: 10 }}
          >
            Watch my live broadcast
          </button>
        </div>
      )}

      {/* "Save broadcast to my profile" opt-in.  Now shown to the
          owner at every phase that can produce a recording — pre-live
          (decide before going live), during-live (decide mid-stream
          if the broadcast turns out to be cool), and post-live (use
          the dedicated Save/Discard CTAs further down).  We always
          run the MediaRecorder while live regardless of this flag;
          the toggle just decides whether the buffered tape
          auto-uploads on stop or waits for an explicit Save click. */}
      {isOwner && onToggleSaveBroadcast && phase !== 'ended' && (
        <label style={saveBroadcastToggleStyle}>
          <input
            type="checkbox"
            checked={!!saveBroadcastEnabled}
            onChange={(e) => onToggleSaveBroadcast(e.target.checked)}
            style={{ marginRight: 8, transform: 'translateY(1px)' }}
          />
          <span>
            <strong style={{ fontWeight: 600 }}>Save broadcast to my profile</strong>
            <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {phase === 'live' || phase === 'connecting' || phase === 'countdown'
                ? 'Auto-saves a copy under "Past broadcasts" when this broadcast ends. You can also flip this off and decide on the summary screen.'
                : 'Keeps a copy under "Past broadcasts" so visitors can replay it later. Auto-deleted after 30 days unless pinned.'}
            </span>
          </span>
        </label>
      )}

      {/* Post-broadcast Save / Discard.  Shown when the recorder
          captured a tape but the broadcaster didn't auto-save (or the
          duration cap auto-ended the live before they could click).
          The 30-minute upload-grant window backs this UI server-side. */}
      {isOwner && pendingRecordingMeta && phase === 'ended' && (
        <div style={{
          marginTop: 12, padding: 12, borderRadius: 10,
          background: '#eff6ff', border: '1px solid #bfdbfe',
          fontSize: 13, color: '#1e3a8a',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Recording ready to save
          </div>
          <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
            Captured {Math.round(pendingRecordingMeta.durationSec)}s ·{' '}
            {(pendingRecordingMeta.sizeBytes / (1024 * 1024)).toFixed(1)} MB.
            Save it to your profile so visitors can replay it later, or discard
            it if you'd rather not.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onSaveNow?.()}
              disabled={!!savingPendingRecording}
              style={{
                ...btnAccent,
                opacity: savingPendingRecording ? 0.6 : 1,
                cursor: savingPendingRecording ? 'wait' : 'pointer',
              }}
            >
              {savingPendingRecording ? 'Saving…' : 'Save copy to my profile'}
            </button>
            <button
              type="button"
              onClick={() => onDiscardRecording?.()}
              disabled={!!savingPendingRecording}
              style={btnSecondary}
            >
              Discard
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
            You have ~30 minutes to decide. After that the upload window closes
            and the recording is dropped.
          </div>
        </div>
      )}

      {savedRecordingNotice && (
        <div style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 8,
          background: '#ecfdf5', border: '1px solid #a7f3d0',
          fontSize: 12, color: '#047857',
        }}>
          {savedRecordingNotice}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        {!broadcast && !remoteLiveSession && phase !== 'payment_pending' && phase !== 'connecting' && phase !== 'awaiting_go_live' && phase !== 'countdown' && (() => {
          // STRICT location guard mirror — must match the runtime guard
          // inside `handleStartBroadcast` (ProfileOverlay).  Buttons that
          // call `onStart` are disabled when the country wasn't resolved
          // from the viewer's actual GPS, so we don't let a user pay for
          // a country picked from a panned map centre or a marker click.
          const locationOk = countrySource === 'location' && !!hasLocation;
          const noCamera   = cameraProbeError?.kind === 'no_camera_device';
          const isDisabled =
            phase === 'loading_policy' ||
            phase === 'policy_blocked' ||
            !policy ||
            policy.policy !== WEBRTC_POLICY.ALLOWED ||
            noCamera ||
            !locationOk;
          const disabledTitle = !locationOk
            ? 'Tap "Go LIVE with my location" first — broadcasts require your real GPS so we can pin your stream on the map.'
            : noCamera
              ? 'No camera detected on this device'
              : undefined;
          return (
            <button
              onClick={() => { if (!isDisabled) onStart(); }}
              disabled={isDisabled}
              style={{
                ...btnAccent,
                opacity: isDisabled ? 0.6 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
              title={disabledTitle}
            >
              {!locationOk
                ? 'Share location to broadcast'
                : policy && Number(policy.sessionCost) > 0
                  ? `Pay ${costDisplay} XLM & continue`
                  : 'Start live broadcast'}
            </button>
          );
        })()}
        {phase === 'awaiting_go_live' && onGoLive && (
          <>
            <button onClick={onGoLive} style={btnGoLive} title="Begin the 3-2-1 countdown and go live">
              <span style={goLiveDotStyle} /> Start live
            </button>
            <button onClick={onStop} style={btnSecondary} title="Cancel without going live — the paid session will be released">
              Cancel
            </button>
          </>
        )}
        {phase === 'countdown' && (
          <span style={btnHint}>Going live in {countdownSec ?? '…'}…</span>
        )}
        {phase === 'payment_pending' && <span style={btnHint}>Waiting for wallet payment…</span>}
        {phase === 'connecting'      && <span style={btnHint}>Connecting to signaling hub…</span>}
        {broadcast && (
          <button onClick={onStop} style={btnDanger}>
            Stop broadcast ({viewerCount} viewer{viewerCount === 1 ? '' : 's'})
          </button>
        )}
        {broadcast && onTogglePause && (
          <button
            onClick={onTogglePause}
            style={broadcastPaused ? btnAccent : btnSecondary}
            title={broadcastPaused
              ? 'Resume sending video and audio to your viewers'
              : 'Pause your broadcast — viewers see a frozen frame and a "Paused" overlay'}
          >
            {broadcastPaused ? '▶ Resume broadcast' : '⏸ Pause broadcast'}
          </button>
        )}
        {broadcast && onStartScreenShare && onStopScreenShare && (
          screenShareMode ? (
            <>
              <button
                onClick={() => onStartScreenShare(screenShareMode === 'fullscreen' ? 'split' : 'fullscreen')}
                style={btnSecondary}
                title={screenShareMode === 'fullscreen'
                  ? 'Switch to split-screen (your camera stays visible as picture-in-picture)'
                  : 'Switch to full-screen share (hide your camera)'}
              >
                {screenShareMode === 'fullscreen' ? '🖥 Split-screen' : '🖥 Fullscreen share'}
              </button>
              <button onClick={onStopScreenShare} style={btnDanger} title="Stop sharing your screen and return to camera">
                Stop sharing
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onStartScreenShare('fullscreen')}
                style={btnSecondary}
                title="Share your screen — viewers see only the shared window/tab/display"
              >
                🖥 Share screen
              </button>
              <button
                onClick={() => onStartScreenShare('split')}
                style={btnSecondary}
                title="Share your screen with your camera in a picture-in-picture corner"
              >
                🖥 Share + camera PiP
              </button>
            </>
          )
        )}
        {broadcast && videoDevices.length > 1 && (
          <button
            onClick={() => { void onSelectCamera(); }}
            disabled={flipping}
            style={{ ...btnSecondary, opacity: flipping ? 0.6 : 1, cursor: flipping ? 'wait' : 'pointer' }}
            title="Switch to the other camera on this device (e.g. front ↔ rear)"
          >
            {flipping ? 'Switching…' : '🔄 Flip camera'}
          </button>
        )}
        {broadcast && onMinimize && (
          <button
            onClick={onMinimize}
            style={btnSecondary}
            title="Keep broadcasting but collapse the overlay so you can browse the map"
          >
            Minimise & browse map
          </button>
        )}
        {broadcast && sfuBroadcastSource && (
          <div style={{
            flexBasis: '100%',
            width: '100%',
            marginTop: 10,
            padding: 12,
            borderRadius: 10,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            fontSize: 12,
            color: '#422006',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Video source (SFU)</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginBottom: 8 }}>
              Active: <strong>{sfuBroadcastSource.current}</strong>
              {' · '}Pick a source, then <strong>Apply</strong>. Drone mode sends RTP from your companion link (GStreamer/ffmpeg) to the host/port below — your webcam can stay open locally for self-framing.
            </div>
            <details style={{ marginBottom: 10 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, outline: 'none' }}>
                FAA / safety — read before flying (U.S.)
              </summary>
              <div style={{ marginTop: 8, lineHeight: 1.5, fontSize: 11, color: '#451a03' }}>
                <p style={{ margin: '0 0 8px' }}>
                  GeoTrust only carries your video signal. <strong>You</strong> are the remote pilot in command (PIC). GeoTrust does <strong>not</strong> check your certificate, waivers, airspace, weight class, or whether your operation is legal — compliance with federal, state, and local rules is entirely your responsibility.
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    <strong>Which rule set applies?</strong> Most non-recreational work is under <strong>14 CFR Part 107</strong> (requires a Remote Pilot Certificate and following Part 107 operating rules). Most purely recreational flying falls under the <strong>Exception for Limited Recreational Operations</strong> (49 U.S.C. §44809) with different requirements. If you are unsure, stop and confirm before takeoff.
                  </li>
                  <li>
                    <strong>Visual line of sight (VLOS)</strong> — keep the aircraft within unaided visual line of sight (or follow only what your FAA waiver explicitly allows). A downlink or map does <strong>not</strong> replace VLOS.
                  </li>
                  <li>
                    <strong>14 CFR Part 89 — Remote ID</strong> — operate with Standard Remote ID, a Remote ID broadcast module, or only at FAA-recognised FRIAs as the rules require for your aircraft. Register the aircraft with the FAA when required; carry proof of registration when required.
                  </li>
                  <li>
                    <strong>Controlled airspace, TFRs, NOTAMs</strong> — use LAANC / authorisation where required; respect temporary flight restrictions and other active restrictions. GeoTrust does <strong>not</strong> grant airspace clearance or detect every restriction.
                  </li>
                  <li>
                    <strong>Recreational basics</strong> — if flying under the recreational exception, you must also follow the FAA recreational flyer requirements (e.g. TRUST for eligible recreational flyers, flying for recreation only, and applicable altitude / people / hazard rules). See <a href="https://www.faa.gov/uas" target="_blank" rel="noopener noreferrer">faa.gov/uas</a> for the current checklist.
                  </li>
                  <li>
                    This panel is <strong>not</strong> legal advice. When in doubt, use official FAA sources or a qualified aviation attorney.
                  </li>
                </ul>
              </div>
            </details>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span>Source</span>
                <select
                  value={sfuBroadcastSource.draft}
                  onChange={(e) => sfuBroadcastSource.onDraftChange(e.target.value)}
                  disabled={sfuBroadcastSource.busy}
                  style={{ minWidth: 220, fontSize: 12 }}
                >
                  <option value="camera">Camera (browser)</option>
                  <option value="screen_fs">Screen — fullscreen</option>
                  <option value="screen_split">Screen + camera PiP</option>
                  {sfuBroadcastSource.drones.length === 0 && (
                    <option value="drone:" disabled>No registered drones / vehicles</option>
                  )}
                  {sfuBroadcastSource.drones.map((d) => (
                    <option key={d.id} value={`drone:${d.id}`}>
                      Drone / vehicle: {d.displayName || d.id}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => sfuBroadcastSource.onApply()}
                disabled={sfuBroadcastSource.busy}
                style={{ ...btnAccent, opacity: sfuBroadcastSource.busy ? 0.6 : 1 }}
              >
                {sfuBroadcastSource.busy ? 'Applying…' : 'Apply video source'}
              </button>
            </div>
            {sfuBroadcastSource.rtp && (
              <div style={{
                marginTop: 10,
                padding: 8,
                borderRadius: 8,
                background: '#fff',
                border: '1px solid #fde68a',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 11,
                wordBreak: 'break-all',
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#92400e' }}>RTP target (send H.264 here)</div>
                <div>ingestId: {sfuBroadcastSource.rtp.ingestId}</div>
                <div>UDP: {sfuBroadcastSource.rtp.rtp.ip}:{sfuBroadcastSource.rtp.rtp.port}</div>
                <div>PT: {sfuBroadcastSource.rtp.rtp.payloadType} · SSRC: {sfuBroadcastSource.rtp.rtp.ssrc}</div>
                <div>{sfuBroadcastSource.rtp.rtp.mimeType}</div>
              </div>
            )}
          </div>
        )}
        {phase === 'live' && onExtend && policy && Number(policy.sessionCost) > 0 && (
          <button
            onClick={() => onExtend(false)}
            disabled={extending || remaining > 60 || !!extensionDisabled}
            style={{
              ...(remaining <= 60 ? btnDanger : btnSecondary),
              opacity: (extending || remaining > 60 || extensionDisabled) ? 0.55 : 1,
              cursor:
                (extending || remaining > 60 || extensionDisabled)
                  ? 'not-allowed'
                  : 'pointer',
            }}
            title={
              extensionGateReason
              || (remaining > 60
                ? `Extend becomes available in the last minute (T-${formatDuration(Math.max(0, remaining - 60))})`
                : `Pay ${extensionCostDisplay || costDisplay} XLM to add ${extensionDurationDisplay || maxDurationDisplay} to your broadcast`)
            }
          >
            {extending
              ? 'Extending…'
              : `Extend +${extensionDurationDisplay || maxDurationDisplay} · ${extensionCostDisplay || costDisplay} XLM`}
          </button>
        )}
      </div>

      {phase === 'live' && activeSession?.scheduledEndAt && (
        <div style={{ marginTop: 10, fontSize: 13, color: remaining <= 60 ? '#b91c1c' : '#0f172a' }}>
          Time remaining: <strong>{formatDuration(remaining)}</strong>
          {' · '}ends {new Date(activeSession.scheduledEndAt).toLocaleTimeString()}
          {extensionCount > 0 && (
            <span style={{ marginLeft: 8, color: '#0f766e' }}>
              · {extensionCount}× extended
            </span>
          )}
        </div>
      )}

      {(broadcast || localStream) && (
        <div style={{ position: 'relative' }}>
          {(phase === 'awaiting_go_live' || phase === 'countdown') && (
            <div style={countdownOverlayStyle} aria-live="polite">
              {phase === 'awaiting_go_live' ? (
                <>
                  <div style={countdownTitleStyle}>Ready when you are</div>
                  <div style={countdownSubStyle}>
                    Your payment is locked in. Tap <strong>Start live</strong> to begin the countdown.
                  </div>
                </>
              ) : (
                <>
                  <div style={countdownNumberStyle} key={countdownSec ?? 0}>
                    {countdownSec ?? ''}
                  </div>
                  <div style={countdownSubStyle}>Going live…</div>
                </>
              )}
            </div>
          )}
          {phase === 'live' && broadcastPaused && (
            <div style={pausedOverlayStyle} aria-live="polite">
              <div style={pausedBadgeStyle}>Paused</div>
              <div style={pausedTitleStyle}>You're paused</div>
              <div style={pausedSubStyle}>Viewers see a frozen frame. Tap Resume when you're ready.</div>
            </div>
          )}
        <VideoPane
          videoRef={localVideoRef}
          muted
          label="Your live camera preview"
          giftToasts={giftToasts}
          overlaySlot={
            /* Broadcaster preview shows tools in `broadcaster` mode so
               hidden / queued cards stay visible (dimmed) for the
               operator while staying hidden on every viewer.  The
               grant token is forwarded so cards become draggable —
               drag a card into a different corner / sidebar / center
               slot to reposition it without opening the panel. */
            liveSessionId
              ? <LiveToolsLayer
                  sessionId={liveSessionId}
                  mode="broadcaster"
                  grantToken={liveSessionGrant ?? null}
                />
              : null
          }
          fullscreenActions={(() => {
            const acts: FullscreenAction[] = [];
            if (broadcast && onTogglePause) {
              acts.push({
                key: 'pause',
                label: broadcastPaused ? '▶ Resume' : '⏸ Pause',
                onClick: onTogglePause,
                variant: broadcastPaused ? 'accent' : 'neutral',
                title: broadcastPaused
                  ? 'Resume sending video and audio to your viewers'
                  : 'Pause — viewers see a frozen frame and a paused overlay',
              });
            }
            if (phase === 'live' && onExtend && policy && Number(policy.sessionCost) > 0) {
              acts.push({
                key: 'extend',
                label: extending
                  ? 'Extending…'
                  : `Extend +${extensionDurationDisplay || maxDurationDisplay} · ${extensionCostDisplay || costDisplay} XLM`,
                onClick: () => onExtend(false),
                disabled: extending || remaining > 60 || !!extensionDisabled,
                variant: remaining <= 60 ? 'warn' : 'neutral',
                title: extensionGateReason
                  || (remaining > 60
                    ? `Extend becomes available in the last minute (T-${formatDuration(Math.max(0, remaining - 60))})`
                    : `Pay to add ${extensionDurationDisplay || maxDurationDisplay} to your broadcast`),
              });
            }
            // Note: "End broadcast" is already exposed via
            // `hud.fullscreenStop` as a prominent top-right red pill,
            // so we don't duplicate it in this action bar.
            return acts;
          })()}
          hud={{
            live: phase === 'live',
            title: broadcasterTitle,
            subtitle: `You · ${shortAddr(wallet)}`,
            avatarUrl: record.avatarUrl ?? null,
            wallet:    wallet || null,
            metrics: [
              { label: 'Viewers', value: String(viewerCount) },
              ...(activeSession?.scheduledEndAt
                ? [{
                    label: 'Remaining',
                    value: formatDuration(remaining),
                    emphasis: (remaining <= 60 ? 'warn' : 'normal') as HudMetric['emphasis'],
                  }]
                : []),
              ...(elapsedSec > 0 ? [{ label: 'Elapsed', value: formatDuration(elapsedSec) }] : []),
              ...(endsAt ? [{ label: 'Ends at', value: endsAt }] : []),
              ...(extensionCount > 0 ? [{ label: 'Extended', value: `${extensionCount}×` }] : []),
              ...(approxLabel ? [{ label: '~ Region', value: approxLabel }] : []),
              ...(policy && Number(policy.sessionCost) > 0
                ? [{ label: 'Paid', value: `${costDisplay} XLM` }]
                : []),
            ],
            fullscreenStop: broadcast
              ? { label: 'End broadcast', onClick: onStop }
              : undefined,
          }}
        />
        </div>
      )}

      {/*
        Broadcaster-only Live Tools control surface.  Rendered as a
        collapsible pane below the local preview so adding / configuring
        tools doesn't crowd the camera HUD or fight the gift overlay.
        Stays out of the way when collapsed (just a thin header bar) and
        expands to the full editor when the broadcaster wants to manage
        their layout.

        The pane is only mounted once the paid live session has reached
        a state the live-tools backend will accept (`PAID_READY` or
        `LIVE`, surfaced here as the corresponding client phases).  In
        earlier phases — `payment_pending`, the wallet popup, etc. —
        showing the pane would let the user tap Add and immediately get
        an `invalid_state` error from the verifyGrant check, since the
        grant exists but the session hasn't been marked paid yet.
      */}
      {isOwner && liveSessionId && (
        phase === 'awaiting_go_live' ||
        phase === 'countdown' ||
        phase === 'connecting' ||
        phase === 'live'
      ) && (
        <BroadcasterLiveToolsPane
          sessionId={liveSessionId}
          grantToken={liveSessionGrant ?? null}
          broadcasterWallet={wallet}
          pilotEntities={pilotEntities}
        />
      )}

      {/* Owner-on-second-device watch pane: when this device has no
          camera (Tesla) or the owner clicked "Watch my live broadcast"
          from a non-broadcasting device, render the incoming stream
          here with a dedicated Stop control. */}
      {!broadcast && (viewer || remoteStream) && (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button style={btnDanger} onClick={onStopWatch}>Stop watching</button>
            {onMinimize && (
              <button
                onClick={onMinimize}
                style={btnSecondary}
                title="Keep watching but collapse the overlay so you can browse the map"
              >
                Minimise & browse map
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <VideoPane
              videoRef={remoteVideoRef}
              label="Your live broadcast (from another device)"
              unmuteable
              overlaySlot={
                /* Owner-on-second-device: render their own tools on
                   the remote stream so they get a parity preview of
                   what viewers see, but in `broadcaster` mode so
                   hidden tools are still visible (and draggable). */
                liveSessionId
                  ? <LiveToolsLayer
                      sessionId={liveSessionId}
                      mode="broadcaster"
                      grantToken={liveSessionGrant ?? null}
                    />
                  : null
              }
              hud={{
              live: isLive,
              title: broadcasterTitle,
              subtitle: `You · ${shortAddr(wallet)}`,
              avatarUrl: record.avatarUrl ?? null,
              wallet:    wallet || null,
              metrics: [
                { label: 'Watching', value: String(Math.max(0, viewerCount)) },
                ...(endsAt ? [{ label: 'Ends at', value: endsAt }] : []),
                ...(activeSession?.scheduledEndAt
                  ? [{
                      label: 'Remaining',
                      value: formatDuration(remaining),
                      emphasis: (remaining <= 60 ? 'warn' : 'normal') as HudMetric['emphasis'],
                    }]
                  : []),
                ...(extensionCount > 0 ? [{ label: 'Extended', value: `${extensionCount}×` }] : []),
                ...(approxLabel ? [{ label: '~ Region', value: approxLabel }] : []),
              ],
              fullscreenStop: viewer
                ? { label: 'Stop watching', onClick: onStopWatch }
                : undefined,
              }}
            />
          </div>
        </>
      )}

      {phase === 'ended' && isOwner && audienceLog && audienceLog.length > 0 && (
        <BroadcastSummaryCard
          endedReason={endedReason}
          audienceLog={audienceLog}
          startedAt={activeSession?.startedAt}
          endedAt={activeSession?.endedAt}
          totalElapsedSec={elapsedSec}
          settlements={activeSession?.settlements}
        />
      )}
      {phase === 'ended' && endedReason && (!isOwner || !audienceLog || audienceLog.length === 0) && (
        <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{prettyReason(endedReason)}</div>
      )}
      {/*
        The banner fires for three overlapping conditions:
          1. notice === 'country_unknown'                 — couldn't resolve any country at all.
          2. notice === 'location_required_for_broadcast' — the runtime guard rejected a Start tap.
          3. isOwner && location not from real GPS        — proactively surface so the
             user sees the CTA the moment they open the Live tab, even before they tap Start.
        All three render the same `CountryEvalBanner` so the messaging stays consistent.
      */}
      {(
        notice === 'country_unknown' ||
        notice === 'location_required_for_broadcast' ||
        (isOwner && (countrySource !== 'location' || !hasLocation))
      ) ? (
        <CountryEvalBanner
          countryName={countryName}
          countrySource={countrySource}
          hasLocation={hasLocation}
          onRequestLocation={onRequestLocation}
        />
      ) : (
        notice && <div style={errStyle}>{prettyReason(notice)}</div>
      )}
    </div>
  );
};

/**
 * Banner rendered on the Live tab when the viewer's country cannot
 * be determined.  Offers two concrete ways out:
 *
 *   - "Use my location" — kicks off the same geolocation request as
 *     the game-panel "Go online" button.  Shown when the user hasn't
 *     granted location yet (or the granted fix expired).
 *   - A refresher showing *which* country the map centre is currently
 *     over (if any), so when the idle globe auto-spin moves the
 *     centre to a new country and the policy re-evaluates, the user
 *     can see why the banner keeps refreshing.
 *
 * Also tells the user how to re-trigger evaluation on demand: close
 * and reopen the profile after panning the map.  The overlay freezes
 * its country snapshot at open-time so the policy doesn't jitter in
 * the background while they're reading.
 */
const CountryEvalBanner: React.FC<{
  countryName?: string;
  countrySource?: 'location' | 'marker' | 'map';
  hasLocation?: boolean;
  onRequestLocation?: () => void;
}> = ({ countryName, countrySource, hasLocation, onRequestLocation }) => {
  // Decide which sub-state we're in so the banner can show the right
  // explanation.  We deliberately collapse "no country at all" and
  // "country resolved from map/marker, not GPS" into the same banner —
  // both have the same fix (share location) and showing two near-
  // identical banners would be confusing.
  const locationOk     = countrySource === 'location' && !!hasLocation;
  const fromMap        = countrySource === 'map';
  const fromMarker     = countrySource === 'marker';
  const sourceHint     = countrySource === 'location' ? 'your current location'
                       : fromMarker                    ? 'the selected marker'
                       : fromMap                       ? 'the map centre (moves as the globe auto-spins)'
                       : null;
  return (
    <div style={countryEvalBannerStyle}>
      <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: 6 }}>
        {locationOk ? 'Live-session policy not loaded' : 'Share your location to go live'}
      </div>
      <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.5 }}>
        {/*
          Primary copy: short and action-oriented per product guidance.
          We expand with one line of context only when a non-GPS country
          was tentatively resolved, so the user understands why a country
          name they see elsewhere on the map (USA, etc.) isn't enough to
          start a paid broadcast.
        */}
        Live broadcasts require your real location. Tap Go LIVE with my
        location to continue.
        {!locationOk && countryName && sourceHint && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
            We can see <strong>{countryName}</strong> from {sourceHint},
            but we won't price or pin a broadcast against that — only your
            real GPS counts.
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        {!hasLocation && onRequestLocation && (
          <button
            type="button"
            onClick={onRequestLocation}
            style={btnAccent}
            title="Share my location AND turn on Stay Live — same single tap as the Go LIVE button on the game panel."
          >
            📍 Go LIVE with my location
          </button>
        )}
        {hasLocation && !countryName && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Location is enabled, but it doesn't resolve to a known country.
            Try panning the map over the country you're in and reopening
            this profile.
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Stack of gift toasts rendered over a video pane.  Each toast is
 * a small pill that slides in from the right, shows the sender +
 * asset + amount, and fades out after ~6 seconds (the parent
 * controls the lifetime by removing the entry from `toasts`).
 *
 * We deliberately keep this purely presentational — the parent
 * component owns the toast list and the auto-dismiss timers so
 * unmount (overlay close, session end) doesn't leak timers.
 */
const GiftToastLayer: React.FC<{ toasts: LiveGiftEvent[] }> = ({ toasts }) => {
  if (!toasts.length) return null;
  return (
    <div style={giftToastLayerStyle} aria-live="polite" aria-label="Live gifts">
      {toasts.map((g) => (
        <div
          key={`${g.sentAt}-${g.fromPeerId}-${g.txHash || ''}`}
          style={giftToastStyle}
        >
          <div style={giftToastEmojiStyle}>
            <StellarIcon size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <div style={giftToastTitleStyle}>
              {g.fromWallet ? `${g.fromWallet.slice(0, 6)}…${g.fromWallet.slice(-4)}` : 'Someone'}
              {' '}
              sent
              {' '}
              <strong>{g.amount} {g.assetCode}</strong>
            </div>
            {g.memo && <div style={giftToastMemoStyle}>"{g.memo}"</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * End-of-broadcast audience summary shown to the broadcaster.
 * Renders the wallet + watch duration + origin country of every
 * viewer who connected during the session, along with the
 * aggregate totals (unique wallets, total watch-minutes) so the
 * broadcaster can see at-a-glance how their stream performed
 * before starting the next one.
 */
const BroadcastSummaryCard: React.FC<{
  endedReason: string | null;
  audienceLog: Array<{
    peerId: string;
    wallet?: string;
    country?: number | null;
    joinedAt: number;
    leftAt: number | null;
  }>;
  startedAt?: string | null;
  endedAt?: string | null;
  totalElapsedSec: number;
  settlements?: LiveSession['settlements'];
}> = ({ endedReason, audienceLog, startedAt, endedAt, totalElapsedSec, settlements }) => {
  const entries = [...audienceLog].sort((a, b) => a.joinedAt - b.joinedAt);
  const uniqueWallets = new Set(entries.map((e) => e.wallet).filter(Boolean)).size;
  const totalWatchSec = entries.reduce((acc, e) => {
    const end = e.leftAt ?? Date.now();
    return acc + Math.max(0, Math.floor((end - e.joinedAt) / 1000));
  }, 0);

  // Aggregate payouts so the broadcaster sees exactly what hit their
  // wallet, what's still pending manual settlement, and what the
  // platform retained.  Uses BigInt so rolling up many extensions
  // never loses precision on i128-sized stroops.
  const payoutRollup = (() => {
    if (!settlements || settlements.length === 0) return null;
    const ZERO = BigInt(0);
    let broadcasterPaid = ZERO;
    let broadcasterPending = ZERO;
    let platformKept = ZERO;
    let succeededCount = 0;
    let pendingCount = 0;
    let failedCount = 0;
    for (const r of settlements) {
      const paid = safeBigInt(r.paidStroops);
      const bcst = safeBigInt(r.broadcasterStroops);
      const plat = safeBigInt(r.platformStroops);
      platformKept += plat;
      if (r.status === 'succeeded') {
        broadcasterPaid += bcst;
        succeededCount += 1;
      } else if (r.status === 'failed' || (r.status === 'skipped' && bcst > ZERO)) {
        broadcasterPending += bcst;
        if (r.status === 'failed') failedCount += 1; else pendingCount += 1;
      } else {
        // skipped + zero broadcaster share → legacy all-to-treasury
        platformKept += (paid - plat - bcst); // defensive, should be 0
      }
    }
    return {
      broadcasterPaid, broadcasterPending, platformKept,
      succeededCount, pendingCount, failedCount,
    };
  })();

  return (
    <div style={summaryCardStyle}>
      <div style={summaryHeaderStyle}>
        <span style={summaryBadgeStyle}>Broadcast ended</span>
        {endedReason && <span style={summaryReasonStyle}>{prettyReason(endedReason)}</span>}
      </div>
      <div style={summaryStatsRowStyle}>
        <SummaryStat label="Viewers" value={String(entries.length)} />
        <SummaryStat label="Unique wallets" value={String(uniqueWallets)} />
        <SummaryStat label="Watch-minutes" value={(totalWatchSec / 60).toFixed(1)} />
        <SummaryStat label="Broadcast length" value={formatDuration(totalElapsedSec)} />
      </div>
      {payoutRollup && (payoutRollup.broadcasterPaid > BigInt(0)
          || payoutRollup.broadcasterPending > BigInt(0)) && (
        <div style={payoutRollupStyle}>
          <strong style={{ color: '#e2e8f0' }}>Revenue split</strong>
          <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>
            Paid to your wallet:{' '}
            <strong>{stroopsToDisplay(payoutRollup.broadcasterPaid.toString())} XLM</strong>
            {payoutRollup.broadcasterPending > BigInt(0) && (
              <>
                {' · '}
                <span style={{ color: '#f59e0b' }}>
                  Pending: {stroopsToDisplay(payoutRollup.broadcasterPending.toString())} XLM
                </span>
              </>
            )}
            {' · '}
            Platform kept:{' '}
            <strong>{stroopsToDisplay(payoutRollup.platformKept.toString())} XLM</strong>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {payoutRollup.succeededCount} settled
            {payoutRollup.pendingCount ? ` · ${payoutRollup.pendingCount} pending sweep` : ''}
            {payoutRollup.failedCount ? ` · ${payoutRollup.failedCount} failed (ops will retry)` : ''}
          </div>
        </div>
      )}
      {(startedAt || endedAt) && (
        <div style={summaryTimeRowStyle}>
          {startedAt && <span>Started {new Date(startedAt).toLocaleTimeString()}</span>}
          {startedAt && endedAt && <span> · </span>}
          {endedAt && <span>Ended {new Date(endedAt).toLocaleTimeString()}</span>}
        </div>
      )}
      <div style={summaryTableWrapStyle}>
        <div style={summaryTableHeaderStyle}>
          <span style={{ flex: 2 }}>Viewer</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Watched</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Joined</span>
        </div>
        {entries.map((e) => {
          const durSec = Math.max(0, Math.floor(((e.leftAt ?? Date.now()) - e.joinedAt) / 1000));
          const label = e.wallet ? shortAddr(e.wallet) : e.peerId.slice(0, 8);
          return (
            <div key={e.peerId} style={summaryTableRowStyle}>
              <span style={{ flex: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {label}
                {e.country != null && (
                  <span style={{ marginLeft: 6, color: '#64748b', fontFamily: 'inherit' }}>· {e.country}</span>
                )}
              </span>
              <span style={{ flex: 1, textAlign: 'right' }}>{formatDuration(durSec)}</span>
              <span style={{ flex: 1, textAlign: 'right', color: '#64748b' }}>
                {new Date(e.joinedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SummaryStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={summaryStatStyle}>
    <span style={summaryStatValueStyle}>{value}</span>
    <span style={summaryStatLabelStyle}>{label}</span>
  </div>
);

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Inline Stellar "swoosh" icon used to brand the gift / tip surfaces
 * (toast, Send-gift button, viewer & fullscreen gift buttons).  The
 * asset is a dark-silhouette PNG served from /public/images, so we
 * render it at-text-size with optional inversion when placed on a
 * dark background.  Keeping this as a simple `<img>` avoids a heavier
 * SVG inline without losing retina sharpness (the source file is
 * already crisp at 24–32 dp).
 */
const StellarIcon: React.FC<{
  size?: number;
  invert?: boolean;
  style?: React.CSSProperties;
}> = ({ size = 16, invert = false, style }) => (
  <img
    src="/images/stellar-xlm.png"
    alt=""
    aria-hidden
    width={size}
    height={size}
    style={{
      display: 'inline-block',
      verticalAlign: 'text-bottom',
      filter: invert ? 'invert(1)' : undefined,
      ...style,
    }}
  />
);

/**
 * Map a backend error code from `/api/broadcasts/recordings` into
 * the message the broadcaster sees in the "Saved recording" banner.
 * The raw codes (e.g. `storage_readonly`) are useful in logs but
 * unhelpful in the UI on their own.
 */
function humanizeRecordingSaveError(code: string | null | undefined): string {
  switch (code) {
    case 'video_too_large':
      return 'Couldn\'t save the recording: it exceeded the per-file size limit. Try shorter broadcasts or contact support.';
    case 'quota_exceeded':
      return 'Couldn\'t save the recording: every saved-broadcast slot is pinned. Unpin or delete one and record again.';
    case 'storage_readonly':
    case 'storage_path_missing':
    case 'storage_unavailable':
      return 'Couldn\'t save the recording: the server\'s recording storage isn\'t writable right now. Your live broadcast went out fine — only the VOD copy was lost.';
    case 'invalid_state':
    case 'invalid_grant':
    case 'grant_expired':
      return 'Couldn\'t save the recording: the live-session grant expired before the upload finished.';
    case 'upload_window_expired':
      return 'Couldn\'t save the recording: the 30-minute window to archive this broadcast has closed.';
    case undefined:
    case null:
    case '':
      return 'Couldn\'t save the recording: unknown error.';
    default:
      return `Couldn't save the recording: ${code.replaceAll('_', ' ')}`;
  }
}

function shortAddr(addr: string): string {
  if (!addr) return '';
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

/**
 * Format an approximate (0.1°-quantised) coordinate pair for display.
 * Produces something like `37.7°N · 122.4°W` — degrees with hemisphere
 * letters, which reads more naturally than signed decimals and removes
 * any false sense of precision.
 */
function formatApproxLocation(loc: ApproxLocation | null): string | null {
  if (!loc) return null;
  const { lat, lng } = loc;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const latStr = `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? 'N' : 'S'}`;
  const lngStr = `${Math.abs(lng).toFixed(1)}°${lng >= 0 ? 'E' : 'W'}`;
  return `${latStr} · ${lngStr}`;
}

// ── Styles ──────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
  padding: 12, boxSizing: 'border-box',
};

const boxStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 14,
  padding: 'clamp(12px, 3vw, 20px)',
  width: '100%', maxWidth: 640,
  // dvh avoids the mobile-browser chrome resize jitter; vh is the fallback.
  maxHeight: 'min(90vh, calc(100dvh - 24px))',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  boxSizing: 'border-box',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
};

const videoStyle: React.CSSProperties = {
  width: '100%', maxWidth: '100%',
  height: 'auto', aspectRatio: '16 / 9',
  maxHeight: 'min(50vh, 420px)',
  borderRadius: 8, background: '#000',
  objectFit: 'contain', display: 'block',
  position: 'relative',
  zIndex: 0,
  // `touch-action: manipulation` tells iOS Safari to drop its
  // built-in double-tap-to-zoom behaviour on this element, which
  // otherwise swallows our double-tap-to-fullscreen gesture and
  // leaves users unable to go fullscreen without the corner button.
  touchAction: 'manipulation',
};

/**
 * When the container is in native fullscreen, drop the aspect-ratio lid
 * and let the <video> fill the viewport.  The element itself is in the
 * fullscreen top layer, so normal sizing rules don't apply.
 */
const videoStyleFullscreen: React.CSSProperties = {
  width: '100%', height: '100%',
  maxWidth: 'none', maxHeight: 'none',
  borderRadius: 0, background: '#000',
  objectFit: 'contain', display: 'block',
  // Hold the <video> at z-index: 0 so the LiveToolsLayer (z=18) and
  // gift overlay (z=30), which are absolutely positioned siblings
  // inside the same fullscreen container, render unambiguously above
  // the video.  Without this some browsers promote the fullscreened
  // video to its own compositor layer, which can occlude
  // position:absolute siblings even though they have higher z-index
  // values in the document.
  position: 'relative',
  zIndex: 0,
};

const videoPaneStyle: React.CSSProperties = {
  position: 'relative', marginTop: 12,
  width: '100%', borderRadius: 8, overflow: 'hidden',
  background: '#000',
};

const videoPaneStyleFullscreen: React.CSSProperties = {
  position: 'relative', margin: 0,
  width: '100%', height: '100%',
  borderRadius: 0, overflow: 'hidden',
  background: '#000',
};

const hudTopLeftStyle: React.CSSProperties = {
  position: 'absolute', top: 8, left: 8,
  display: 'flex', alignItems: 'center', gap: 8,
  maxWidth: 'calc(100% - 60px)',
  padding: '6px 10px', borderRadius: 10,
  background: 'rgba(17, 24, 39, 0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  color: '#fff',
  boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
  pointerEvents: 'none',
};

const liveHudBadgeStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '2px 8px', borderRadius: 999,
  background: '#dc2626', color: '#fff',
  fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
  textTransform: 'uppercase',
};

const livePulseDotStyle: React.CSSProperties = {
  width: 8, height: 8, borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 0 0 rgba(255,255,255,0.6)',
  animation: 'gt-live-pulse 1.4s ease-out infinite',
};

const hudTitleStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
};

const hudSubtitleStyle: React.CSSProperties = {
  fontSize: 11, color: '#cbd5f5', opacity: 0.85,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const hudBottomLeftStyle: React.CSSProperties = {
  position: 'absolute', bottom: 8, left: 8,
  display: 'flex', flexWrap: 'wrap', gap: 6,
  maxWidth: 'calc(100% - 16px)',
  pointerEvents: 'none',
};

const hudMetricStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'baseline', gap: 4,
  padding: '4px 9px', borderRadius: 8,
  background: 'rgba(17, 24, 39, 0.6)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  fontSize: 12, lineHeight: 1.1,
  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
};

const hudMetricLabelStyle: React.CSSProperties = {
  fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5,
};

const hudMetricValueStyle: React.CSSProperties = {
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
};

const hudStopBtnStyle: React.CSSProperties = {
  position: 'absolute', bottom: 12, right: 12,
  padding: '10px 16px', borderRadius: 10,
  border: 'none', color: '#fff',
  background: '#dc2626',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(220,38,38,0.5)',
};

const unmuteOverlayStyle: React.CSSProperties = {
  position: 'absolute', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexDirection: 'column', gap: 8,
  background: 'rgba(15,23,42,0.35)',
  border: 'none', color: '#fff',
  fontSize: 15, fontWeight: 600, cursor: 'pointer',
  textShadow: '0 1px 3px rgba(0,0,0,0.6)',
  zIndex: 2,
};

const unmuteIconStyle: React.CSSProperties = {
  fontSize: 36, lineHeight: 1,
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
};

const teslaWarningStyle: React.CSSProperties = {
  marginBottom: 10,
  padding: '8px 12px',
  borderRadius: 8,
  background: '#fef3c7',
  border: '1px solid #fcd34d',
  color: '#92400e',
  fontSize: 12,
  lineHeight: 1.45,
};

const reconnectBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  margin: '10px 12px 0',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  color: '#fff',
  boxShadow: '0 4px 12px rgba(217, 119, 6, 0.35)',
};

const minimizedReconnectBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'rgba(245, 158, 11, 0.95)',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
};

const reconnectDotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#fff',
  flexShrink: 0,
  boxShadow: '0 0 0 0 rgba(255,255,255,0.7)',
  animation: 'gt-live-pulse 1.2s ease-in-out infinite',
};

const connectionDetailsStyle: React.CSSProperties = {
  marginTop: 10,
  padding: '6px 10px',
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  fontSize: 11,
  color: '#374151',
};

const connectionSummaryStyle: React.CSSProperties = {
  cursor: 'pointer',
  fontWeight: 600,
  userSelect: 'none',
  listStyle: 'disclosure-closed',
};

const connectionBodyStyle: React.CSSProperties = {
  marginTop: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  lineHeight: 1.4,
};

const fullscreenBtnStyle: React.CSSProperties = {
  position: 'absolute', top: 10, right: 10,
  // 44×44 hits the WCAG / iOS HIG minimum touch target so users on
  // iPhone Safari (where double-tap-to-fullscreen is unreliable)
  // can still enter fullscreen via a single deliberate tap.
  width: 44, height: 44, borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.18)',
  color: '#fff',
  background: 'rgba(17, 24, 39, 0.78)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  fontSize: 20, lineHeight: 1, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 4px 10px rgba(0,0,0,0.45)',
  // Disable Safari's tap highlight so the button feels native.
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
};

// TikTok-style navigation chevrons on the right edge of the video.
// `transform: translateY(-50%)` centres each button on its nominal
// `top` percentage so the pair sits symmetrically around the vertical
// midline of the pane.
const feedNavBtnStyle: React.CSSProperties = {
  position: 'absolute',
  right: 10,
  width: 36, height: 36, borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.25)',
  color: '#fff',
  background: 'rgba(17, 24, 39, 0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  fontSize: 16, lineHeight: 1, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.45)',
  transform: 'translateY(-50%)',
  zIndex: 3,
};

const feedSwipeHintStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8, right: 52,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'rgba(17, 24, 39, 0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  color: '#e5e7eb',
  fontSize: 11,
  letterSpacing: 0.3,
  pointerEvents: 'none',
  zIndex: 2,
};

// Fullscreen action bar — a floating row of pill buttons at the
// bottom-center of the pane.  Only shown while the pane is in
// fullscreen (the regular button row below the pane is hidden by the
// browser while a fullscreen element is active).  Gives viewers and
// broadcasters parity with the non-fullscreen experience.
const fsActionBarStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 18,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 8,
  maxWidth: '92%',
  padding: '8px 10px',
  borderRadius: 999,
  background: 'rgba(17, 24, 39, 0.55)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
  zIndex: 4,
};

const fsActionBtnStyle: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(31, 41, 55, 0.85)',
  color: '#f9fafb',
  padding: '8px 14px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const fsActionBtnDangerStyle: React.CSSProperties = {
  background: 'rgba(185, 28, 28, 0.92)',
  borderColor: 'rgba(248, 113, 113, 0.4)',
};

const fsActionBtnAccentStyle: React.CSSProperties = {
  background: 'rgba(124, 58, 237, 0.92)',
  borderColor: 'rgba(167, 139, 250, 0.4)',
};

const fsActionBtnWarnStyle: React.CSSProperties = {
  background: 'rgba(202, 138, 4, 0.92)',
  borderColor: 'rgba(250, 204, 21, 0.4)',
};

// Fullscreen gift pill — warm yellow-to-white gradient with dark
// text so the Stellar-branded CTA reads well over both bright and
// dark broadcast backgrounds.  Kept visually distinct from the
// red/danger variant so the Stop/End action stays the "this ends
// the session" colour.
const fsActionBtnGiftStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fde68a 0%, #ffffff 100%)',
  color: '#1f2937',
  borderColor: 'rgba(245, 158, 11, 0.7)',
  boxShadow: '0 2px 8px rgba(234, 179, 8, 0.35)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
};

const closeBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22,
  color: '#6b7280', width: 32, height: 32,
};

const minBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  fontSize: 28, lineHeight: '18px',
  color: '#6b7280', width: 32, height: 32,
};

/** Share button — same chrome as min/close so the header stays
 *  visually balanced.  The arrow glyph hints at "outbound" without
 *  needing copy. */
const shareBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  fontSize: 18, lineHeight: '18px',
  color: '#6b7280', width: 32, height: 32,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  transition: 'color 150ms ease',
};

// Minimised pill — docked at bottom-centre, sits above the map.
// zIndex stays under toast/modal layers (3000) but above everything
// map-related, so the pill is always reachable while the broadcast runs.
const minimizedPillWrapStyle: React.CSSProperties = {
  position: 'fixed',
  left: '50%', bottom: 16,
  transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'stretch', gap: 6,
  zIndex: 2900,
  pointerEvents: 'auto',
  maxWidth: 'calc(100vw - 24px)',
};

const minimizedPillStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '6px 14px 6px 8px',
  borderRadius: 999,
  background: 'rgba(15, 23, 42, 0.9)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
  cursor: 'pointer',
  color: '#fff',
  minWidth: 0,
};

const minimizedExpandHintStyle: React.CSSProperties = {
  fontSize: 14, opacity: 0.7, marginLeft: 4,
};

const minimizedStopBtnStyle: React.CSSProperties = {
  padding: '0 14px',
  borderRadius: 999,
  border: 'none',
  background: '#dc2626',
  color: '#fff',
  fontSize: 12, fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 6px 14px rgba(220,38,38,0.4)',
};

const avatarStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
  border: '1px solid #e5e7eb',
};

const liveBadgeStyle: React.CSSProperties = {
  padding: '2px 8px', fontSize: 10, background: '#dc2626', color: '#fff',
  borderRadius: 999, letterSpacing: 1, fontWeight: 700,
};

const privateBadgeStyle: React.CSSProperties = {
  padding: '2px 8px', fontSize: 10, background: '#f3f4f6', color: '#374151',
  borderRadius: 999, letterSpacing: 0.5, fontWeight: 600,
  border: '1px solid #d1d5db',
};

const walletTextStyle: React.CSSProperties = {
  fontSize: 11, color: '#6b7280', marginTop: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const readOnlyCardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb', background: '#fafafa', borderRadius: 10, padding: 14,
};

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: 8,
  padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
};

const metaStyle: React.CSSProperties = {
  fontSize: 11, color: '#9ca3af', marginTop: 10,
};

const errStyle: React.CSSProperties = {
  fontSize: 12, color: '#b91c1c', marginTop: 8,
};

// Banner surfaced when the Live tab can't resolve the viewer's
// country.  Styled similar to `onChainNoteStyle` but with a
// distinctive amber accent so users notice the actionable buttons
// inside (Use my location) instead of scrolling past a grey notice.
const countryEvalBannerStyle: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  borderRadius: 8,
  border: '1px solid #fcd34d',
  background: '#fffbeb',
};

const onChainNoteStyle: React.CSSProperties = {
  fontSize: 12, color: '#4b5563',
  background: '#f0f9ff', border: '1px solid #bae6fd',
  borderRadius: 8, padding: 10, marginTop: 10,
};

const btnBase: React.CSSProperties = {
  padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: '1px solid transparent',
};
const btnPrimary:   React.CSSProperties = { ...btnBase, background: '#111827', color: '#fff', border: '1px solid #111827' };
const btnSecondary: React.CSSProperties = { ...btnBase, background: '#fff', color: '#111827', border: '1px solid #d1d5db' };
const btnAccent:    React.CSSProperties = { ...btnBase, background: '#16a34a', color: '#fff', border: '1px solid #16a34a' };
const btnDanger:    React.CSSProperties = { ...btnBase, background: '#dc2626', color: '#fff', border: '1px solid #dc2626' };
const saveBroadcastToggleStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start',
  marginTop: 12, padding: '10px 12px',
  background: '#f9fafb', border: '1px solid #e5e7eb',
  borderRadius: 8, fontSize: 13, color: '#374151',
  cursor: 'pointer',
};
// Gift / tip button — yellow-and-white Stellar palette.  Dark text
// keeps contrast on the yellow fill, and the soft shadow gives the
// pill a subtle "warm CTA" lift without leaning on the red/danger
// semantic that we reserve for Stop / End actions.
const btnGift: React.CSSProperties = {
  ...btnBase,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'linear-gradient(135deg, #fde68a 0%, #ffffff 100%)',
  color: '#1f2937',
  border: '1px solid #f59e0b',
  boxShadow: '0 2px 6px rgba(234, 179, 8, 0.35)',
};
const btnGoLive:    React.CSSProperties = {
  ...btnBase,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 22px',
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  color: '#fff',
  border: '1px solid #b91c1c',
  boxShadow: '0 6px 20px rgba(220, 38, 38, 0.45)',
};

const goLiveDotStyle: React.CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 0 0 0 rgba(255,255,255,0.7)',
  animation: 'gt-live-pulse 1.2s ease-in-out infinite',
};

const countdownOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  background: 'rgba(15, 23, 42, 0.62)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  borderRadius: 12,
  color: '#fff',
  pointerEvents: 'none',
  padding: 16,
  textAlign: 'center',
};

const countdownTitleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: 0.3,
};

const countdownSubStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'rgba(255,255,255,0.85)',
  maxWidth: 320,
};

const giftToastLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 21,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  pointerEvents: 'none',
  maxWidth: 320,
};

// Gift toast: yellow-on-white Stellar palette.  The gradient picks
// up the brand-yellow accent on the left and fades toward a soft
// cream on the right so the dark-silhouette Stellar icon stays
// readable.  Text is near-black instead of the old white because
// white on yellow fails WCAG contrast.
const giftToastStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 14px',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #fde68a 0%, #fef3c7 55%, #ffffff 100%)',
  color: '#1f2937',
  border: '1px solid rgba(234, 179, 8, 0.55)',
  boxShadow: '0 8px 24px rgba(15,23,42,0.25)',
  fontSize: 13,
  animation: 'gt-gift-slide-in 0.35s ease-out',
};

const giftToastEmojiStyle: React.CSSProperties = {
  lineHeight: 0,
  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))',
};

const giftToastTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.25,
};

const giftToastMemoStyle: React.CSSProperties = {
  fontSize: 12,
  fontStyle: 'italic',
  opacity: 0.9,
  lineHeight: 1.2,
};

const pausedOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 19,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: 16,
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  borderRadius: 12,
  color: '#fff',
  pointerEvents: 'none',
  textAlign: 'center',
};

const pausedBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: 999,
  background: 'rgba(245, 158, 11, 0.95)',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
};

const pausedTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 800,
};

const pausedSubStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'rgba(255,255,255,0.85)',
  maxWidth: 320,
};

const liveEndedOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: 20,
  background: 'rgba(15, 23, 42, 0.72)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  borderRadius: 12,
  color: '#fff',
  textAlign: 'center',
};

const liveEndedBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: 999,
  background: 'rgba(239, 68, 68, 0.9)',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
};

const liveEndedTitleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 800,
  marginTop: 6,
};

const liveEndedSubStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'rgba(255,255,255,0.85)',
  maxWidth: 360,
};

const liveEndedActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 12,
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const summaryCardStyle: React.CSSProperties = {
  marginTop: 14,
  padding: 16,
  borderRadius: 12,
  background: '#fff',
  border: '1px solid #e5e7eb',
  boxShadow: '0 4px 18px rgba(15, 23, 42, 0.06)',
};

const summaryHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 12,
  flexWrap: 'wrap',
};

const summaryBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: 999,
  background: '#1e293b',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
};

const summaryReasonStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#475569',
};

const summaryStatsRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 10,
  marginBottom: 12,
};

const summaryStatStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '10px 12px',
  borderRadius: 10,
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
};

const summaryStatValueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 800,
  color: '#0f172a',
  lineHeight: 1.1,
};

const summaryStatLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  marginTop: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const summaryTimeRowStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
  marginBottom: 10,
};

const payoutRollupStyle: React.CSSProperties = {
  margin: '10px 0',
  padding: '10px 12px',
  borderRadius: 10,
  background: '#0d1721',
  border: '1px solid #1e293b',
};

function safeBigInt(s: string | number | undefined | null): bigint {
  try {
    if (s == null || s === '') return BigInt(0);
    return typeof s === 'number' ? BigInt(Math.trunc(s)) : BigInt(String(s));
  } catch { return BigInt(0); }
}

const summaryTableWrapStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  overflow: 'hidden',
  maxHeight: 260,
  overflowY: 'auto',
};

const summaryTableHeaderStyle: React.CSSProperties = {
  display: 'flex',
  padding: '8px 12px',
  background: '#f1f5f9',
  borderBottom: '1px solid #e5e7eb',
  fontSize: 11,
  fontWeight: 700,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const summaryTableRowStyle: React.CSSProperties = {
  display: 'flex',
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
  fontSize: 13,
  color: '#0f172a',
};

const countdownNumberStyle: React.CSSProperties = {
  fontSize: 120,
  fontWeight: 900,
  lineHeight: 1,
  color: '#fff',
  textShadow: '0 6px 24px rgba(0,0,0,0.45)',
  animation: 'gt-countdown-pop 1s ease-out',
};
const btnHint:      React.CSSProperties = {
  ...btnBase, background: '#e0f2fe', color: '#075985', border: '1px solid #bae6fd', cursor: 'default',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3100,
};

const modalBoxStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 18, width: 460, maxWidth: '94vw',
  boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
};

export default ProfileOverlay;
